import { Button } from '@helix-ds/react'
import { AlertBanner, type AlertSectionLink } from '@/components/common/AlertBanner'
import { Navbar } from '@/components/Navbar'
import { NewOrderModal, type OrderFormData } from '@/components/NewOrderModal'
import { OrderLoadError, OrderLoading, OrderNotFound } from '@/components/OrderLoadStates'
import type { HPOTerm } from '@/components/common/HPOSearch'
import { ComparatorModal } from '@/components/modals/ComparatorModal'
import { EditFlowStaleHpoBanner } from '@/components/order/EditFlowStaleHpoBanner'
import { TestOptionsModal } from '@/components/modals/TestOptionsModal'
import { parseClinicalBaselineSummary, shouldShowStaleHpoBanner } from './staleHpoBaseline'
import { blocksSubmit, reviewForUnreviewedDiagnosis } from './livePhenotypeMerge'
import { sideNavSectionsForMergedClinicalSummary } from './mergedClinicalSummaryState'
import { FamilyHistorySummaryModal } from '@/components/modals/FamilyHistorySummaryModal'
import { GuardianContactModal } from '@/components/modals/GuardianContactModal'
import { OrderFormViewModal } from '@/components/modals/OrderFormViewModal'
import { CancelOrderConfirmModal } from '@/components/modals/CancelOrderConfirmModal'
import { ConfirmDialog } from '@/components/modals/ConfirmDialog'
import {
  SendOrderUpdatesConfirmModal,
  type SendOrderUpdatesSectionId,
} from '@/components/modals/SendOrderUpdatesConfirmModal'
import { SEND_UPDATES_SECTION_SHORT_LABELS } from '@/components/modals/sendOrderUpdatesSections'
import { EhrDemographicsUpdateModal } from '@/components/modals/EhrDemographicsUpdateModal'
import {
  compareProbandDemographicsToEhr,
  type EhrDemographicsFieldDiff,
  type EhrDemographicsMergePatch,
} from '@/pages/order/ehrDemographicsDrift'
import { OrderExternalIdLabel } from '@/components/order/OrderExternalIdLabel'
import { RestartOrderAction } from '@/components/order/RestartOrderAction'
import { CopyableValue } from '@/components/common/CopyableValue'
import { ClinicalIndicationsModal } from '@/components/modals/ClinicalIndicationsModal'
import { TruncatedText } from '@/components/common/Tooltip'
import {
  ClinicalSummaryEditor,
  type ClinicalSummaryEditorControls,
  type ClinicalSummarySnapshot,
} from '@/components/order/ClinicalSummaryEditor'
import { ClinicalSummaryCard } from '@/components/order/ClinicalSummaryCard'
import { FamilyHistoryCard } from '@/components/order/FamilyCard'
import { GuardianContactCard } from '@/components/order/GuardianContactCard'
import { guardianContactCardFieldsFromCanonical } from '@/components/order/guardianContactCardFields'
import type { FamilyHistorySummaryData, TestOptions } from '@/components/order/OrderTypes'
import {
  comparatorConsentAnyApplicable,
  incidentalConsentGated,
  resolveConsentInteractionGates,
} from '@/components/order/comparatorParentConsentConfig'
import { ProbandConsentSections } from '@/components/order/ProbandConsentSections'
import { TestOptionsCard } from '@/components/order/TestOptionsCard'
import {
  formatTurnaroundTimeSequencingCardSubtitle,
  canCancelOrder,
  canAddComparators,
  canEditExistingMemberHpo,
  getFulfillmentStrategy,
  getPhenotypeModel,
  isCancellableStatusForStrategy,
  getClientName,
  getPatientConsents,
  getTestTypeConfig,
  getTurnaroundTimeDayBasisForTestType,
  recoverSelectedOptionForCanonical,
  isTrfSignatureDocusignEnabled,
} from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import type { OrderReportResponse, ReanalysisDecision } from '@/types/OrderReportTypes'
import { getAllOrderReports, getReanalysisPreview } from '@/features/report/services/reportClient'
import {
  applyHiddenSecondaryConsentDefaults,
  useEnforceNonApplicableSecondaryConsentDefaults,
} from '@/hooks/useEnforceNonApplicableSecondaryConsentDefaults'
import { type ConfiguredOrderStep, useConfiguredOrderSteps } from '@/hooks/useConfiguredOrderSteps'
import { copyWithoutSigning } from '@/utils/copyWithoutSigning'
import { useResolveIndicationHpoLabels } from './hooks/useResolveIndicationHpoLabels'
import { useUnsavedChangesNavGuard } from './hooks/useUnsavedChangesNavGuard'
import { getCallerFromOrderData, resolveOrderFetchCaller } from './orderCaller'
import { buildValidationFailureSectionLinks, type ValidationFailureComparatorSection } from './orderInProgressSectionLinks'
import {
  allOrderComparatorsComplete,
  incompleteChangedSendSections,
  type SendUpdatesCompletenessInput,
} from './orderInProgressSendGate'
import { getEpicEhrEncounterForOrder } from '@/features/auth/epicRedoxContextStorage'
import { getActiveEpicState, useLaunchMode } from '@/features/auth/launchContext'
import { AuthorizationContext } from '@/features/authorization/authorizationHooks'
import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import { EpicSessionExpiryWarning } from '@/features/auth/components/EpicSessionExpiryWarning'
import { isHpoCode } from '@/features/terminology/hpo/hpoTypes'
import { normalizeHpoId } from '@/features/terminology/hpo/hpoLabelResolver'
import { hpoAPIClient } from '@/features/terminology/hpo/HPOAPIClient'
import type { ClinicalSummaryReview } from './clinicalSummaryReviewTypes'
import {
  augmentCanonicalWithRfdGuardian,
  type CanonicalFamily,
  type CanonicalOrderPayload,
  type Comparator,
  legacyTrfPayloadToCanonical,
  overlayRichComparatorsFromRfd,
  rehydrateSparseCanonicalWithLegacyTrf,
  reconcileCanonicalOrderTypeWithLegacyTrf,
  stripGuardianComparators,
  stripPlaceholderDobsFromCanonical,
} from '@/ordering/canonicalOrder'
import {
  deriveOrderInProgressFamilyRows,
  parentComparatorSamplesAllowedForFamilyRowDerivation,
} from '@/ordering/orderInProgressFamilyCards'
import { projectComparatorsToOrderInProgressRows } from '@/ordering/comparatorsToOrderInProgressRows'
import { ComparatorList } from '@/components/order/ComparatorList'
import { ComparatorCard } from '@/components/order/ComparatorCard'
import { ComparatorRow } from '@/components/order/ComparatorRow'
import { OrderSideNav, OrderNavToggle } from '@/components/order/OrderSideNav'
import { PatientConsentCard } from '@/components/order/PatientConsentCard'
import { TrfDeclineReasonRow, TrfSignatureBadge } from '@/components/order/TrfSignatureBadge'
import { TrfSignaturePreparing } from '@/components/order/TrfSignaturePreparing'
import { TrfSignatureRecording } from '@/components/order/TrfSignatureRecording'
import {
  ComparatorSideNavModals,
  type ComparatorSectionValidationProfile,
} from '@/components/order/ComparatorSideNavModals'
import {
  PROBAND_INCIDENTAL_FINDINGS_ANCHOR_ID,
  isKitOrderingEnabled,
  isOrderSideNavEnabled,
  useOrderSideNavController,
  useOrderStickyTop,
} from '@/pages/order/orderSideNavModel'
import {
  backfillComparatorIds,
  isTestingParentComparator,
  normalizeInboundParentComparators,
  parentSideForComparator,
} from '@/ordering/comparatorWriteAdapter'
import {
  ADD_FAMILY_MEMBER_RELATIONSHIP_OPTIONS,
  NON_PARENT_COMPARATOR_RELATIONSHIP_OPTIONS,
  formatComparatorRelationshipLabel,
  relationshipOptionsForStrategy,
} from '@/ordering/comparatorRelationshipOptions'
import { orderInProgressComparatorSequencingCopy } from '@/ordering/orderInProgressSequencingCopy'
import {
  comparatorIdentityKeys,
  dropMatchingCancellations,
  isComparatorAddedAfterSubmit,
  isNewlyAddedComparator,
  recordAddedAfterSubmitKeys,
  type PendingComparatorCancellation,
  stripSessionRemovedComparators,
} from './orderInProgressComparatorGuard'
import {
  applySequencingOptionsChangeToCanonical,
  comparatorSectionsReadyForSequencingSend,
  familyMembersForTestOptionsModal,
  getInProgressSequencingModalAllowedTestTypes,
  persistedTestTypeFromSequencingBaselineSerialized,
  resolveSequencingTierForComparators,
  testOptionsFromCanonicalOrder,
  testOptionsFromSequencingBaselineSerialized,
} from '@/ordering/sequencingComparatorUpgrade'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  mergeClinical,
  mergeFamily,
  mergeOrderSelection,
  mergePatient,
  mergeReason,
  removeComparator,
  setCanonicalOrder,
  setGuardianContact,
  setOrderType,
  setClinicalSummaryReview,
  upsertComparator,
} from '@/store/slices/canonicalOrderSlice'
import { OrderStatusDisplay } from '@/types/NavigationTypes'
import { mapOrderStatusToDisplay } from '@/utils/mapOrderStatusToDisplay'
import {
  type CategorizedHPOOption,
  CollectionType,
  type OrderFormInfoData,
  Sex,
  TestType,
} from '@/types/RequisitionFormTypes'
import {
  type ExistingRequestPayload,
  cancelOrder,
  getOrderById,
  getOrderByPatient,
  getOrderStatusByPatient,
  getOrderTrfSignature,
  inflectionExternalIdFromOrder,
  isMrnConflictError,
  isOrderRejectedError,
  OrderFetchError,
  OrderStatus,
  updateOrder,
} from '@/utils/DatabaseOperations'
import { orderTrfSignatureFrom, type OrderTrfSignature } from '@/utils/orderTrfSignature'
import { firstParsableTrfSentAt, laterTrfSentAt, parseTrfSentAt } from '@/utils/trfSentAt'
import { trfSignatureSubmittedRecently, useTrfSignaturePolling } from '@/pages/order/useTrfSignaturePolling'
import { markTrfSigningInProgress, useTrfSigningInProgress } from '@/pages/order/useTrfSigningInProgress'
import { trfSigningPath } from '@/features/docusign/signingPath'
import { useReissueWatch } from '@/pages/order/reissueWatch'
import { canShowSignTrfCta, signingHandoffAfterUpdate } from '@/pages/order/signTrfCta'
import { resolveEffectiveConsentFlowForSubmit, testLinesFromUiConfig } from '@/pages/order/orderingSubmitValidation'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'
import {
  clinicalTextLimitError,
  comparatorClinicalNoteBudget,
  probandClinicalTextBudget,
  isValidFreeText,
} from '@/utils/freeTextValidation'
import { isOrderEditable, isOrderEditableForStrategy } from '@/utils/OrderEditability'
import { canEditComparatorParents as canEditComparatorParentsFn } from '@/utils/canEditComparatorParents'
import { useComparatorAdditionWindow } from '@/pages/order/useComparatorAdditionWindow'
import { useIsOrderEditable } from '@/pages/order/useIsOrderEditable'
import { orderHasGuardianContact, resolveComparatorPlatformCollects } from '@/ordering/comparatorPlatformConsent'
import { getParentComparator, parentComparatorCountForOrder } from '@/ordering/comparatorReadAdapter'
import {
  backfillResolvedPhenotypeLabels,
  captureCanonicalBaselines,
  clinicalSummaryHasDisallowedChars,
  serializeClinicalBaseline,
  serializeComparatorHpoSig,
  serializeComparatorsBaseline,
  serializeFamilyBaseline,
  serializeGuardian,
  serializeIndicationsBaseline,
  serializePatientConsentBaseline,
  serializePatientDemographicsBaseline,
  serializeSequencingBaseline,
  shouldBlockUnsavedChangesNav,
  stagedPhenotypesWouldChange,
} from './orderInProgressUnsavedChanges'
import { selectionHasRelationship, withRelationshipSelection } from '@/ordering/testOptionsRelationships'
import {
  formatAge,
  formatBirthDate,
  formatFamilyHistoryDetails,
  formatMrnForDisplay,
  formatPatientNameLastFirst,
  rawMrnValue,
  mapCategoryToType,
  mapCollectionStringToSampleTypeStrict,
  mapCollectionTypeToSampleType,
  type SampleTypeOption,
} from '@/utils/orderFormUtils'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { ChartPie, ChevronDown, Close, DotsVertical, FileLines, Pen, UserAdd } from 'flowbite-react-icons/outline'
import { OrderStepsRow } from '@/components/order/OrderStepsRow'
import type { RootState } from '@/store'
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useStore } from 'react-redux'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'

type OrderInProgressState =
  | {
      epicState?: string
      orderId?: string
      orderData?: OrderFormInfoData
      backendStatus?: string
      /**
       * Set by the submit flow when it navigates here (GEN-5027). Arms the
       * "preparing your document for signature" state across the window before the
       * envelope reaches `sent`, where the order has no signature request yet and
       * the status alone cannot tell "envelope not created yet" from "org has no
       * e-signature". Lost on reload by design — a reload mid-preparation falls
       * back to the `created` status for the same cue.
       */
      justSubmitted?: boolean
    }
  | string
  | null
  | undefined

/** Navigation state for patient demographics passed from other routes (e.g. GEN-1277). */
interface OrderInProgressLocationState {
  patientDemographics?: {
    dateOfBirth?: string
    firstName?: string
    lastName?: string
    mrn?: string
  }
}

/** Same canonical merge as main fetch: prefer stored canonicalOrder + always augment guardian from RFD. */
function mergeCanonicalFromOrderJsonPayload(
  json_payload: (Partial<ExistingRequestPayload> & { orderFormInfo?: OrderFormInfoData }) | null | undefined,
): CanonicalOrderPayload | null {
  if (!json_payload) {
    return null
  }
  const rfd = json_payload.requisitionFormData
  const co = json_payload.canonicalOrder
  // The server's authoritative full comparator list lives on
  // ``requisitionFormData.comparators`` (all relationships, with demographics /
  // specimen). The sparse ``canonicalOrder`` often omits comparators and the
  // legacy-TRF reconstruction only seeds MOTHER / FATHER, so non-parent rows
  // (siblings, aunts/uncles, guardians) would vanish from the R5 side-nav on
  // reload. Overlay this rich list onto whatever canonical we reconstruct.
  // (Not on the typed PartialRequisitionFormData surface — read defensively.)
  const rfdComparators = (rfd as { comparators?: Comparator[] | null } | undefined)?.comparators ?? null
  if (co?.schemaVersion) {
    if (json_payload.orderFormInfo && rfd) {
      const legacy = legacyTrfPayloadToCanonical({
        orderFormInfo: json_payload.orderFormInfo,
        requisitionFormData: rfd,
      })
      let merged = rehydrateSparseCanonicalWithLegacyTrf(co as CanonicalOrderPayload, legacy)
      merged = augmentCanonicalWithRfdGuardian(merged, rfd)
      merged = reconcileCanonicalOrderTypeWithLegacyTrf(merged, {
        orderFormInfo: json_payload.orderFormInfo,
        requisitionFormData: rfd ?? null,
      })
      return stripGuardianComparators(overlayRichComparatorsFromRfd(merged, rfdComparators))
    }
    let merged = augmentCanonicalWithRfdGuardian(co as CanonicalOrderPayload, rfd)
    merged = reconcileCanonicalOrderTypeWithLegacyTrf(merged, {
      orderFormInfo: json_payload.orderFormInfo,
      requisitionFormData: rfd ?? null,
    })
    return stripGuardianComparators(overlayRichComparatorsFromRfd(merged, rfdComparators))
  }
  if (json_payload.orderFormInfo && rfd) {
    const canonical = legacyTrfPayloadToCanonical({
      orderFormInfo: json_payload.orderFormInfo,
      requisitionFormData: rfd,
    })
    return stripGuardianComparators(
      overlayRichComparatorsFromRfd(augmentCanonicalWithRfdGuardian(canonical, rfd), rfdComparators)
    )
  }
  return null
}

/** Map canonical family slice to Family History modal shape (in-progress / submitted orders). */
function familyCanonicalToSummaryData(f: CanonicalFamily | null | undefined): FamilyHistorySummaryData {
  const summary = (f?.familyHistory ?? '').trim()
  const hasMemberFlags =
    Boolean(f?.hasParent) || Boolean(f?.hasGrandparent) || Boolean(f?.hasSibling) || Boolean(f?.hasCousin)
  return {
    includeFamilyHistory: summary.length > 0 || hasMemberFlags,
    summary: f?.familyHistory ?? '',
    hasInteracted: true,
    familyMembersWithPhenotypes: {
      parent: Boolean(f?.hasParent),
      grandparent: Boolean(f?.hasGrandparent),
      sibling: Boolean(f?.hasSibling),
      cousin: Boolean(f?.hasCousin),
    },
  }
}

/** Matches OrderStart `familyHistoryDetails` — relative categories with phenotypes context. */
function familyHistoryDetailsFromCanonical(f: CanonicalFamily | null | undefined): string | undefined {
  if (!f) return undefined
  return formatFamilyHistoryDetails({
    parent: f.hasParent,
    grandparent: f.hasGrandparent,
    sibling: f.hasSibling,
    cousin: f.hasCousin,
  })
}

/**
 * Every identity key of every non-proband comparator in a baseline (last-sent)
 * canonical order. A session removal counts as a *pending cancellation* only
 * when the removed row shares an identity key with this set — a comparator both
 * added and removed within the session was never sent, so its cancellation is a
 * no-op the provider need not confirm.
 */
function buildComparatorBaselineKeySet(
  comparators: ReadonlyArray<Comparator | null | undefined> | null | undefined
): Set<string> {
  const keys = new Set<string>()
  if (!Array.isArray(comparators)) return keys
  for (const c of comparators) {
    if (!c || c.relationship === 'PROBAND') continue
    for (const k of comparatorIdentityKeys(c)) keys.add(k)
  }
  return keys
}

export function OrderInProgress() {
  const { config } = useUiConfig()
  const location = useLocation()
  const navigate = useNavigate()
  const { orderId: urlOrderId, patientId: urlPatientId } = useParams<{ orderId?: string; patientId?: string }>()

  const { user } = useAuth0()
  const dispatch = useAppDispatch()
  const appStore = useStore<RootState>()
  const locationState = location.state as OrderInProgressState

  // Reanalysis flow flag. Set when the user arrives via the results page
  // "Request Reanalysis" action, which navigates here with `?reanalysis=true`
  // (see OrderResultReady.handleRequestReanalysis). Use this to branch the
  // reanalysis-specific behavior of this edit session (e.g. Send Updates copy /
  // payload). Read from the URL so it survives reloads.
  const isReanalysis = new URLSearchParams(location.search).get('reanalysis') === 'true'

  const [fetchedOrderData, setFetchedOrderData] = useState<OrderFormInfoData | undefined>(undefined)
  const [inflectionExternalId, setInflectionExternalId] = useState<string | null>(null)
  // Timestamp (ISO) the lab accessioned the proband sample, from the order
  // status response. Drives the "accessioned on ..." banner detail while the
  // order is still in progress (SAMPLE_RECEIVED). Null when not yet accessioned
  // or the backend did not provide it.
  const [accessionedOn, setAccessionedOn] = useState<string | null>(null)
  /**
   * Status of the signature request this order's TRF is waiting on (GEN-4030). Read from
   * the order-details response the page already fetches, and reset by the order-switch
   * effect below so it never outlives the order it describes.
   *
   * Null until that fetch resolves, and for orders with no outstanding request —
   * organizations without e-signature enabled, and orders that predate it — which is why
   * it renders nothing rather than "unsigned".
   */
  const [trfSignature, setTrfSignature] = useState<OrderTrfSignature | null>(null)
  /**
   * The order's `updated_at` from the order-details fetch, used to bound the
   * `null`-signature "preparing" window on submit recency (GEN-5027). Submit
   * bumps `updated_at`, so a recent value means an envelope is genuinely in
   * flight; a stale one means a historical unsigned order that will never get
   * one, which must render nothing rather than spin. Reset by the order-switch
   * effect so it never outlives the order it describes.
   */
  const [orderUpdatedAt, setOrderUpdatedAt] = useState<string | Date | null>(null)
  /**
   * The server-sourced instant the order's current TRF was sent (GEN-5494),
   * from `order.trf_sent_at ?? order.created_at` on the order-details fetch and
   * refreshed from the PUT result after Send Updates. The "Order was sent"
   * banner reads ONLY this state — never `orderData.orderDate`, never
   * `new Date()` — so it stays stable across re-render / remount instead of
   * re-rendering the viewer's current local time. Null until the fetch resolves,
   * or when neither timestamp is available: the banner then omits the date.
   * Order-scoped, so the order-switch effect resets it.
   */
  const [trfSentAt, setTrfSentAt] = useState<string | null>(null)
  /**
   * The superseded envelope's document id while a post-edit reissue is in flight
   * (GEN-5027). A material edit reissues the TRF asynchronously, and the reissue
   * is status-invisible — the order keeps projecting the old envelope's `sent`
   * status until the Lambda swaps the document — so this arms the poll to watch
   * for the `external_document_id` change and clears once the new document lands.
   *
   * Held in sessionStorage rather than React state because the window routinely
   * spans a full-page load: a reissuing update hands the clinician off to /sign
   * by hard navigation, and coming back must not present the superseded envelope
   * as live. Restoring is keyed on the order, so switching orders resets it.
   */
  const {
    awaitingFromDocumentId: reissueAwaitingFromDocId,
    arm: armReissueWatch,
    clear: clearReissueWatch,
  } = useReissueWatch(urlOrderId)
  /**
   * Cell D (F1) — the order's reports, used to compute the 7-day add-comparator
   * window via `canAddComparators`. Loaded with the same `getAllOrderReports`
   * path sibling pages use (OrderResultReady / OrderGuidance); empty until the
   * fetch resolves, and empty ⇒ addable (the "no final report" default). Only
   * `inflection_test` orders act on this (rcigm is a no-op in the helper).
   */
  const [orderReports, setOrderReports] = useState<OrderReportResponse[]>([])
  /**
   * Starts `true` whenever there is an id to fetch so the very first render is
   * the shared loader — not a blank/partial page for the frame before the fetch
   * effect flips it on.
   */
  const [isLoadingOrder, setIsLoadingOrder] = useState(() => Boolean(urlOrderId || urlPatientId))
  /**
   * Outcome of the order-id fetch when it cannot render the page. This route has
   * no `OrderScopeGuard`: `GET /database/orders/{id}` is org-scoped server-side
   * and its 404 (`null`) means missing OR out-of-org, exactly like the scope
   * probe. Patient-only URLs keep their previous behavior (never set here).
   */
  const [orderLoadFailure, setOrderLoadFailure] = useState<'not_found' | 'unauthorized' | 'error' | null>(null)
  /**
   * Flips true once the order-details fetch has populated the canonical store
   * and captured the demographics baseline for THIS order. Gates the EHR drift
   * check so it can only compare against the fully-hydrated saved demographics —
   * never the callback's pre-hydration snapshot while `OrderInProgress`'s own
   * `setCanonicalOrder` is still in flight. Reset per order.
   */
  const [orderHydrated, setOrderHydrated] = useState(false)
  /**
   * De-dupes concurrent order fetches. This is a ref, not the `isLoadingOrder`
   * state, because the fetch effect deliberately omits loading from its deps:
   * a state flag would still read `true` in the closure of the effect run that
   * replaces an in-flight fetch, so the replacement would bail and never retry
   * once loading cleared. Keyed by fetch id so only a genuine duplicate of the
   * SAME request is skipped.
   */
  const inFlightFetchIdRef = useRef<string | null>(null)
  const hasInitiallyFetched = useRef(false)
  /**
   * The id of the order currently on screen (GEN-5494). Re-checked after the
   * Send Updates `await` so the post-save banner refresh only applies when the
   * saved order is still this order — not when the PUT targeted a different id
   * or the user navigated away mid-flight. Seeded from the route id and
   * refreshed to the resolved order's id; reset by the order-switch effect.
   */
  const currentOrderIdRef = useRef<string | null>(null)

  // Use location state first, fall back to fetched data
  const orderData =
    (typeof locationState === 'object' && locationState !== null ? locationState.orderData : undefined) ||
    fetchedOrderData

  const { canonicalOrder: storedCanonical } = useAppSelector((s) => s.canonicalOrder)
  const [orderStatus, setOrderStatus] = useState<OrderStatusDisplay>(
    mapOrderStatusToDisplay(
      typeof locationState === 'object' && locationState !== null ? locationState.backendStatus : undefined
    )
  )
  // Raw backend status string (e.g. "reanalysis"), kept alongside the mapped
  // `orderStatus`. `mapOrderStatusToDisplay('reanalysis') -> RESULT_READY`, so
  // the mapped enum can no longer distinguish a reanalysis order from a
  // completed one; the raw value is needed to drive the reanalysis read-only
  // lock (`reanalysisLocked` below). Seeded from nav state, refreshed by the
  // order fetch + status effects.
  const [rawBackendStatus, setRawBackendStatus] = useState<string | undefined>(
    typeof locationState === 'object' && locationState !== null ? locationState.backendStatus : undefined
  )

  // Auth flow detection (Callback passes epicState; support legacy state for backward compatibility).
  // Prefer navigation state (in-memory), then fall back to the persisted, session-validated Epic
  // state from `useLaunchMode` (sessionStorage/localStorage). Without the fallback, a page reload
  // or deep link into an in-progress order loses `location.state` and would drop the Epic session —
  // breaking the comparator MRN lookup — even though a live Epic OAuth session still exists. This
  // mirrors how OrderStart resolves the session (via `useLaunchMode` + persisted state).
  const { mode: launchMode, epicState: persistedEpicState } = useLaunchMode()
  const epicStateFromLocation =
    typeof locationState === 'object' && locationState !== null
      ? (locationState.epicState ?? (locationState as { state?: string }).state)
      : null
  const epicState = epicStateFromLocation ?? persistedEpicState ?? null
  const authFlow = epicState || launchMode === 'epic_smart' ? 'epic' : 'standalone'

  /**
   * Caller string for the signature-status poll (GEN-5027), resolved the same
   * way the main order fetch resolves it so both reads are attributed
   * identically. Recomputed only when its inputs change; the poll interval reads
   * a stable value rather than re-resolving on every tick.
   */
  const pollCaller = useMemo(() => {
    const epicContext = epicState ? EpicAuthClient.getInstance().getContext(epicState) : null
    return resolveOrderFetchCaller(epicContext, user)
  }, [epicState, user])

  // Whether this order was submitted recently enough for a `null` signature to
  // still be an envelope in flight (GEN-5027). Bounds the `null` preparing/poll
  // window so reopening a historical unsigned order — permanently `null` at a
  // DocuSign org — renders nothing instead of spinning for the full cap. Memoized
  // on `orderUpdatedAt` so the recency read is taken once per order load (the poll
  // cap bounds the far end); recomputed when a fresh fetch swaps the timestamp.
  const trfSubmittedRecently = useMemo(() => trfSignatureSubmittedRecently(orderUpdatedAt), [orderUpdatedAt])

  // Carry the signature badge from submit to a request that is out for signature
  // without a manual refresh: poll provider-api (never a signature vendor) while
  // the envelope is still being prepared, and hand each read back to the badge.
  // `preparing` drives the "preparing your document for signature" state and is
  // bounded by the poll's lifetime, so it never spins forever; polling stops the
  // moment the status reaches `sent`.
  const { preparing: trfSignaturePreparingState } = useTrfSignaturePolling({
    orderId: urlOrderId,
    caller: pollCaller,
    signature: trfSignature,
    docusignEnabled: isTrfSignatureDocusignEnabled(config),
    submittedRecently: trfSubmittedRecently,
    awaitingReissueFromDocumentId: reissueAwaitingFromDocId,
    onSignature: setTrfSignature,
  })

  // Optimistic "signing in progress" cue (GEN-5259). The Sign click is a hard
  // navigation to /sign, so the intent is stashed in sessionStorage and read back
  // here on return. While it holds, the page shows a spinner and disables Sign; it
  // resolves when the poll above reads a terminal status (the badge takes over) or
  // when the on-screen budget elapses (revert to the real, still-signable status
  // so the clinician can try again).
  const { signingInProgress: trfSigningInProgress } = useTrfSigningInProgress({
    orderId: urlOrderId,
    signature: trfSignature,
  })

  // Clear the reissue watch once the replacement envelope's document lands: the
  // poll stops itself when the id changes, but clearing here keeps the state
  // honest and lets a later edit re-arm. A `null` read (the old envelope voided
  // before the new one appears) is not the replacement, so it does not clear.
  useEffect(() => {
    if (
      reissueAwaitingFromDocId !== null &&
      trfSignature?.externalDocumentId != null &&
      trfSignature.externalDocumentId !== reissueAwaitingFromDocId
    ) {
      clearReissueWatch()
    }
  }, [trfSignature, reissueAwaitingFromDocId, clearReissueWatch])

  const { logout: auth0Logout, user: auth0User } = useAuth0()

  // The signed-in user's NPI, resolved the same way the order flow resolves it
  // (GEN-5028): the Epic context's provider NPI on an Epic launch, else the
  // Auth0 NPI claim. Used only to gate the Sign TRF CTA to the clinician of
  // record client-side; the server re-derives and enforces this on every mint.
  const currentUserNpi = useMemo((): string => {
    if (authFlow === 'epic' && epicState) {
      const context = EpicAuthClient.getInstance().getContext(epicState)
      return context?.providerNpi?.trim() ?? ''
    }
    const claim =
      (auth0User as { [k: string]: unknown } | undefined)?.['https://inflectionmedicine.com/npi'] ??
      (auth0User as { [k: string]: unknown } | undefined)?.['https://provider.dev.inflectionmedicine.com/npi']
    return typeof claim === 'string' ? claim.trim() : ''
  }, [authFlow, epicState, auth0User])

  // Whether to surface the in-app Sign TRF affordance: an embedded-channel
  // signature that is still signable, and the current user is the clinician of
  // record. Non-clinicians and delegates never see it.
  //
  // Withheld while the document is being prepared. During a re-issue the badge
  // is the only thing that changes — the ledger keeps projecting the superseded
  // envelope as `sent`, so the signable test still passes — and offering the
  // button there hands the clinician the form their edit just replaced.
  const showSignTrfCta = useMemo(
    () =>
      !trfSignaturePreparingState &&
      canShowSignTrfCta({
        signature: trfSignature,
        providers: storedCanonical?.providers ?? null,
        currentUserNpi,
      }),
    [trfSignature, trfSignaturePreparingState, storedCanonical?.providers, currentUserNpi]
  )

  // Navigate (full document load, not client-side) to the standalone /sign page.
  // A hard navigation is required so the signing document is fetched under the
  // /sign* CloudFront behaviour that carries the relaxed CSP/COEP the DocuSign
  // ceremony iframe needs; a client-side route change would keep the order
  // page's stricter headers.
  const handleSignTrf = useCallback(() => {
    if (!urlOrderId) return
    Sentry.startSpan({ op: 'ui.click', name: 'Sign TRF' }, () => {
      // Stash the signing intent before leaving (GEN-5259): the optimistic cue
      // must survive the hard navigation to /sign and the full-page return, so it
      // is recorded in sessionStorage rather than React state.
      markTrfSigningInProgress(urlOrderId)
      window.location.assign(trfSigningPath(urlOrderId))
    })
  }, [urlOrderId])

  // Best-effort org id for the proband MRN duplicate check in the demographics
  // editor. Read via the raw context (not `useAuthorization`, which throws
  // without a provider) so the page still renders in provider-less test setups;
  // a missing org id simply makes the MRN uniqueness check fail open.
  const orgId = useContext(AuthorizationContext)?.orgId ?? null

  // The order's own organization UUID (from the order payload), used as the
  // `additionalUuidCandidate` for the MRN uniqueness lookup so a demographics
  // edit isn't blocked when the auth orgId is a non-UUID client key and
  // token-based IMDB org resolution fails (Epic flows). Mirrors OrderStart.
  const orderOrgUuidCandidate =
    (orderData as { organizationId?: string; institution?: { id?: string } } | null)?.organizationId ??
    (orderData as { organizationId?: string; institution?: { id?: string } } | null)?.institution?.id ??
    undefined

  const handleLogout = () => {
    auth0Logout({ logoutParams: { returnTo: window.location.origin + '/login' } })
  }
  const [clinicalSummaryModalOpen, setClinicalSummaryModalOpen] = useState(false)
  // Remounts the editor so a conflict reload or a discard rebuilds it from canonical
  // rather than leaving the previous instance's merge state on screen.
  const [clinicalSummaryEditorEpoch, setClinicalSummaryEditorEpoch] = useState(0)
  const latestClinicalSummaryEditorEpoch = useRef(clinicalSummaryEditorEpoch)
  latestClinicalSummaryEditorEpoch.current = clinicalSummaryEditorEpoch
  const clinicalEditorControls = useRef<ClinicalSummaryEditorControls>(null)
  // A submitted order stages edits in canonical and ships them via Send Updates, so
  // "saved" here means staged — the same thing the modal's autosave used to report.
  const [clinicalEditorLastSaveTime, setClinicalEditorLastSaveTime] = useState<number | null>(null)
  const noteClinicalEditorSave = useCallback(() => {
    setClinicalEditorLastSaveTime(Date.now())
  }, [])
  const [familyHistorySummaryModalOpen, setFamilyHistorySummaryModalOpen] = useState(false)
  const [guardianContactModalOpen, setGuardianContactModalOpen] = useState(false)
  // GEN-5654: the Guardian Contact modal was opened by a comparator editor because
  // the platform collects consent to test and the order has no guardian contact.
  const [guardianContactRequiredForComparator, setGuardianContactRequiredForComparator] = useState(false)
  const requestGuardianContactForComparator = useCallback(() => {
    setGuardianContactRequiredForComparator(true)
    setGuardianContactModalOpen(true)
  }, [])
  // F.1c (GEN-2999): canonical Comparator edit state. Populated by the
  // <ComparatorList> edit-pencil affordance or a validation fix-me link
  // (`openComparatorEditorForParent`); opens <ComparatorModal> with the picked
  // row's comparator. P8 (MOTHER/FATHER migration): editor state is keyed by
  // the comparator itself (which carries `id` + `relationship`); the legacy
  // `side: 'mother' | 'father'` discriminator was removed because the save
  // handler derives the parent side from `updated.relationship` and any other
  // consumer can call `parentSideForComparator(state.comparator)`.
  const [comparatorEditState, setComparatorEditState] = useState<{
    comparator: Comparator
  } | null>(null)
  /**
   * GEN-3152 Phase 2: add/edit a non-parent family member (e.g. a legal
   * guardian who consents but is not sequenced). `comparator === null` opens
   * the blank add flow; an existing comparator opens the edit flow. Kept
   * separate from `comparatorEditState` (the mother/father pencils) so the
   * parent flow is untouched. Saving only dispatches `upsertComparator` —
   * non-parent relationships never touch the legacy mother/father slots.
   */
  const [memberEditState, setMemberEditState] = useState<{
    comparator: Comparator | null
  } | null>(null)
  /**
   * F.1c (GEN-2999) Stage 5: opens the canonical <ComparatorModal> for a parent
   * side. Replaces the legacy <BiologicalMother/FatherModal> openers (the
   * validation fix-me links). Plan C (C-read-4): resolve the parent through the
   * read adapter, which finds the comparator (by side — relies on the
   * MOTHER / FATHER 1-of-1 invariant) and enriches Sample Type / consent /
   * demographicId from the legacy slot at the adapter boundary. Falls back to a
   * bare comparator when neither a comparator nor a slot exists yet.
   */
  const openComparatorEditorForParent = useCallback(
    (side: 'mother' | 'father') => {
      const co = appStore.getState().canonicalOrder.canonicalOrder
      const relationship = side === 'mother' ? 'MOTHER' : 'FATHER'
      const comparator: Comparator = getParentComparator(co, side) ?? { relationship }
      setComparatorEditState({ comparator })
    },
    [appStore]
  )
  // Post-accession comparator-addition window (tenant-gated by
  // `editabilityWindowEnabled`). Open only when the order is accessioned
  // (`SAMPLE_RECEIVED`), is an ULTRA-RAPID (urWGS) order, the feature is enabled
  // for the org, and we are before the configured deadline (accession time +
  // `comparatorAdditionWindowHours`). When open, it re-opens EXACTLY ONE
  // affordance the status gate would freeze at accession: adding NEW comparators
  // (via `canEditSequencingComparators`, the sequencing/add-comparator entry
  // point). All proband fields and existing-member edits stay locked — this
  // never feeds `canEditIndications` or `canEditComparatorParents` (which govern
  // edits/removals). Fails safe to closed for non-ultra-rapid orders,
  // unmigrated/deployed configs (feature disabled), or a missing/invalid
  // accession timestamp / window duration. The hook also keeps this state live
  // across the deadline (flips open→closed on an idle page via a timer).
  const { open: editabilityWindowOpen, window: comparatorAddWindow } = useComparatorAdditionWindow({
    orderStatus,
    selectedOption: storedCanonical?.order?.selectedOption,
    accessionedOn,
    config,
  })

  const canEditIndicationsRaw = isOrderEditableForStrategy(orderStatus, getFulfillmentStrategy(config))
  // Reanalysis read-only lock: once an order's backend status is "reanalysis"
  // (an inflection_test order whose first, still-`complete` reanalysis edit has
  // been submitted), a plain load of the route (WITHOUT `?reanalysis=true`) must
  // present the order fully read-only — no status bar, no Cancel Order, no
  // edits (incl. comparator add/edit/remove), no Send Updates. The
  // `?reanalysis=true` edit session (status still `complete`) is unaffected
  // because `isReanalysis` is true there. Defined here (before the comparator
  // gates below) so every editability chokepoint can reference it.
  const reanalysisLocked =
    rawBackendStatus === OrderStatus.REANALYSIS &&
    !isReanalysis &&
    getFulfillmentStrategy(config) === 'inflection_test'
  const canEditSequencingComparators =
    !reanalysisLocked &&
    (orderStatus === OrderStatusDisplay.ORDER_SUBMITTED || editabilityWindowOpen)
  // Clinical / indications / family-history editability.
  //   - rcigm: unchanged — the local status gate (placed/submitted only).
  //   - inflection_test: same strategy-aware window as comparator edits
  //     (`isOrderEditableForStrategy`): editable through accessioned + complete,
  //     blocked only at cancelled (Rule 2). Existing-member HPO editing is
  //     further gated by the SeqOne lock via `canEditExistingHpo` below.
  // (Previously gated on `editWindowEditable` from GET /orders/{id}/edit-window,
  // which fail-safes to read-only and locked ALL proband clinical fields.)
  const clinicalFieldsEditableRaw =
    getFulfillmentStrategy(config) === 'inflection_test'
      ? isOrderEditableForStrategy(orderStatus, 'inflection_test')
      : isOrderEditable(orderStatus)

  // Editability API gate for inflection_test (LSMC) orgs. The hook only fetches
  // for inflection_test, calling provider-api's `GET /orders/{id}/editability`
  // (which proxies mc-api, the SOURCE OF TRUTH). For rcigm it returns `null`
  // ("not applicable"); while loading / on error it fails safe to `true`.
  const { editable: apiOrderEditable } = useIsOrderEditable({
    fulfillmentStrategy: getFulfillmentStrategy(config),
    orderId: urlOrderId,
    caller: getCallerFromOrderData(orderData),
  })

  // AUTHORITATIVE semantics for inflection_test: when mc-api returns a definite
  // answer (`true`/`false`), it fully governs editability in BOTH directions —
  // `false` LOCKS the clinical/indications/demographics + comparator sections
  // and Send Updates regardless of the local status window; `true` opens them.
  // `null` (rcigm, or the hook not applicable) leaves the local status gate in
  // charge. The reanalysis read-only lock still hard-overrides everything.
  //
  // `resolveEditable(rawGate)` returns the authoritative decision for an
  // inflection_test order with a definite API answer, else the local `rawGate`.
  const isInflectionTest = getFulfillmentStrategy(config) === 'inflection_test'
  const resolveEditable = (rawGate: boolean): boolean =>
    isInflectionTest && apiOrderEditable !== null ? apiOrderEditable : rawGate

  const canEditIndications = !reanalysisLocked && resolveEditable(canEditIndicationsRaw)
  const clinicalFieldsEditable = !reanalysisLocked && resolveEditable(clinicalFieldsEditableRaw)
  const [isOrderFormModalOpen, setIsOrderFormModalOpen] = useState(false)
  const [patientDemographicsEditOpen, setPatientDemographicsEditOpen] = useState(false)
  // Latches once the proband demographics are edited locally this session. Unlike
  // `hasPatientDemographicsChanges` (which clears after a successful Send Updates),
  // this stays true so the header keeps preferring the canonical name/MRN/DOB over
  // the stale `orderData`/nav-state snapshots — which are NOT refreshed post-send —
  // until a full reload re-derives them from IMDB.
  const [demographicsLocallyEdited, setDemographicsLocallyEdited] = useState(false)
  // GEN: EHR demographic drift. When a submitted order is reopened from an Epic
  // launch, we re-fetch the proband's demographics from the EHR and compare them
  // to what was saved on the order. If name/DOB/sex/MRN differ, the drift modal
  // offers to push the EHR values to the lab. `ehrDriftFields` drives the modal's
  // field-by-field list; `ehrDriftMergePatch` is the canonical-patient patch
  // (drifted fields ONLY) merged into the patient and then sent when the user
  // accepts — never the full EHR snapshot, so blank EHR values can't overwrite
  // good saved fields. Guarded so it fires at most once per loaded order.
  const [ehrDriftFields, setEhrDriftFields] = useState<EhrDemographicsFieldDiff[]>([])
  const [ehrDriftMergePatch, setEhrDriftMergePatch] = useState<EhrDemographicsMergePatch | null>(null)
  const [ehrDriftModalOpen, setEhrDriftModalOpen] = useState(false)
  const ehrDriftCheckedForOrderRef = useRef<string | null>(null)
  // Always-current view of the "can the order still be updated?" gate, so the
  // async EHR drift completion path and the modal confirm handler can re-check
  // it AFTER the fetch resolved. `canAddOrSendComparator` / `isCancelled` /
  // `reanalysisLocked` are captured in the effect closure at fetch-start time;
  // if any flips (order cancelled, reanalysis lock lands) while the fetch is in
  // flight, the stale closure would still open the modal and confirm would
  // `mergePatient` even though `handleUpdateOrder` bails — leaving unsent local
  // EHR edits on a locked order. Reading this ref instead keeps both paths honest.
  const ehrDriftSendableRef = useRef(false)
  // Seed values for the post-submit proband demographics editor (`NewOrderModal`
  // in `edit` variant). Mirrors OrderStart's `newOrderPatientEditInitial`; sourced
  // from the canonical patient so it always reflects the latest in-session edits.
  // `null` when there is no patient yet, so the modal simply does not render.
  const patientDemographicsEditInitial = useMemo((): OrderFormData | null => {
    const p = storedCanonical?.patient
    if (!p) return null
    // Canonical stores DOB as `yyyy-mm-dd`; pass it straight through (an
    // `<input type="date">` wants exactly that). Only fall back to a parse for a
    // non-ISO value, and build the string from LOCAL parts so a date-only value
    // is not shifted a day by UTC interpretation.
    let dateStr = ''
    if (p.dateOfBirth) {
      const iso = /^\d{4}-\d{2}-\d{2}/.exec(p.dateOfBirth)
      if (iso) {
        dateStr = iso[0]
      } else {
        const date = new Date(p.dateOfBirth)
        if (!isNaN(date.getTime())) {
          const y = date.getFullYear()
          const m = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          dateStr = `${y}-${m}-${day}`
        }
      }
    }
    const orderingPhysician =
      `${auth0User?.given_name ?? ''} ${auth0User?.family_name ?? ''}`.trim() ||
      auth0User?.name ||
      auth0User?.email ||
      ''
    return {
      patientFirstName: p.firstName ?? '',
      patientLastName: p.lastName ?? '',
      dateOfBirth: dateStr,
      mrn: p.mrn ?? '',
      sex: (p.sex as Sex) ?? Sex.Unknown,
      orderingPhysician,
    }
  }, [storedCanonical?.patient, auth0User])

  // Persist proband demographic edits into the canonical order. Send Updates
  // serializes the full canonical (incl. `.patient`), so a `mergePatient` here is
  // all that is needed. Preserves the existing `middleName` (not editable in the
  // modal). Canonical is the source of truth on this page, so — unlike OrderStart
  // — there is no local `orderFormInfoData`/`patientInfo` to keep in sync.
  const handlePatientDemographicsSave = useCallback(
    (data: OrderFormData) => {
      const existingMiddle = storedCanonical?.patient?.middleName
      dispatch(
        mergePatient({
          firstName: data.patientFirstName,
          lastName: data.patientLastName,
          middleName: existingMiddle?.trim() || null,
          mrn: data.mrn,
          sex: String(data.sex),
          dateOfBirth: data.dateOfBirth,
        })
      )
      setDemographicsLocallyEdited(true)
      setPatientDemographicsEditOpen(false)
    },
    [dispatch, storedCanonical?.patient?.middleName]
  )
  /**
   * Bumped each time the "View Order Form" modal is opened so the viewer re-fetches a freshly
   * regenerated TRF from the current canonical (see the regenerate path in `RequisitionFormView`).
   * Without this, re-opening after further edits would reuse the previous fetch.
   */
  const [orderFormRegenerateToken, setOrderFormRegenerateToken] = useState(0)
  const [sendUpdatesConfirmOpen, setSendUpdatesConfirmOpen] = useState(false)
  // Authoritative reanalysis decision from provider-api for the pending edit
  // (POST /orders/{id}/reanalysis-preview). The SPA no longer computes whether an
  // edit triggers a reanalysis — the backend owns the decision + enforcement
  // (eligibility, in-flight / per-order cap). `null` until previewed / when not a
  // reanalysis session; the UI fails safe to "no reanalysis" on absent data.
  //
  // The decision is stored alongside the exact signals it was computed for
  // (`signalsKey`) so the modal can tell a FRESH decision from a STALE one: when
  // the user toggles edits (e.g. adds a comparator after a clinical-only edit),
  // the signals change and the previously-returned decision no longer describes
  // the pending edit. A stale/pending decision must not drive the modal copy, or
  // it would promise a reanalysis the commit won't fire (and vice-versa). See the
  // `reanalysisDecisionFresh` gate below (`freshReanalysisDecision`).
  const [reanalysisDecision, setReanalysisDecision] = useState<{
    decision: ReanalysisDecision
    signalsKey: string
  } | null>(null)
  const [editOrderMenuOpen, setEditOrderMenuOpen] = useState(false)
  const editOrderMenuRef = useRef<HTMLDivElement>(null)
  const [cancelMenuOpen, setCancelMenuOpen] = useState(false)
  const cancelMenuRef = useRef<HTMLDivElement>(null)

  /**
   * Which declined envelope's reason the header is currently showing.
   *
   * Held as the envelope's identity rather than a boolean so that navigating to
   * another order closes the row on its own: the page stays mounted across that
   * switch, and an open flag would carry over and reveal the next order's reason
   * unasked.
   */
  const [declineReasonOpenFor, setDeclineReasonOpenFor] = useState<string | null>(null)
  const declineReasonKey =
    trfSignature?.status === 'declined' ? (trfSignature.externalDocumentId ?? urlOrderId ?? '') : null
  /**
   * The reason row and the badge stand or fall together: the only control that
   * closes the row lives on the badge, so a row shown while the preparing cue has
   * taken the badge's place would be undismissable — and would contradict it,
   * since reading the reason and sending updates is exactly what puts a reissue
   * in flight. The same flag drives both the row and the badge's control, so the
   * two cannot drift apart. Reopening is remembered: if the reissue never lands
   * and the declined envelope comes back, so does the row the clinician opened.
   */
  const declineReasonOpen =
    !trfSignaturePreparingState && declineReasonKey !== null && declineReasonKey === declineReasonOpenFor
  const toggleDeclineReason = () =>
    setDeclineReasonOpenFor((current) => (current === declineReasonKey ? null : declineReasonKey))

  // Reanalysis: navigate back to the completed order's results page. Only wired
  // into the actions menu when `isReanalysis` (the order is complete, so results
  // exist). Uses `urlOrderId` (the route's order id).
  const handleViewReport = () => {
    setCancelMenuOpen(false)
    if (!urlOrderId) return
    navigate(`/order/results/${encodeURIComponent(urlOrderId)}`)
  }
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [comparatorRemovalConfirmOpen, setComparatorRemovalConfirmOpen] = useState(false)
  // Snapshot of the comparator being removed, captured when the confirm dialog
  // opens. The dialog is non-blocking, so the live `selectedComparator*` could
  // change (or clear) while it's open; the snapshot pins the title and the
  // confirm side effects to the row the provider actually clicked Remove on.
  const [comparatorPendingRemoval, setComparatorPendingRemoval] = useState<{
    id: string
    comparator: Comparator
  } | null>(null)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [indications, setIndications] = useState({ phenotypes: [] as HPOTerm[] })
  const { resetIndicationLabelResolution } = useResolveIndicationHpoLabels(indications, setIndications)
  const baselineIndicationsRef = useRef<string | null>(null)
  const baselineGuardianRef = useRef<string | null>(null)
  const baselineClinicalRef = useRef<string | null>(null)
  const baselineFamilyRef = useRef<string | null>(null)
  const baselineComparatorsRef = useRef<string | null>(null)
  /**
   * Identity keys ({@link comparatorIdentityKeys}) of the comparators present
   * in the current baseline (last-sent-to-lab shape). A removed comparator only
   * counts as a *pending cancellation* (surfaced in the Send Updates modal) when
   * its identity is in this set — a comparator added and removed within the same
   * session was never sent, so cancelling it is a no-op the provider need not
   * confirm. Rebuilt alongside every `baselineComparatorsRef` update.
   */
  const baselineComparatorKeysRef = useRef<Set<string>>(new Set())
  /**
   * Whether {@link baselineComparatorKeysRef} has been populated from a real
   * fetch/send for the CURRENT order. It starts `false` and is reset to `false`
   * on every order switch, because the keys set is empty on mount and stale after
   * a switch — and `isNewlyAddedComparator` treats a comparator as "new" whenever
   * none of its keys are in the (empty/stale) baseline. Without this gate a
   * persisted comparator would be misclassified as newly-added and briefly gain
   * Edit/Remove affordances during the editability window until the fetch lands.
   * The "newly-added" determination is only trusted once this is `true`.
   */
  const baselineComparatorKeysReadyRef = useRef(false)
  /**
   * GEN-5654: sticky identity keys of comparators first seen as new in this page
   * session. Unlike {@link baselineComparatorKeysRef} it is NOT advanced by Send
   * Updates, so a comparator added after submit keeps needing platform-collected
   * contact once it lands in the last-sent baseline. Order-scoped (cleared on switch).
   * Residual gap: lost on reload (the server exposes no per-comparator marker).
   */
  const addedAfterSubmitKeysRef = useRef<Set<string>>(new Set())
  // True while the current session holds at least one NEW (added-this-session,
  // not in the last-sent baseline) comparator that has not yet been sent to the
  // lab. `baselineComparatorKeysRef` is a ref read during render, but every path
  // that mutates it (initial load, add-comparator, successful Send Updates) also
  // updates comparator state, so a re-render always follows and this stays fresh.
  //
  // This is the "in-flight adds" grace signal: the editability *window* governs
  // whether NEW comparators may be STARTED (and the idle deadline timer closes it
  // on its own), but a comparator added seconds before the cutoff must not be
  // orphaned — its Edit pencils and the Send Updates button have to survive the
  // window closing so the provider can finish and submit the change they already
  // began. See `comparatorEditingActive` / `canEditComparatorRow` below.
  //
  // Gated on `baselineComparatorKeysReadyRef` so we never trust the "newly-added"
  // determination while the baseline is empty (mount) or stale (post-switch),
  // which would otherwise misclassify persisted comparators as new.
  //
  // The PROBAND self-row is excluded to match `buildComparatorBaselineKeySet`,
  // which never adds proband keys to the baseline: without this filter the
  // proband would always read as "newly added" and keep the grace path open
  // forever (Send Updates / Edit Order menu staying available with no real
  // unsent comparator add). Only genuine comparator (non-proband) additions
  // should extend the window.
  const hasUnsentNewComparators =
    baselineComparatorKeysReadyRef.current &&
    (storedCanonical?.comparators ?? []).some(
      (c) => c?.relationship !== 'PROBAND' && isNewlyAddedComparator(c, baselineComparatorKeysRef.current)
    )
  // Grace-extended edit/send state: open while the window is open OR while
  // unsent, newly-added comparators remain. Unlike `editabilityWindowOpen`
  // (which alone still governs STARTING new adds via `canEditSequencingComparators`
  // / `canAddComparatorsForOrder`), this keeps the finish-and-send affordances
  // (`canAddOrSendComparator`, per-row Edit pencils) alive past the deadline so
  // in-flight adds are never stranded. Collapses back once the adds are sent
  // (baseline absorbs them ⇒ `hasUnsentNewComparators` → false) or discarded.
  const comparatorEditingActive =
    !reanalysisLocked && (editabilityWindowOpen || hasUnsentNewComparators)
  // Affordances needed to add + send a NEW comparator during the accession
  // editability window, WITHOUT unlocking any proband-field edits. During the
  // window (or the normal pre-accession editable states) the Edit Order menu
  // trigger and the Send Updates button must render so a freshly-added
  // comparator can be submitted; the individual proband edit items inside the
  // menu remain gated on `canEditIndications` / `clinicalFieldsEditable` and
  // stay hidden. Uses `comparatorEditingActive` (not the raw window) so an
  // in-flight add started before the deadline can still be edited and sent after
  // the window closes.
  const canAddOrSendComparator =
    !reanalysisLocked && (canEditIndications || comparatorEditingActive)
  const baselinePatientConsentRef = useRef<string | null>(null)
  const baselinePatientDemographicsRef = useRef<string | null>(null)
  const sequencingBaselineRef = useRef<string | null>(null)
  /**
   * Forces a re-render whenever `sequencingBaselineRef.current` is mutated.
   * Mutating a ref does not re-render, but the baseline is read during render
   * (e.g. the `TestOptionsModal` `discardBaseline` prop reads
   * `sequencingBaselineRef.current`), so this state bump makes the UI reflect a
   * fresh baseline after a successful Send Updates. The counter value also gates
   * the sequencing dirty-detection effect so it re-evaluates against a freshly
   * captured baseline (fetch/hydrate/send).
   */
  const [sequencingBaselineVersion, setSequencingBaselineVersion] = useState(0)
  const bumpSequencingBaselineVersion = useCallback(() => {
    setSequencingBaselineVersion((v) => v + 1)
  }, [])
  const prevSequencingDirtyRef = useRef(false)
  const hasHydratedFormFromBackend = useRef(false)
  /**
   * Identity keys (see `comparatorIdentityKey`) of comparators the user removed
   * during this session. A later re-hydration re-runs
   * `overlayRichComparatorsFromRfd`, which re-projects the authoritative
   * `requisitionFormData.comparators` and would otherwise resurrect a
   * just-removed non-parent (extended relative / sibling). We filter these keys
   * out of every hydration dispatch so a removal is durable until it is
   * persisted (the next Send Updates), matching the "persisted by omission"
   * contract of `removeComparator`.
   */
  const removedComparatorKeysRef = useRef<Set<string>>(new Set())
  /**
   * Comparators the user removed this session that are pending cancellation
   * (persisted-by-omission on the next Send Updates). Each entry carries the
   * display `label` and ALL identity keys ({@link comparatorIdentityKeys}) of
   * the removed row. Surfaced in the Send Updates confirmation modal so the
   * provider sees which comparators will be removed.
   *
   * Matching a re-added row uses key-set OVERLAP (any shared key), not the
   * removed row's primary key: Add Comparator assigns a fresh client `id` via
   * `genComparatorId`, so the re-added row shares only the `demo:` / `rnd:`
   * identity with the removed one. Storing the full key set (and clearing on
   * overlap) mirrors the session-removed guard so a remove-then-re-add reliably
   * drops the stale "will be removed" notice.
   *
   * Cleared on order change, on re-add of the same person, and after a
   * successful send.
   */
  const [pendingComparatorCancellations, setPendingComparatorCancellations] = useState<
    PendingComparatorCancellation[]
  >([])
  const [hasIndicationsChanges, setHasIndicationsChanges] = useState(false)
  const [hasGuardianChanges, setHasGuardianChanges] = useState(false)
  const [hasClinicalSummaryChanges, setHasClinicalSummaryChanges] = useState(false)
  const [hasFamilyHistoryChanges, setHasFamilyHistoryChanges] = useState(false)
  // GEN-3393: dirty detection covers ALL comparators (parents AND non-parent
  // members) plus the proband consent block, so a consent edit on any of them
  // enables "Send Updates". Previously only MOTHER / FATHER were diffed.
  const [hasComparatorChanges, setHasComparatorChanges] = useState(false)
  const [hasPatientConsentChanges, setHasPatientConsentChanges] = useState(false)
  const [hasPatientDemographicsChanges, setHasPatientDemographicsChanges] = useState(false)
  const [hasSequencingChanges, setHasSequencingChanges] = useState(false)
  /** Last-sent-to-lab test selection (`order.selectedOption`); drives upgrade-only radio list in sequencing modal. */
  const [persistedSequencingTestType, setPersistedSequencingTestType] = useState('')
  const [testOptionsComparatorModalOpen, setTestOptionsComparatorModalOpen] = useState(false)
  const [sequencingModalKey, setSequencingModalKey] = useState(0)
  const [sequencingModalPrevOptions, setSequencingModalPrevOptions] = useState<TestOptions | null>(null)
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false)
  const [updateError, setUpdateError] = useState<string | null>(null)
  /**
   * Resolved human-readable labels for parent comparator HPO terms keyed by id.
   * Populated lazily for legacy canonical rows whose `label` is the HP code itself
   * (e.g. `HP:0010942`) so the FamilyCard summary renders the same readable text
   * shown by Indications for Testing rather than raw codes.
   */
  const [parentHpoLabelOverrides, setParentHpoLabelOverrides] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!editOrderMenuOpen) return
    function handleClickOutside(event: MouseEvent) {
      if (editOrderMenuRef.current && !editOrderMenuRef.current.contains(event.target as Node)) {
        setEditOrderMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [editOrderMenuOpen])

  useEffect(() => {
    if (!cancelMenuOpen) return
    function handleClickOutside(event: MouseEvent) {
      if (cancelMenuRef.current && !cancelMenuRef.current.contains(event.target as Node)) {
        setCancelMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [cancelMenuOpen])

  // Reset fetch flag and indications baseline when order/patient changes (e.g. opening different order from workspace)
  useEffect(() => {
    hasInitiallyFetched.current = false
    // Seed the on-screen order id from the route (GEN-5494) so a Send Updates
    // whose result arrives before this order's fetch resolves still compares
    // against the right order. The fetch handler refreshes it to the resolved
    // order's id (which the patient-route load only learns from the response).
    currentOrderIdRef.current = urlOrderId ?? null
    baselineIndicationsRef.current = null
    baselineGuardianRef.current = null
    baselineClinicalRef.current = null
    baselineFamilyRef.current = null
    baselineComparatorsRef.current = null
    // The comparator identity-key baseline is order-scoped. Clear it AND mark it
    // not-ready so the previous order's keys can't drive the newly-added/edit
    // determination for the next order until its own fetch repopulates them.
    baselineComparatorKeysRef.current = new Set()
    baselineComparatorKeysReadyRef.current = false
    addedAfterSubmitKeysRef.current = new Set()
    baselinePatientConsentRef.current = null
    baselinePatientDemographicsRef.current = null
    /**
     * Sequencing dirty detection (and `comparatorModalAllowedTypes`) read from
     * `sequencingBaselineRef`/`persistedSequencingTestType`. If either is left
     * over from the previous order, the new order would compare against the
     * old baseline — falsely flagging `hasSequencingChanges` and enabling
     * "Send Updates" without any user edits.
     */
    sequencingBaselineRef.current = null
    bumpSequencingBaselineVersion()
    prevSequencingDirtyRef.current = false
    setPersistedSequencingTestType('')
    /** Parent HPO label cache is also order-scoped; clear so legacy labels
     *  from the previous order don't bleed into the new one before resolution. */
    setParentHpoLabelOverrides({})
    // Session-removed comparators are order-scoped; clear so a removal on the
    // previous order can't strip a same-identity comparator on the next one.
    removedComparatorKeysRef.current = new Set()
    /**
     * The signature badge is order-scoped and only written when the order-details
     * fetch succeeds. Clearing it here is what keeps a failed fetch from leaving the
     * previous order's status on screen: the catch below only reports to Sentry, so
     * without this the next order would render the last one's badge indefinitely.
     * An unknown status shows nothing, which is the honest fallback.
     */
    setTrfSignature(null)
    // Submit-recency timestamp is order-scoped (GEN-5027): clear it so the
    // previous order's recency can't keep the `null` preparing window open on
    // the next order until its own fetch writes a fresh `updated_at`.
    setOrderUpdatedAt(null)
    // The TRF "sent" instant is order-scoped (GEN-5494): clear it so the previous
    // order's banner time can't linger on the next order until its own fetch
    // writes a fresh value. Null renders the banner with no date, never `new Date()`.
    setTrfSentAt(null)
    // The reissue watch is order-scoped too (GEN-5027), and `useReissueWatch`
    // resets it by keying its restore on the order id: the next order reads back
    // its own watch, or null when it has none. Clearing it by hand here would
    // land in the same render and overwrite that restore.
    // Accession timestamp is order-scoped and only (re)written when the status
    // fetch for THIS order resolves. Clear it here so the previous order's
    // accession time can't drive the comparator-addition window or the
    // "accessioned on" banner on the next order until its own status fetch lands.
    setAccessionedOn(null)
    setPendingComparatorCancellations([])
    // EHR demographic drift is order-scoped: clear the one-shot guard and any
    // pending drift so a new order re-checks against its own EHR values and the
    // previous order's drift modal can't carry over.
    ehrDriftCheckedForOrderRef.current = null
    setEhrDriftFields([])
    setEhrDriftMergePatch(null)
    setEhrDriftModalOpen(false)
    // A new order has not hydrated yet; block the EHR drift check until this
    // order's fetch repopulates the canonical and captures its baseline.
    setOrderHydrated(false)
  }, [urlPatientId, urlOrderId, bumpSequencingBaselineVersion])

  // Fetch order data and HPO terms from API
  useEffect(() => {
    let cancelled = false
    const fetchOrder = async () => {
      // GEN-1277: Prefer order_id over patient_id for fetching
      const fetchId = urlOrderId || urlPatientId
      if (!fetchId) {
        // Nothing to load: never leave a loader / failure from a previous id up.
        setIsLoadingOrder(false)
        setOrderLoadFailure(null)
        return
      }

      // Skip if already fetched for this order
      if (hasInitiallyFetched.current) {
        return
      }

      // Skip only a duplicate request for this same id; a fetch still in flight
      // for a previous order must not block this one.
      if (inFlightFetchIdRef.current === fetchId) {
        return
      }

      // Check if we already have order header data from location.state (still fetch to hydrate canonical)
      const hasOrderDataFromState = !!(
        typeof locationState === 'object' &&
        locationState !== null &&
        locationState.orderData
      )

      inFlightFetchIdRef.current = fetchId
      setIsLoadingOrder(true)
      setOrderLoadFailure(null)
      try {
        // Determine caller based on auth flow. Prefer navigation state, then the
        // resolved (persisted) Epic state so a reload / deep link still recovers
        // the Epic context instead of falling back to the standalone caller.
        const epicStateForCaller =
          (typeof locationState === 'object' && locationState !== null
            ? (locationState.epicState ?? (locationState as { state?: string }).state)
            : null) ?? epicState
        const epicContext = epicStateForCaller
          ? EpicAuthClient.getInstance().getContext(epicStateForCaller)
          : null
        const caller = resolveOrderFetchCaller(epicContext, user)

        // GEN-1277: Use order_id if available, fall back to patient_id
        const order = urlOrderId ? await getOrderById(urlOrderId, caller) : await getOrderByPatient(fetchId, caller)

        /**
         * The params moved on while this was in flight, so this response describes an
         * order the user is no longer looking at; writing here would stick the previous
         * order's data — signature status included — onto the order that replaced it.
         * The replacement fetch has already started (the in-flight guard above is keyed
         * by fetch id), so dropping this response loses nothing. Mirrors the reports
         * fetch below.
         */
        if (cancelled) return

        // 404 on the order-id route = missing or not in the caller's org
        // (server-side `enforce_order_in_caller_org`). Render the shared
        // not-found screen instead of an empty order form.
        if (urlOrderId && order === null) {
          setOrderLoadFailure('not_found')
          return
        }

        const resolvedExternalId = inflectionExternalIdFromOrder(order)
        if (resolvedExternalId) {
          setInflectionExternalId(resolvedExternalId)
        }

        // Record the resolved order id so the post-save banner guard compares
        // against the order actually on screen (GEN-5494) — the patient-route
        // load only learns this id from the response.
        currentOrderIdRef.current = order?.id ?? urlOrderId ?? null

        setTrfSignature(orderTrfSignatureFrom(order))
        // Capture submit recency for the `null`-preparing bound (GEN-5027).
        setOrderUpdatedAt(order?.updated_at ?? null)
        // GEN-5494: the persisted TRF "sent" instant. Pick the first of
        // [trf_sent_at, created_at] that parses, so a dropped/garbage trf_sent_at
        // still shows the created_at fallback rather than no date or the viewer's
        // current time. Both getOrderById and getOrderByPatient shapes feed here.
        setTrfSentAt(firstParsableTrfSentAt(order?.trf_sent_at, order?.created_at))

        if (order?.json_payload) {
          const jp = order.json_payload
          if (jp.orderFormInfo && !hasOrderDataFromState) {
            setFetchedOrderData(jp.orderFormInfo)
          }

          setOrderStatus(mapOrderStatusToDisplay(order.status))
          setRawBackendStatus(order.status)

          // Canonical-first orders may omit orderFormInfo; API still merges IMDB secondary_consent into
          // json_payload.canonicalOrder — always merge so the UI shows proband consent after submit.
          const merged = mergeCanonicalFromOrderJsonPayload(jp)
          if (merged) {
            // `setCanonicalOrder` folds populated mother/father slots into
            // `comparators[]` (normalizeInboundParentComparators), and the R5
            // side-nav backfills client ids on any comparator that arrives
            // without one. Capture the baseline from that SAME id-backfilled,
            // normalized shape Redux settles on, otherwise the folded parent (or
            // a freshly-backfilled id) reads as a phantom comparator change and
            // falsely shows "Updates are ready to send" on load.
            const normalized = backfillComparatorIds(normalizeInboundParentComparators(merged))
            // GEN-3xxx: some orders were persisted before the create-path seed
            // reliably wrote `order.selectedOption`; on reload a blank value
            // leaves `selectedTestTypeConfig` undefined and permanently disables
            // comparator editing. Recover a UI-gating SKU from the order's own
            // orderType (server still enforces the authoritative SKU on submit).
            const recoveredOption = recoverSelectedOptionForCanonical(normalized, config)
            const hydratedBase =
              recoveredOption && !normalized.order?.selectedOption?.trim()
                ? { ...normalized, order: { ...(normalized.order ?? {}), selectedOption: recoveredOption } }
                : normalized
            // Cell C: apply the authoritative SeqOne HPO lock from the order ROW
            // (not the possibly-stale canonicalOrder blob). A later SeqOne event
            // flips `order.seqone_hpo_lock`, which the blob written at edit-time
            // never reflects — so the row value must override.
            const hydrated = { ...hydratedBase, seqoneHPOLock: order.seqone_hpo_lock ?? null }
            const hydratedForStore = stripSessionRemovedComparators(hydrated, removedComparatorKeysRef.current)
            dispatch(setCanonicalOrder(hydratedForStore))
            // `useEnforceNonApplicableSecondaryConsentDefaults` runs right after
            // this dispatch and rewrites unset consents to policy defaults
            // (e.g. incidentalFindingsSelection null -> 'opt-out'). Applying the
            // identical enforcement here keeps that normalization from reading
            // as a phantom "unsaved change" that trips the nav guard on load.
            // `setCanonicalOrder` also strips the IMDB `1900-01-01` placeholder
            // DOB from what Redux stores, so strip it here too — otherwise the
            // baseline keeps the placeholder while the store drops it, leaving a
            // permanent phantom comparator/guardian diff (false "Updates are
            // ready to send" + unsaved-changes modal on the Inflection logo).
            const baselineSource = applyHiddenSecondaryConsentDefaults(
              stripPlaceholderDobsFromCanonical(hydrated),
              getPatientConsents(config)
            )
            const baselines = captureCanonicalBaselines(baselineSource)
            baselineIndicationsRef.current = baselines.indications
            baselineGuardianRef.current = baselines.guardian
            baselineClinicalRef.current = baselines.clinical
            baselineFamilyRef.current = baselines.family
            baselineComparatorsRef.current = baselines.comparators
            baselineComparatorKeysRef.current = buildComparatorBaselineKeySet(baselineSource.comparators)
            baselineComparatorKeysReadyRef.current = true
            baselinePatientConsentRef.current = baselines.patientConsent
            baselinePatientDemographicsRef.current = baselines.patientDemographics
            // Sequencing baseline is captured from the SAME fully-hydrated
            // canonical as every other section (not lazily from the first
            // storedCanonical dispatch), so a multi-dispatch hydration can't
            // leave a stale pre-hydration signature that falsely flags
            // "Sequencing option was updated" on load.
            sequencingBaselineRef.current = baselines.sequencing
            bumpSequencingBaselineVersion()
            setPersistedSequencingTestType((baselineSource.order?.selectedOption ?? '').trim())
            // The canonical store now holds THIS order's fully-hydrated patient
            // and the demographics baseline is captured; the EHR drift check may
            // now safely compare against the saved demographics.
            setOrderHydrated(true)
          }

          hasInitiallyFetched.current = true
        } else if (order) {
          hasInitiallyFetched.current = true
        }
      } catch (error) {
        Sentry.captureException(error, { tags: { operation: 'fetch-order-by-patient' } })
        // Order-id route only: a failed load must not fall through to an empty,
        // editable form (patient-only URLs keep their previous behavior).
        if (!cancelled && urlOrderId) {
          // 401 mirrors the scope guard this route used to have: re-login,
          // rather than a dead-end "try again" screen.
          setOrderLoadFailure(error instanceof OrderFetchError && error.status === 401 ? 'unauthorized' : 'error')
        }
      } finally {
        // A superseded fetch owns neither the in-flight marker nor the spinner —
        // the cleanup released the marker and the replacement has since claimed
        // both. Clearing them here would drop the spinner (and later admit a
        // duplicate request) while the replacement is still loading.
        if (!cancelled) {
          inFlightFetchIdRef.current = null
          setIsLoadingOrder(false)
        }
      }
    }

    fetchOrder()
    return () => {
      cancelled = true
      // Release the marker with the run that owned it. The re-run that follows
      // (params or `user` changed) must be free to fetch even when it resolves
      // to the same id, since this run's response is now discarded.
      inFlightFetchIdRef.current = null
    }
    // urlOrderId included so a direct standalone navigation with only an orderId triggers the fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlOrderId, urlPatientId, user])

  /**
   * Cell D (F1) — load the order's reports so the 7-day add-comparator window
   * (`canAddComparators`) actually fires in the live UI. Mirrors the
   * report-loading path on OrderResultReady / OrderGuidance (`getAllOrderReports`).
   * Only fetched for `inflection_test` orders — rcigm never gates on the window,
   * so we skip the request there. Failures fall back to `[]` (addable), matching
   * the no-final-report default; the authoritative gate is provider-api's 409.
   */
  useEffect(() => {
    if (!urlOrderId) {
      setOrderReports([])
      return
    }
    if (getFulfillmentStrategy(config) !== 'inflection_test') {
      setOrderReports([])
      return
    }
    let cancelled = false
    const run = async () => {
      try {
        const caller = getCallerFromOrderData(orderData)
        const reports = await getAllOrderReports(urlOrderId, caller)
        if (!cancelled) setOrderReports(reports)
      } catch (error) {
        if (!cancelled) setOrderReports([])
        Sentry.captureException(error, { tags: { operation: 'order.in-progress.fetch-reports' } })
      }
    }
    void run()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlOrderId, config])

  // Raw change facts the SPA reports to provider-api for the reanalysis decision.
  // The backend owns the decision + enforcement; the SPA only says WHAT changed.
  //   - clinicalInformationModified: HPO/indications or the clinical summary changed.
  //   - comparatorAdded: a NEW (non-proband) comparator was added this session.
  // How these combine into a reanalysis (and how each affects the cap) is a
  // server-owned rule the SPA does not reconstruct — it only reports the facts.
  const reanalysisClinicalInformationModified = hasIndicationsChanges || hasClinicalSummaryChanges
  const reanalysisComparatorAdded = hasUnsentNewComparators
  // Stable key for the current signals. The stored decision is tagged with the
  // key it was computed for; a decision whose key != this is stale (the user
  // changed edits since) and must not drive the modal copy.
  const reanalysisSignalsKey = `${reanalysisClinicalInformationModified ? 1 : 0}:${reanalysisComparatorAdded ? 1 : 0}`

  // Reanalysis session only: ask provider-api what this pending edit WOULD do
  // (POST /orders/{id}/reanalysis-preview) so the Send Updates modal copy matches
  // the committed outcome. The backend applies eligibility + the in-flight /
  // per-order cap gate — the SPA no longer re-derives any of that. Fails safe
  // (null → "no reanalysis") on any error. Only previews when there is a signal
  // to report; a session with no clinical change / comparator add never warrants
  // a reanalysis, so clear the decision rather than call.
  useEffect(() => {
    // Any signal change (or leaving the eligible state) invalidates the previous
    // decision immediately — clear it synchronously so the modal shows neutral
    // "pending" copy during the window before the fresh preview resolves, rather
    // than the previous answer which no longer matches the pending edit.
    setReanalysisDecision(null)
    if (!urlOrderId || !isReanalysis || (!reanalysisClinicalInformationModified && !reanalysisComparatorAdded)) {
      return
    }
    const signalsKey = reanalysisSignalsKey
    const controller = new AbortController()
    let cancelled = false
    const run = async () => {
      try {
        const caller = getCallerFromOrderData(orderData)
        const decision = await getReanalysisPreview(
          urlOrderId,
          {
            clinicalInformationModified: reanalysisClinicalInformationModified,
            comparatorAdded: reanalysisComparatorAdded,
          },
          caller,
          controller.signal
        )
        if (!cancelled) setReanalysisDecision({ decision, signalsKey })
      } catch (error) {
        if (controller.signal.aborted) return
        if (!cancelled) setReanalysisDecision(null)
        Sentry.captureException(error, { tags: { operation: 'order.in-progress.reanalysis-preview' } })
      }
    }
    void run()
    return () => {
      cancelled = true
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlOrderId, isReanalysis, reanalysisClinicalInformationModified, reanalysisComparatorAdded])

  const birthDate = useMemo(() => {
    // A local demographics edit makes the canonical the freshest source, so it
    // must win over the static nav-state / orderData snapshots captured at
    // navigation time (otherwise the header shows the pre-edit DOB until reload).
    // Latched on `demographicsLocallyEdited` (not the dirty flag) so the display
    // stays correct after a successful Send Updates clears the dirty flag.
    if (demographicsLocallyEdited && storedCanonical?.patient?.dateOfBirth) {
      return storedCanonical.patient.dateOfBirth
    }
    const navState = location.state as OrderInProgressLocationState | null | undefined
    if (navState?.patientDemographics?.dateOfBirth) {
      return navState.patientDemographics.dateOfBirth
    }
    if (orderData?.patient?.birthDate) {
      return orderData.patient.birthDate
    }
    if (storedCanonical?.patient?.dateOfBirth) {
      return storedCanonical.patient.dateOfBirth
    }
    return null
  }, [
    demographicsLocallyEdited,
    storedCanonical?.patient?.dateOfBirth,
    orderData?.patient?.birthDate,
    location.state,
  ])

  const patientName = (() => {
    // Prefer the canonical name once the user has edited demographics locally
    // (see `birthDate`), so the header reflects the change immediately.
    if (demographicsLocallyEdited) {
      const edited = formatPatientNameLastFirst(storedCanonical?.patient)
      if (edited) return edited
    }
    if (orderData?.patient?.name?.firstName || orderData?.patient?.name?.lastName) {
      return formatPatientNameLastFirst(orderData.patient.name)
    }
    const navState = location.state as OrderInProgressLocationState | null | undefined
    if (navState?.patientDemographics) {
      return formatPatientNameLastFirst(navState.patientDemographics)
    }
    return formatPatientNameLastFirst(storedCanonical?.patient)
  })()

  const mrn = (() => {
    if (demographicsLocallyEdited && storedCanonical?.patient?.mrn) {
      return storedCanonical.patient.mrn
    }
    const navState = location.state as OrderInProgressLocationState | null | undefined
    if (navState?.patientDemographics?.mrn) return navState.patientDemographics.mrn
    if (orderData?.patient?.mrn) return orderData.patient.mrn
    return storedCanonical?.patient?.mrn ?? ''
  })()
  const mrnDisplay = formatMrnForDisplay(mrn)
  const mrnRaw = rawMrnValue(mrn)
  const ageDisplay = formatAge(birthDate)
  const dobDisplay = formatBirthDate(birthDate)

  const selectedTestTypeConfig = useMemo(
    () => getTestTypeConfig((storedCanonical?.order?.selectedOption ?? '') as TestType, config),
    [config, storedCanonical?.order?.selectedOption]
  )

  const inProgressSequencingTestOptions = useMemo(
    () => (storedCanonical ? testOptionsFromCanonicalOrder(storedCanonical, config) : null),
    [storedCanonical, config]
  )

  // Parent selection lives in `selectedComparatorRelationships`. P4: read
  // each side directly through the relationship accessor instead of
  // destructuring booleans, so the boolean-pair contract isn't a lock-in.
  const inProgressMotherSelected = inProgressSequencingTestOptions
    ? selectionHasRelationship(inProgressSequencingTestOptions, 'MOTHER')
    : false
  const inProgressFatherSelected = inProgressSequencingTestOptions
    ? selectionHasRelationship(inProgressSequencingTestOptions, 'FATHER')
    : false

  // Phase C (MOTHER/FATHER migration): the FCC canonical predicates take the
  // selected-comparator relationship list instead of the retired
  // biologicalMother/Father boolean pair. Derive it from the same in-progress
  // selection those booleans came from so behavior is unchanged.
  const inProgressSelectedParentRelationships = useMemo(
    () => [
      ...(inProgressMotherSelected ? (['MOTHER'] as const) : []),
      ...(inProgressFatherSelected ? (['FATHER'] as const) : []),
    ],
    [inProgressMotherSelected, inProgressFatherSelected]
  )

  // MRN on comparators is required only in the Epic (SMART/EHR) flow; standalone
  // orders treat comparator MRN as optional regardless of fulfillment strategy.
  // Resolved once here and threaded to every comparator completeness / send-gate
  // / modal / side-nav site below.
  const fulfillmentStrategy = getFulfillmentStrategy(config)
  const comparatorMrnRequired = authFlow === 'epic'

  const sequencingComparatorSendReady = useMemo(
    () => comparatorSectionsReadyForSequencingSend(storedCanonical, config, { mrnRequired: comparatorMrnRequired }),
    [storedCanonical, config, comparatorMrnRequired]
  )

  // C1.1: resolved comparator-shaped views for mother / father, preferring
  // `comparators[]` and falling back to a legacy-slot synthesis. Replaces the
  // direct `storedCanonical?.mother` / `storedCanonical?.father` reads
  // throughout the page-level display logic so consent / specimen / clinical
  // metadata that lives on the comparator (but not the legacy slot) renders
  // for old orders too. Baseline/diff snapshots remain on the legacy slot —
  // see "Send Updates" plumbing below.
  const motherComp = useMemo(
    () => FormCompletionChecker.readParent(storedCanonical, 'mother'),
    [storedCanonical]
  )
  const fatherComp = useMemo(
    () => FormCompletionChecker.readParent(storedCanonical, 'father'),
    [storedCanonical]
  )

  // Parent-section completeness for the Send-Updates banner. Aligned with the
  // R5 comparator editor / side-nav predicate (`isCanonicalComparatorComplete`)
  // so a parent the modal accepts can't leave the banner flagging "needs input"
  // — the editor treats DOB and Phenotypic Trait as optional, so requiring them
  // here (via the legacy `isBiologicalParentComplete` clinical-identity gate)
  // would strand fully-entered parents. MRN is required only for the `rcigm`
  // strategy (comparatorMrnRequired); `inflection_test` treats it as optional
  // (see the `mrnRequired` note on the ComparatorModal below).
  const motherComparatorSectionComplete = useMemo(() => {
    if (!inProgressMotherSelected) return true
    const consents = getPatientConsents(config)
    const gates = resolveConsentInteractionGates(consents)
    return FormCompletionChecker.isCanonicalComparatorComplete(motherComp, {
      incidentalApplicable: gates.incidentalRequireInteraction,
      mrnRequired: comparatorMrnRequired,
      probandResearchContactConsent: storedCanonical?.patient?.researchContactConsent === true,
      nyRetentionRequireInteraction: gates.nyRetentionRequireInteraction,
      researchContactRequireInteraction: gates.researchContactRequireInteraction,
    })
  }, [
    motherComp,
    inProgressMotherSelected,
    config,
    comparatorMrnRequired,
    storedCanonical?.patient?.researchContactConsent,
  ])

  const fatherComparatorSectionComplete = useMemo(() => {
    if (!inProgressFatherSelected) return true
    const consents = getPatientConsents(config)
    const gates = resolveConsentInteractionGates(consents)
    return FormCompletionChecker.isCanonicalComparatorComplete(fatherComp, {
      incidentalApplicable: gates.incidentalRequireInteraction,
      mrnRequired: comparatorMrnRequired,
      probandResearchContactConsent: storedCanonical?.patient?.researchContactConsent === true,
      nyRetentionRequireInteraction: gates.nyRetentionRequireInteraction,
      researchContactRequireInteraction: gates.researchContactRequireInteraction,
    })
  }, [
    fatherComp,
    inProgressFatherSelected,
    config,
    comparatorMrnRequired,
    storedCanonical?.patient?.researchContactConsent,
  ])

  // GEN-2998: parent edit-pencil gating. Rule + tests live in
  // src/utils/canEditComparatorParents.ts. UI gating only — server-side
  // editability is enforced by provider-api on every mutation. Strategy-aware:
  // inflection_test stays editable through accessioning/complete (only CANCELLED
  // is terminal); rcigm keeps the pre-accession allowlist.
  const canEditComparatorParents =
    !reanalysisLocked &&
    resolveEditable(
      canEditComparatorParentsFn({
        orderStatus,
        hasSelectedTestTypeConfig: Boolean(selectedTestTypeConfig),
        fulfillmentStrategy: getFulfillmentStrategy(config),
      })
    )

  // Per-row comparator edit gate. Normally follows `canEditComparatorParents`
  // (pre-accession for rcigm; through-accession for inflection_test). During the
  // post-accession editability window it ADDITIONALLY allows editing a comparator
  // that was ADDED in this session (not present in the last-sent baseline) — so a
  // mother/member just added during the window can be filled in — while
  // already-persisted comparators stay locked. Mirrors the new-vs-existing split
  // the HPO lock already uses (`canEditNewComparatorHpo`).
  //
  // Gated on `comparatorEditingActive` (window OR unsent new comparators) rather
  // than the raw window, so a comparator added just before the deadline keeps its
  // Edit pencils after the idle timer closes the window — otherwise its unsent
  // local edits would be stranded (can neither finish nor send them).
  //
  // The newly-added branch additionally requires `baselineComparatorKeysReadyRef`:
  // until the baseline for the current order has been established, the key set is
  // empty (mount) or stale (post-switch) and `isNewlyAddedComparator` would return
  // true for a PERSISTED row, briefly unlocking its Edit/Remove affordances. The
  // PROBAND self-row is also excluded (it is never a comparator baseline key), so
  // it can never be misclassified as a newly-added comparator.
  const canEditComparatorRow = useCallback(
    (comparator: Comparator | null | undefined): boolean => {
      if (canEditComparatorParents) return true
      if (
        comparatorEditingActive &&
        baselineComparatorKeysReadyRef.current &&
        comparator?.relationship !== 'PROBAND' &&
        isNewlyAddedComparator(comparator, baselineComparatorKeysRef.current)
      ) {
        return true
      }
      return false
    },
    [canEditComparatorParents, comparatorEditingActive]
  )

  // Cell D — inflection_test UX gating (no-op for rcigm). All helpers are pure
  // and unit-tested in src/config/uiConfig.*.test.ts; the authoritative gate is
  // provider-api's 409 (Cell C). These layer on top of the status allowlist
  // above so rcigm behavior is unchanged. `fulfillmentStrategy` is resolved
  // above (with `comparatorMrnRequired`) and reused here.
  // F1: real reports (fetched via getAllOrderReports below, like the sibling
  // result/guidance pages) drive the 7-day add-comparator window. Empty until
  // the fetch resolves and empty ⇒ addable (no-final-report default), so the
  // window only ever tightens once reports arrive.
  // Add-comparator affordance gate. Normally requires the strategy-aware
  // editable window (`canEditComparatorParents`, which also governs edit/remove).
  // During the post-accession editability window we additionally allow ADD only
  // — `editabilityWindowOpen` is intentionally NOT folded into
  // `canEditComparatorParents`, so existing-comparator edit/remove pencils stay
  // locked. `canAddComparators` still bounds inflection_test by its own window.
  // Add-comparator affordance gate. For inflection_test with a definite mc-api
  // answer, editability is authoritative (false => no add), but the SeqOne
  // report-count bound (`canAddComparators`) still applies. Otherwise it follows
  // the strategy-aware editable window (`canEditComparatorParents`) plus the
  // post-accession add-only window (`editabilityWindowOpen`), bounded by
  // `canAddComparators`.
  const canAddComparatorsForOrder =
    isInflectionTest && apiOrderEditable !== null
      ? apiOrderEditable && canAddComparators(fulfillmentStrategy, orderReports)
      : (canEditComparatorParents || editabilityWindowOpen) &&
        canAddComparators(fulfillmentStrategy, orderReports)
  // HPO editing on existing/persisted members (proband + already-saved
  // comparators) is frozen when the SeqOne lock is engaged. Newly-added
  // comparators are unaffected (canEditNewComparatorHpo is always true).
  const canEditExistingHpo =
    canEditIndications && canEditExistingMemberHpo(fulfillmentStrategy, storedCanonical?.seqoneHPOLock)
  // F2: HPO editor lock passed to the comparator edit modals. `true` only when
  // the order is locked AND the member is already persisted (has an id); a
  // newly-added comparator (no id / null initialData) always keeps HPO editable.
  const isExistingComparatorHpoLocked = fulfillmentStrategy === 'inflection_test' && !canEditExistingHpo

  /**
   * Decoupling (R5): family structure is a function of the order's parent
   * comparators / `orderType`, not the chosen test type's `parentSamplesRequired`
   * — an org may run a duo/trio on one TestType (e.g. urWGS) whose config tier is
   * 0. In-progress must show comparator family copy when the placed order is a
   * duo/trio, regardless of the config tier.
   */
  const { parentSamplesForSequencingCard, comparatorSamplesAllowed } = useMemo(() => {
    const n = parentComparatorCountForOrder(storedCanonical)
    return { parentSamplesForSequencingCard: n, comparatorSamplesAllowed: n > 0 }
  }, [storedCanonical])

  const familyDisplayParentComparatorSamplesAllowed = useMemo(
    () =>
      parentComparatorSamplesAllowedForFamilyRowDerivation(
        storedCanonical?.orderType,
        comparatorSamplesAllowed,
        inProgressSelectedParentRelationships
      ),
    [comparatorSamplesAllowed, storedCanonical?.orderType, inProgressSelectedParentRelationships]
  )

  const sequencingFamilySamplesTile = useMemo(() => {
    if (!comparatorSamplesAllowed) return undefined
    return orderInProgressComparatorSequencingCopy(
      parentSamplesForSequencingCard,
      storedCanonical?.orderType
    )
  }, [comparatorSamplesAllowed, parentSamplesForSequencingCard, storedCanonical?.orderType])

  const comparatorParentConsentConfigs = useMemo(
    () => getPatientConsents(config),
    [config]
  )
  // NY / research submit gates: engaged only when applicable AND requireInteraction.
  const consentInteractionGates = useMemo(
    () => resolveConsentInteractionGates(comparatorParentConsentConfigs),
    [comparatorParentConsentConfigs]
  )
  /**
   * Whether the proband "Additional Options" consent section is outstanding:
   * incidental unanswered while applicable, or NY / research unanswered while
   * their `requireInteraction` gate is engaged. Drives the summary card's
   * needs-attention accent and the inline editor's inline validation.
   */
  const probandConsentNeedsAttention = useMemo(() => {
    const p = storedCanonical?.patient
    const incidentalIncomplete =
      consentInteractionGates.incidentalRequireInteraction &&
      p?.incidentalFindingsSelection !== 'opt-in' &&
      p?.incidentalFindingsSelection !== 'opt-out'
    const nyIncomplete =
      consentInteractionGates.nyRetentionRequireInteraction &&
      p?.newYorkSampleRetentionOptIn !== true &&
      p?.newYorkSampleRetentionOptIn !== false
    const researchIncomplete =
      consentInteractionGates.researchContactRequireInteraction &&
      p?.researchContactConsent !== true &&
      p?.researchContactConsent !== false
    return Boolean(incidentalIncomplete || nyIncomplete || researchIncomplete)
  }, [storedCanonical?.patient, consentInteractionGates])

  useEnforceNonApplicableSecondaryConsentDefaults(comparatorParentConsentConfigs, storedCanonical, dispatch)

  const orderingDomainTestLines = useMemo(() => testLinesFromUiConfig(config), [config])
  const effectiveConsentFlowForOrder = useMemo(
    () =>
      resolveEffectiveConsentFlowForSubmit(
        config,
        storedCanonical?.order?.selectedOption ?? '',
        orderingDomainTestLines
      ),
    [config, storedCanonical?.order?.selectedOption, orderingDomainTestLines]
  )

  // GEN-5654: does the platform collect consent to test for this comparator? "Added after
  // submission" is anything not proven part of the initial submission: absent from the
  // last-sent baseline, or first seen as new this session (sticky across Send Updates).
  const platformCollectsForComparator = (comparator: Comparator | null | undefined): boolean =>
    resolveComparatorPlatformCollects(
      effectiveConsentFlowForOrder,
      isComparatorAddedAfterSubmit(comparator, {
        baselineKeys: baselineComparatorKeysRef.current,
        baselineReady: baselineComparatorKeysReadyRef.current,
        addedAfterSubmitKeys: addedAfterSubmitKeysRef.current,
      })
    )
  const orderGuardianContactOnFile = orderHasGuardianContact(storedCanonical)
  const comparatorGuardianPropsFor = (comparator: Comparator | null | undefined) => ({
    platformCollectsConsentToTest: platformCollectsForComparator(comparator),
    guardianContactOnFile: orderGuardianContactOnFile,
    onRequestGuardianContact: requestGuardianContactForComparator,
    coveredByGuardianModal: guardianContactModalOpen,
  })

  const familyDisplay = useMemo(
    () =>
      deriveOrderInProgressFamilyRows(storedCanonical, {
        parentComparatorSamplesAllowed: familyDisplayParentComparatorSamplesAllowed,
      }),
    [storedCanonical, familyDisplayParentComparatorSamplesAllowed],
  )

  /**
   * R5 side-nav (``featureFlags.orderSideNav``) — parity with OrderStart. When
   * enabled, the submitted-order layout grows a left section nav + a comparator
   * detail view; the legacy single-column ComparatorList is hidden because
   * comparators are surfaced as nav groups. Editability still flows through the
   * page's existing gates (`canEditComparatorParents` / `canEditIndications`).
   */
  const sideNavEnabled = isOrderSideNavEnabled(
    config.featureFlags?.orderSideNav,
    new URLSearchParams(location.search).get('sidenav')
  )
  // Collection-kit ordering — parity with OrderStart. Opt-in via
  // ``featureFlags.kitOrdering``.
  const kitOrderingEnabled = isKitOrderingEnabled(config.featureFlags?.kitOrdering)
  const familyHistoryRequired = Boolean(config.validationRules?.requireFamilyHistory)
  const showGuardianContact = sideNavEnabled

  /**
   * Standalone guardian card: required when the proband opts into research
   * contact or the effective consent flow is PRIMARY.
   *
   * Legacy (non-sidenav): only proband-only orders enforce this — family
   * (Trio/Duo) orders lean on the comparator parents' research-contact channel,
   * validated via `comparatorSectionsReadyForSequencingSend`. The R5 side-nav
   * surfaces guardian contact as a patient-level field on every order, so the
   * requirement applies regardless of family structure and is satisfied by the
   * legacy slot OR a primary-guardian comparator (mirrors the shared submit-gate
   * predicate).
   */
  const guardianStandaloneResearchAttention = useMemo(
    () => {
      if (!sideNavEnabled && familyDisplay.orderType !== 'PROBAND_ONLY') return false
      const probandResearchOptIn = storedCanonical?.patient?.researchContactConsent === true
      const needsGuardian = probandResearchOptIn || effectiveConsentFlowForOrder.trim() === 'PRIMARY'
      if (!needsGuardian) return false
      return !FormCompletionChecker.standaloneGuardianContactSatisfied(storedCanonical, {
        decoupledComparators: sideNavEnabled,
        requireContactChannel: probandResearchOptIn,
      })
    },
    [
      sideNavEnabled,
      familyDisplay.orderType,
      storedCanonical,
      effectiveConsentFlowForOrder,
    ],
  )

  /**
   * Re-resolve the sequencing SKU (`order.selectedOption`) + `orderType` from a
   * post-mutation comparator set so the "Sequencing Options" card label always
   * tracks the order's family structure (proband / duo / trio). Both the add
   * and remove comparator flows share this: the tier lives on
   * `order.selectedOption`, which drives the displayed test-type label, while
   * the reducer's `upsertComparator` / `removeComparator` only touch the
   * comparator rows. Derives the tier purely from comparator presence via
   * `orderTypeFromComparators` (never `parentComparatorCountForOrder`, which
   * `Math.max`-es in the still-stale `orderType`) and re-resolves the same
   * product line's SKU for that tier. No-op when the category lacks the tier
   * SKU (keeps the current selection).
   */
  const syncSequencingTierToComparators = useCallback(
    (comparatorsAfter: Comparator[]) => {
      if (!storedCanonical) return
      const { selectedOption, orderType } = resolveSequencingTierForComparators(
        storedCanonical.order?.selectedOption ?? null,
        comparatorsAfter,
        config
      )
      // Always dispatch the resolved SKU (not gated on a diff vs. the closure's
      // `selectedOption`): when this runs AFTER `applySequencingOptionsChangeToCanonical`
      // (testing-parent add), that engine may have already written a *different*
      // SKU into the store than the closure holds. Comparing against the stale
      // closure value would then skip the corrective dispatch and leave the
      // parent-only tier in place (e.g. Trio-via-siblings dropping to Duo when a
      // parent is added). Re-dispatching the same value is a harmless no-op.
      if (selectedOption) {
        dispatch(mergeOrderSelection({ selectedOption }))
      }
      dispatch(setOrderType(orderType))
    },
    [dispatch, storedCanonical, config]
  )

  /**
   * R5 (submitted) Add-Comparator persistence. Adding a *testing* parent (one
   * with a collected sample) re-resolves the order's SKU + orderType to the
   * matching duo/trio tier via the shared sequencing engine — mirroring the
   * draft (OrderStart) flow — so a parent added from the side-nav upgrades the
   * sequencing test instead of leaving the proband-only SKU. The change rides
   * the normal `hasSequencingChanges` send-gate (the parent's required sections
   * must be completed before Send Updates). Non-parent comparators just upsert
   * without touching the SKU. A parent added without a sample yet still upgrades
   * the SKU/orderType via `syncSequencingTierToComparators` so the card label
   * reflects the new family structure immediately (symmetric with removal). Id
   * assignment, modal close, and selection are handled by the shared hook.
   */
  const handleAddComparatorPersist = useCallback(
    (withId: Comparator) => {
      const side = parentSideForComparator(withId)
      const isTestingParent = isTestingParentComparator(withId)
      // If this comparator was removed earlier this session, un-mark it so a
      // later re-hydration no longer strips it (the user has re-added it), and
      // drop any pending-cancellation entry it produced. Match on identity-key
      // OVERLAP, not the re-added row's keys as map keys: Add Comparator assigns
      // a fresh client `id`, so the re-added row shares only the `demo:` / `rnd:`
      // keys with the removed entry — an exact primary-key match would miss it
      // and leave a stale "will be removed" notice.
      const readdKeys = comparatorIdentityKeys(withId)
      for (const k of readdKeys) removedComparatorKeysRef.current.delete(k)
      if (readdKeys.length > 0) {
        setPendingComparatorCancellations((prev) => dropMatchingCancellations(prev, readdKeys))
      }
      if (isTestingParent && storedCanonical) {
        const prev = testOptionsFromCanonicalOrder(storedCanonical, config)
        const next = withRelationshipSelection(prev, side === 'mother' ? 'MOTHER' : 'FATHER', true)
        applySequencingOptionsChangeToCanonical({
          dispatch,
          canonical: storedCanonical,
          prev,
          next,
          config,
          callerProvidedParentSides: side ? [side] : undefined,
        })
      }
      // Persist the row flag alongside the sequencing-option flip: provider-api reads
      // ``bool(comparator["isTestingComparator"])`` to decide sequencing, so a testing
      // parent whose flag is only implicit (default-true, no explicit key) would be
      // treated as contact-only downstream ("No testing requested").
      const persisted = isTestingParent ? { ...withId, isTestingComparator: true } : withId
      dispatch(upsertComparator(persisted))
      // Always re-resolve the SKU tier from the RESULTING comparator set. The
      // parent SKU engine above (`applySequencingOptionsChangeToCanonical`) sizes
      // the tier from the biological-parent count ONLY, so any non-parent testing
      // comparators already on the order are ignored — the tier could stay too
      // low or even DROP (e.g. two tested siblings at Trio, then add a mother →
      // Duo). `syncSequencingTierToComparators` counts EVERY testing comparator
      // (any relationship), so running it last authoritatively corrects the SKU
      // while leaving the parent engine's family seeding / guardian defaulting /
      // orderType promotion intact. Mirrors removal and the OrderStart draft flow.
      if (storedCanonical) {
        const others = (storedCanonical.comparators ?? []).filter((c) => c?.id !== persisted.id)
        syncSequencingTierToComparators([...others, persisted])
      }
    },
    [dispatch, storedCanonical, config, syncSequencingTierToComparators]
  )

  const {
    selectedComparatorId,
    setSelectedComparatorId,
    resolveComparatorById,
    sideNavSections,
    comparatorGroups,
    comparatorLimitReached,
    comparatorLimitReachedReason,
    primaryGuardianComparator,
    primaryGuardianComparatorComplete,
    addComparatorRelationshipOptions,
    comparatorSectionEdit,
    setComparatorSectionEdit,
    addComparatorOpen,
    setAddComparatorOpen,
    patientConsentModalOpen,
    setPatientConsentModalOpen,
    handleComparatorSectionSave,
    handleAddComparatorSave,
  } = useOrderSideNavController({
    sideNavEnabled,
    storedCanonical,
    comparatorParentConsentConfigs,
    requireClinicalSummary: Boolean(config.validationRules?.requireClinicalSummary),
    requirePhenotypes: Boolean(config.validationRules?.requirePhenotypes),
    familyHistoryRequired,
    showGuardianContact,
    comparatorMrnRequired,
    onAddComparatorSave: handleAddComparatorPersist,
    fulfillmentStrategy,
    config,
    selectedCategory: selectedTestTypeConfig?.category,
  })

  // Publish the sticky page header's height as the CSS var the stacked layout
  // uses to pin the left nav and the per-person right-column headers flush
  // beneath it (see useOrderStickyTop / --order-sticky-top).
  useOrderStickyTop(sideNavEnabled)

  // Lifted narrow-screen (`< lg`) side-nav drawer visibility so the "Open order
  // navigation" burger can render inline in the header (next to the patient
  // name) while the drawer + backdrop stay inside OrderSideNav.
  const [sideNavDrawerOpen, setSideNavDrawerOpen] = useState(false)

  const selectedTestTypeLabel =
    selectedTestTypeConfig?.label ??
    selectedTestTypeConfig?.category ??
    config.testTypes[0]?.category ??
    'Ultra-Rapid Whole Genome Sequencing (urWGS)'
  const turnaroundDays = selectedTestTypeConfig?.turnaroundTimeInDays ?? 5
  const turnaroundSequencingSubtitle = formatTurnaroundTimeSequencingCardSubtitle(
    turnaroundDays,
    getTurnaroundTimeDayBasisForTestType(selectedTestTypeConfig)
  )

  const comparatorModalAllowedTypes = useMemo(() => {
    const fromBaseline = persistedTestTypeFromSequencingBaselineSerialized(sequencingBaselineRef.current)
    const persisted =
      fromBaseline ||
      persistedSequencingTestType.trim() ||
      (sequencingBaselineRef.current === null ? (storedCanonical?.order?.selectedOption ?? '').trim() : '')
    return getInProgressSequencingModalAllowedTestTypes(persisted as TestType, config)
  }, [config, persistedSequencingTestType, storedCanonical?.order?.selectedOption])

  /**
   * Submitted, pre-accession: allow opening the sequencing modal when the
   * current test maps in UI config AND the org has opted into post-submit
   * comparator edits via ``featureFlags.allowPostSubmitComparatorEdits``.
   */
  const canOpenSequencingOptionsModal =
    canEditSequencingComparators &&
    Boolean(selectedTestTypeConfig) &&
    comparatorModalAllowedTypes.length > 0 &&
    Boolean(config.featureFlags?.allowPostSubmitComparatorEdits) &&
    // Cell D: the sequencing modal is the comparator-add entry point on the
    // post-submit page, so close it once the inflection_test add-window lapses.
    // No-op for rcigm (canAddComparators returns true there).
    canAddComparators(fulfillmentStrategy, orderReports)

  const openComparatorSequencingModal = () => {
    if (!storedCanonical) return
    if (sequencingBaselineRef.current === null) {
      sequencingBaselineRef.current = serializeSequencingBaseline(storedCanonical)
      bumpSequencingBaselineVersion()
      setPersistedSequencingTestType((storedCanonical.order?.selectedOption ?? '').trim())
    }
    const live = testOptionsFromCanonicalOrder(storedCanonical, config)
    setSequencingModalPrevOptions(live)
    setSequencingModalKey((k) => k + 1)
    setTestOptionsComparatorModalOpen(true)
  }

  const comparatorModalFamilyProps = useMemo(
    () =>
      storedCanonical
        ? familyMembersForTestOptionsModal(
            storedCanonical,
            sequencingModalPrevOptions ?? testOptionsFromCanonicalOrder(storedCanonical, config)
          )
        : {},
    [storedCanonical, sequencingModalPrevOptions, config]
  )

  useEffect(() => {
    if (!storedCanonical) {
      return
    }
    // Do NOT self-seed the baseline from whatever storedCanonical arrives first:
    // the order hydrates via multiple setCanonicalOrder dispatches, so the first
    // one can carry a pre-hydration sequencing signature and permanently latch a
    // phantom "Sequencing option was updated". The authoritative baseline is set
    // explicitly at fetch/hydrate/send (captureCanonicalBaselines.sequencing);
    // until then, fall back to the live snapshot so this reads "not dirty".
    // Mirrors the comparators/consent/demographics dirty effects.
    const snapshot = serializeSequencingBaseline(storedCanonical)
    const baseline = sequencingBaselineRef.current ?? snapshot
    const seqDirty = snapshot !== baseline
    prevSequencingDirtyRef.current = seqDirty
    setHasSequencingChanges(seqDirty)
  }, [storedCanonical, sequencingBaselineVersion])


  /** The clinical section as it was when the editor opened, for Discard Changes. */
  const clinicalModalBaseline = useRef<{
    clinicalNote: string
    differentialDiagnosis: string
    priorGeneticTesting: string
    clinicalDiagnosis: CategorizedHPOOption[]
    userInteracted: boolean
    clinicalSummaryReview: ClinicalSummaryReview | null
  } | null>(null)
  const storedCanonicalRef = useRef(storedCanonical)
  storedCanonicalRef.current = storedCanonical

  useEffect(() => {
    if (!clinicalSummaryModalOpen) return
    const canonical = storedCanonicalRef.current
    clinicalModalBaseline.current = {
      clinicalNote: canonical?.clinical?.clinicalNote ?? '',
      differentialDiagnosis: canonical?.clinical?.differentialDiagnosis ?? '',
      priorGeneticTesting: canonical?.clinical?.priorGeneticTesting ?? '',
      clinicalDiagnosis: canonical?.reason?.clinicalDiagnosis ?? [],
      userInteracted: canonical?.reason?.userInteracted ?? false,
      clinicalSummaryReview: canonical?.reason?.clinicalSummaryReview ?? null,
    }
    setClinicalEditorLastSaveTime(null)
  }, [clinicalSummaryModalOpen])

  const applyClinicalDiagnosisToIndications = useCallback(
    (terms: CategorizedHPOOption[]) => {
      setIndications({
        phenotypes: terms.map((term) => ({
          id: term.id,
          name: term.label,
          hpoCode: term.id,
          type: mapCategoryToType(term.category),
        })),
      })
    },
    [setIndications],
  )

  const saveAndCloseClinicalModal = useCallback(() => {
    // An extraction still in flight has already been merged optimistically; keep it
    // rather than dropping the phenotypes the clinician just watched appear.
    clinicalEditorControls.current?.keepPendingExtraction()
    setClinicalSummaryModalOpen(false)
  }, [])

  const discardClinicalModal = useCallback(() => {
    setClinicalSummaryModalOpen(false)
    const baseline = clinicalModalBaseline.current
    if (!baseline) return
    dispatch(
      mergeClinical({
        clinicalNote: baseline.clinicalNote,
        differentialDiagnosis: baseline.differentialDiagnosis.trim() || null,
        priorGeneticTesting: baseline.priorGeneticTesting.trim() || null,
      }),
    )
    dispatch(
      mergeReason({
        clinicalDiagnosis: baseline.clinicalDiagnosis,
        userInteracted: baseline.userInteracted,
      }),
    )
    dispatch(setClinicalSummaryReview(baseline.clinicalSummaryReview))
    applyClinicalDiagnosisToIndications(baseline.clinicalDiagnosis)
    // Rebuild the editor from the restored canonical on the next open.
    setClinicalSummaryEditorEpoch((epoch) => epoch + 1)
  }, [dispatch, applyClinicalDiagnosisToIndications])

  /**
   * Undo for a whole extraction: the summary text and the phenotype list move back
   * together. Submitted orders stage into canonical, so this is a dispatch rather
   * than a draft write — it resolves once the store holds the snapshot again.
   */
  const handleMergedSummaryUndo = useCallback(
    async (snapshot: ClinicalSummarySnapshot): Promise<void> => {
      dispatch(mergeClinical({ clinicalNote: snapshot.summary }))
      dispatch(
        mergeReason({
          clinicalDiagnosis: snapshot.clinicalDiagnosis,
          userInteracted: snapshot.clinicalDiagnosis.length > 0,
        }),
      )
      dispatch(
        setClinicalSummaryReview(
          snapshot.review ??
            reviewForUnreviewedDiagnosis(snapshot.clinicalDiagnosis, snapshot.summary),
        ),
      )
      applyClinicalDiagnosisToIndications(snapshot.clinicalDiagnosis)
      noteClinicalEditorSave()
    },
    [dispatch, applyClinicalDiagnosisToIndications, noteClinicalEditorSave],
  )

  // Needs-input review rows, or a summary that no longer matches the last merge
  // fingerprint (an edit landed before live extraction finished). This replaces the
  // staged-diff gate: the editor reconciles in place, so what blocks sending is an
  // unresolved row rather than an unconfirmed banner.
  const clinicalSummaryReviewBlocksSend = blocksSubmit(
    storedCanonical?.reason?.clinicalSummaryReview,
    storedCanonical?.clinical?.clinicalNote ?? '',
  )

  const guardianContactEditable = useMemo(
    () =>
      familyDisplay.rows.some(
        (r) =>
          r.kind === 'GUARDIAN_CONTACT' ||
          (r.kind === 'BIOLOGICAL_MOTHER' && r.rowNonTestSubject) ||
          (r.kind === 'BIOLOGICAL_FATHER' && r.rowNonTestSubject),
      ),
    [familyDisplay.rows],
  )

  const currentStepId = useMemo(() => {
    switch (orderStatus) {
      case OrderStatusDisplay.ORDER_PLACED:
        return 'order'
      case OrderStatusDisplay.ORDER_SUBMITTED:
        return 'collection'
      case OrderStatusDisplay.SAMPLE_RECEIVED:
        return 'analysis'
      case OrderStatusDisplay.RESULT_READY:
        return 'results'
      case OrderStatusDisplay.CANCELLED:
        // Cancelled orders don't follow the lab pipeline; the timeline is
        // replaced by a dedicated two-step "signed → canceled" view below.
        return 'cancelled'
      default:
        return 'order'
    }
  }, [orderStatus])
  const pipelineSteps = useConfiguredOrderSteps({ currentStepId })
  const isCancelled = orderStatus === OrderStatusDisplay.CANCELLED

  // Keep the always-current send-gate ref in sync (see `ehrDriftSendableRef`).
  useEffect(() => {
    ehrDriftSendableRef.current = canAddOrSendComparator && !isCancelled && !reanalysisLocked
  }, [canAddOrSendComparator, isCancelled, reanalysisLocked])

  // Cancelled orders collapse the pipeline to two steps: the completed order
  // and a terminal "Canceled" step, matching the cancel-flow design.
  const docusignEnabled = isTrfSignatureDocusignEnabled(config)
  const steps: Array<Omit<ConfiguredOrderStep, 'icon'> & { icon?: ConfiguredOrderStep['icon'] }> = useMemo(() => {
    if (isCancelled) {
      const orderLabel =
        pipelineSteps.find((s) => s.id === 'order')?.label ??
        copyWithoutSigning('Complete & Sign Order', docusignEnabled)
      return [
        { id: 'order', label: orderLabel, status: 'completed' },
        { id: 'cancelled', label: 'Canceled', status: 'cancelled' },
      ]
    }
    // Reanalysis flow: the order is already complete and we are re-running it
    // from the finished state, so the lab-pipeline status bar is not meaningful
    // here — hide the timeline entirely (all render sites gate on
    // `steps.length > 0`).
    if (isReanalysis) {
      return []
    }
    // Reanalysis read-only lock: a reanalysis-status inflection_test order viewed
    // without ?reanalysis=true is presented fully read-only, including hiding the
    // status bar (same empty-steps mechanism as the reanalysis edit flow).
    if (reanalysisLocked) {
      return []
    }
    return pipelineSteps
  }, [isCancelled, isReanalysis, reanalysisLocked, pipelineSteps, docusignEnabled])

  // GEN-5494: human-readable "on <date> at <time>" fragment for the "Order was
  // sent" banner, derived solely from the server-sourced `trfSentAt` state. Null
  // when the time is unavailable (fetch not resolved, neither timestamp present)
  // or unparseable, in which case the banner omits the date rather than falling
  // back to the viewer's current local time.
  const orderSentAtLabel = useMemo(() => {
    const parsed = parseTrfSentAt(trfSentAt)
    if (parsed == null) return null
    return `${parsed.toLocaleDateString('en-US')} at ${parsed.toLocaleTimeString('en-US')}`
  }, [trfSentAt])

  // Human-readable "accessioned on <date> at <time>" fragment shown while the
  // order is accessioned but still in progress. Null when there is no valid
  // timestamp so the banner falls back to the generic analysis-in-progress copy.
  const accessionedOnLabel = useMemo(() => {
    if (!accessionedOn) return null
    const parsed = new Date(accessionedOn)
    if (Number.isNaN(parsed.getTime())) return null
    return `${parsed.toLocaleDateString('en-US')} at ${parsed.toLocaleTimeString('en-US')}`
  }, [accessionedOn])

  // Reusable comparator-addition-window sentence (bold deadline). Shared by the
  // accessioned status banner AND the pending-changes banner so the window
  // deadline is shown alongside pending changes, not hidden by them. Null when
  // the window does not apply to this order.
  const comparatorAddWindowMessage: ReactNode = comparatorAddWindow ? (
    comparatorAddWindow.passed ? (
      <>
        The window for adding new comparators closed on{' '}
        <span className="font-semibold">{comparatorAddWindow.label}</span>; no further comparator
        additions can be made.
      </>
    ) : (
      <>
        New comparators can be added until{' '}
        <span className="font-semibold">{comparatorAddWindow.label}</span>.
      </>
    )
  ) : null

  const statusAlertMessage: ReactNode =
    reanalysisLocked
      ? 'Reanalysis is in progress'
      : orderStatus === OrderStatusDisplay.CANCELLED
        ? 'Order canceled'
        : orderStatus === OrderStatusDisplay.SAMPLE_RECEIVED
          ? (
              <>
                {accessionedOnLabel
                  ? `Sample was accessioned by the lab on ${accessionedOnLabel}. Analysis is in progress. We will notify you when results are ready.`
                  : 'Analysis is in progress. We will notify you when results are ready.'}
                {comparatorAddWindowMessage ? <> {comparatorAddWindowMessage}</> : null}
              </>
            )
          : orderStatus === OrderStatusDisplay.RESULT_READY
            ? "Patient's genetic test results are ready for review."
            : `Order was sent${orderSentAtLabel ? ` on ${orderSentAtLabel}` : ''}. Clinical summary, family history, indications for testing${
                guardianContactEditable ? ', and guardian contact' : ''
              } can be updated before the lab accessions the sample.`

  useEffect(() => {
    const next = backfillResolvedPhenotypeLabels(storedCanonical?.reason?.clinicalDiagnosis, indications.phenotypes)
    if (!next) {
      return
    }
    // Safe against the nav guard: the unsaved-changes baseline keys on code and
    // category, never the label, so this cannot report a phantom edit.
    dispatch(mergeReason({ clinicalDiagnosis: next }))
  }, [indications.phenotypes, storedCanonical?.reason?.clinicalDiagnosis, dispatch])

  useEffect(() => {
    const existingPhenotypes = Array.isArray(storedCanonical?.reason?.clinicalDiagnosis)
      ? storedCanonical!.reason!.clinicalDiagnosis
      : []
    const mappedPhenotypes =
      existingPhenotypes.map((term) => ({
        id: term.id,
        name: term.label,
        hpoCode: term.id,
        type: mapCategoryToType(term.category),
      })) ?? []
    setIndications({ phenotypes: mappedPhenotypes })
    resetIndicationLabelResolution()
    // Baseline is set on fetch/hydrate/send only (see `baselineIndicationsRef`); do not set here —
    // an early run with empty/partial canonical caused false "Updated by you" on open.
    // Only re-sync when clinicalDiagnosis changes; omit storedCanonical from deps to avoid noisy resets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedCanonical?.reason?.clinicalDiagnosis])

  useEffect(() => {
    const current = serializeIndicationsBaseline(storedCanonical?.reason?.clinicalDiagnosis || [])
    const baseline = baselineIndicationsRef.current ?? current
    // Still tracked after the merge: Send Updates reports phenotypes as a changed
    // section even though they are now edited inside the clinical summary.
    setHasIndicationsChanges(current !== baseline)
  }, [storedCanonical?.reason?.clinicalDiagnosis])

  const guardianSnapshot = useMemo(
    () => serializeGuardian(storedCanonical?.guardianContact),
    [storedCanonical]
  )

  useEffect(() => {
    const baseline = baselineGuardianRef.current ?? guardianSnapshot
    setHasGuardianChanges(guardianSnapshot !== baseline)
  }, [guardianSnapshot])

  useEffect(() => {
    const current = serializeClinicalBaseline(storedCanonical?.clinical)
    const baseline = baselineClinicalRef.current ?? current
    setHasClinicalSummaryChanges(current !== baseline)
  }, [storedCanonical?.clinical])

  useEffect(() => {
    const current = serializeFamilyBaseline(storedCanonical?.family)
    const baseline = baselineFamilyRef.current ?? current
    setHasFamilyHistoryChanges(current !== baseline)
  }, [storedCanonical?.family])

  const comparatorsSnapshot = useMemo(
    () => serializeComparatorsBaseline(storedCanonical?.comparators),
    [storedCanonical?.comparators]
  )

  const patientConsentSnapshot = useMemo(
    () => serializePatientConsentBaseline(storedCanonical?.patient),
    [storedCanonical?.patient]
  )

  const patientDemographicsSnapshot = useMemo(
    () => serializePatientDemographicsBaseline(storedCanonical?.patient),
    [storedCanonical?.patient]
  )

  useEffect(() => {
    // Do not self-seed the ref here: this effect runs before the fetch/hydrate
    // baseline is captured (when comparators are still empty), and seeding "[]"
    // then latched a permanent phantom dirty once comparators hydrated. Fall
    // back to `current` (reads "not dirty") until the explicit baseline is set.
    const baseline = baselineComparatorsRef.current ?? comparatorsSnapshot
    setHasComparatorChanges(comparatorsSnapshot !== baseline)
  }, [comparatorsSnapshot])

  useEffect(() => {
    const baseline = baselinePatientConsentRef.current ?? patientConsentSnapshot
    setHasPatientConsentChanges(patientConsentSnapshot !== baseline)
  }, [patientConsentSnapshot])

  useEffect(() => {
    const baseline = baselinePatientDemographicsRef.current ?? patientDemographicsSnapshot
    setHasPatientDemographicsChanges(patientDemographicsSnapshot !== baseline)
  }, [patientDemographicsSnapshot])

  // GEN: On an Epic launch, re-fetch the proband's demographics from the EHR
  // once the order has hydrated and compare them to the saved order. If the
  // name/DOB/sex/MRN differ, open a modal offering to push the EHR values to
  // the lab. Runs at most once per loaded order (guarded by
  // `ehrDriftCheckedForOrderRef`) and only when the order can still be updated,
  // so we never surface drift on a cancelled / reanalysis-locked order.
  //
  // Gated on `orderHydrated` — set only after THIS order's fetch has populated
  // the canonical store and captured its demographics baseline — rather than on
  // the mere presence of a patient. On an Epic launch the first patient can be
  // the callback's pre-hydration snapshot while `OrderInProgress`'s own
  // `setCanonicalOrder` is still in flight; comparing then (if the EHR response
  // lands first) would diff against the WRONG saved demographics and could send
  // a false / incorrect lab update. The saved patient is read from the store at
  // comparison time, and stale responses are dropped via the order-key ref
  // (instead of a per-render cleanup flag) so ordinary hydration re-renders that
  // replace `patient` do not abandon the one-shot fetch.
  useEffect(() => {
    if (authFlow !== 'epic') return
    const oauthState = epicState ?? getActiveEpicState()
    if (!oauthState) return
    // Wait until THIS order's canonical has hydrated and its demographics
    // baseline is captured, so we compare against the real saved demographics.
    if (!orderHydrated) return
    if (!canAddOrSendComparator || isCancelled || reanalysisLocked) return
    // One-shot per order. Key by the order/patient id currently loaded; the
    // order-scoped reset effect clears this when the params change.
    const orderKey = urlOrderId || urlPatientId || null
    if (!orderKey) return
    if (ehrDriftCheckedForOrderRef.current === orderKey) return
    ehrDriftCheckedForOrderRef.current = orderKey

    const run = async () => {
      let ehrData
      try {
        const { extractFormOrderData } = await import('@/utils/FormDataExtraction')
        ehrData = await extractFormOrderData(oauthState)
      } catch (error) {
        // A failed EHR fetch is non-fatal: the provider can still edit and send
        // updates manually. Report and move on rather than blocking the page.
        Sentry.captureException(error, {
          tags: { operation: 'order.in-progress.ehr-demographics-drift' },
        })
        return
      }
      // Drop a stale response: the loaded order changed while this was in
      // flight, so this describes an order the user is no longer looking at.
      // (Uses the ref rather than a per-render cleanup flag so ordinary
      // hydration re-renders that replace `patient` do NOT abandon the check.)
      if (ehrDriftCheckedForOrderRef.current !== orderKey) return
      // Re-check the send gate at completion time: the order may have been
      // cancelled or reanalysis-locked (or otherwise become un-updatable) while
      // this fetch was in flight. Opening the modal then would let confirm merge
      // EHR values that `handleUpdateOrder` will refuse to send.
      if (!ehrDriftSendableRef.current) return
      // Compare against the FRESHEST saved demographics from the store: the
      // order may have finished hydrating (or the user may have edited) after
      // this fetch started, and the store is authoritative.
      const savedPatient = appStore.getState().canonicalOrder.canonicalOrder?.patient ?? null
      const drift = compareProbandDemographicsToEhr(savedPatient, ehrData.patient)
      if (!drift.hasDrift) return
      setEhrDriftFields(drift.changedFields)
      setEhrDriftMergePatch(drift.mergePatch)
      setEhrDriftModalOpen(true)
    }
    void run()
  }, [
    authFlow,
    epicState,
    orderHydrated,
    canAddOrSendComparator,
    isCancelled,
    reanalysisLocked,
    urlOrderId,
    urlPatientId,
    appStore,
  ])

  /**
   * Resolve human-readable labels for parent comparator HPO terms whose `label`
   * is the HP code itself (legacy canonical rows). Mirrors the indications
   * resolver above but writes into a local `parentHpoLabelOverrides` map keyed by
   * id, so the FamilyCard summary renders text like `Delayed CNS myelination`
   * without mutating the canonical store or triggering dirty detection.
   */
  const motherHpoTermsKey = useMemo(
    () => serializeComparatorHpoSig(motherComp?.hpoTerms),
    [motherComp]
  )
  const fatherHpoTermsKey = useMemo(
    () => serializeComparatorHpoSig(fatherComp?.hpoTerms),
    [fatherComp]
  )

  useEffect(() => {
    const collectPending = (terms: unknown): string[] => {
      if (!Array.isArray(terms)) return []
      const ids: string[] = []
      for (const raw of terms) {
        const t = raw as { id?: string; label?: string }
        const id = String(t?.id ?? '').trim()
        const label = String(t?.label ?? '').trim()
        if (id && isHpoCode(label) && !(id in parentHpoLabelOverrides)) {
          ids.push(id)
        }
      }
      return ids
    }
    const pendingIds = Array.from(
      new Set([
        ...collectPending(motherComp?.hpoTerms),
        ...collectPending(fatherComp?.hpoTerms),
      ])
    )
    if (pendingIds.length === 0) return

    let cancelled = false
    const resolve = async () => {
      const additions: Record<string, string> = {}
      for (const rawId of pendingIds) {
        const normalized = normalizeHpoId(rawId) ?? rawId
        try {
          const node = await hpoAPIClient.getHPOTermById(normalized)
          if (node?.lbl && !isHpoCode(node.lbl)) {
            additions[rawId] = node.lbl
          }
        } catch (error) {
          Sentry.captureException(error)
        }
      }
      if (cancelled || Object.keys(additions).length === 0) return
      setParentHpoLabelOverrides((prev) => ({ ...prev, ...additions }))
    }
    resolve()

    return () => {
      cancelled = true
    }
    // `parentHpoLabelOverrides` is intentionally omitted to avoid re-running on
    // every successful resolution; the `pendingIds` guard short-circuits when
    // there is nothing left to resolve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motherHpoTermsKey, fatherHpoTermsKey])

  useEffect(() => {
    const fetchOrderStatus = async () => {
      if (!orderData?.patient?.id) {
        return
      }

      try {
        const caller = getCallerFromOrderData(orderData)
        // A patient lookup resolves the patient's newest order, which is a
        // different order once a cancelled one has been restarted.
        const statusResponse = urlOrderId
          ? await getOrderById(urlOrderId, caller)
          : await getOrderStatusByPatient(orderData.patient.id, caller)
        if (statusResponse) {
          setOrderStatus(mapOrderStatusToDisplay(statusResponse.status))
          setRawBackendStatus(statusResponse.status)
          setAccessionedOn(statusResponse.accessioned_on?.trim() || null)
          const resolvedExternalId = inflectionExternalIdFromOrder(statusResponse)
          if (resolvedExternalId) {
            setInflectionExternalId(resolvedExternalId)
          }
        }
      } catch {
        // Safe-by-default: if the status fetch fails we do not know whether
        // the order is still editable, so map to a non-editable display
        // status rather than showing affordances the server may 409.
        setOrderStatus(OrderStatusDisplay.CANCELLED)
        setRawBackendStatus(undefined)
      }
    }

    fetchOrderStatus()
  }, [orderData, urlOrderId])

  // When arriving from OrderReadyToSend (or refresh) without form in Redux, hydrate from backend so all cards show
  useEffect(() => {
    if (!orderData?.patient?.id || hasHydratedFormFromBackend.current) {
      return
    }
    // The primary `fetchOrder` effect (guarded by `hasInitiallyFetched`) already
    // dispatches `setCanonicalOrder` with the full order payload. This fallback
    // is only for arriving without any Redux canonical state. Re-dispatching here
    // after `fetchOrder` re-runs `backfillComparatorIds`, which mints fresh
    // client ids on every call — that invalidates the current
    // `selectedComparatorId`, blanks the comparator detail view, and drops the
    // Edit affordances. Skip once `fetchOrder` has hydrated for this order.
    if (hasInitiallyFetched.current) {
      hasHydratedFormFromBackend.current = true
      return
    }
    // Race guard: when arriving with `locationState.orderData`, this effect can
    // fire before `fetchOrder` resolves (so `hasInitiallyFetched` is still
    // false). If Redux already carries comparators or a selected test type, the
    // canonical state has been hydrated — skip the redundant fetch so we don't
    // re-mint comparator ids and lose the current selection.
    const hasComparators = Array.isArray(storedCanonical?.comparators) && (storedCanonical?.comparators?.length ?? 0) > 0
    const hasSelectedOption = Boolean(storedCanonical?.order?.selectedOption)
    if (hasComparators || hasSelectedOption) {
      hasHydratedFormFromBackend.current = true
      return
    }
    const hasIndications =
      Array.isArray(storedCanonical?.reason?.clinicalDiagnosis) && (storedCanonical?.reason?.clinicalDiagnosis?.length ?? 0) > 0
    const hasFamilyHistory = Boolean(storedCanonical?.family?.familyHistory?.trim())
    if (hasIndications || hasFamilyHistory) {
      return
    }

    let isActive = true
    const hydrateForm = async () => {
      try {
        const caller = getCallerFromOrderData(orderData)
        const existingOrder = urlOrderId
          ? await getOrderById(urlOrderId, caller)
          : await getOrderByPatient(orderData.patient.id, caller)
        if (!isActive) {
          return
        }
        const merged = mergeCanonicalFromOrderJsonPayload(existingOrder?.json_payload)
        if (merged) {
          // Match the normalized shape Redux stores (parent slots folded into
          // comparators, side-nav client ids backfilled) so the baseline
          // doesn't flag a phantom comparator change.
          const normalized = backfillComparatorIds(normalizeInboundParentComparators(merged))
          // GEN-3xxx: recover a UI-gating SKU when `selectedOption` was never
          // persisted, so comparator editing is not permanently disabled on
          // reload (see the primary fetch effect for the rationale).
          const recoveredOption = recoverSelectedOptionForCanonical(normalized, config)
          const hydratedBase =
            recoveredOption && !normalized.order?.selectedOption?.trim()
              ? { ...normalized, order: { ...(normalized.order ?? {}), selectedOption: recoveredOption } }
              : normalized
          // Cell C: authoritative SeqOne HPO lock from the order ROW overrides
          // the (possibly stale) canonicalOrder blob (see primary fetch effect).
          const hydrated = { ...hydratedBase, seqoneHPOLock: existingOrder?.seqone_hpo_lock ?? null }
          dispatch(setCanonicalOrder(stripSessionRemovedComparators(hydrated, removedComparatorKeysRef.current)))
          // Baseline from the post-enforcement shape (see fetch effect above):
          // mirror the hidden-consent defaults the enforcement hook applies and
          // strip the `1900-01-01` placeholder DOB the same way `setCanonicalOrder`
          // does, so neither registers as a phantom unsaved change.
          const baselineSource = applyHiddenSecondaryConsentDefaults(
            stripPlaceholderDobsFromCanonical(hydrated),
            getPatientConsents(config)
          )
          const baselines = captureCanonicalBaselines(baselineSource)
          baselineIndicationsRef.current = baselines.indications
          baselineGuardianRef.current = baselines.guardian
          baselineClinicalRef.current = baselines.clinical
          baselineFamilyRef.current = baselines.family
          baselineComparatorsRef.current = baselines.comparators
          baselineComparatorKeysRef.current = buildComparatorBaselineKeySet(baselineSource.comparators)
          baselineComparatorKeysReadyRef.current = true
          baselinePatientConsentRef.current = baselines.patientConsent
          baselinePatientDemographicsRef.current = baselines.patientDemographics
          // Match fetchOrder: capture sequencing baseline from the hydrated
          // canonical so a second hydration dispatch can't phantom-flag it.
          sequencingBaselineRef.current = baselines.sequencing
          bumpSequencingBaselineVersion()
          setPersistedSequencingTestType((baselineSource.order?.selectedOption ?? '').trim())
          setOrderHydrated(true)
        }
        hasHydratedFormFromBackend.current = true
      } catch (error) {
        Sentry.captureException(error, { tags: { operation: 'order.in-progress.hydrate-form' } })
      }
    }
    hydrateForm()

    return () => {
      isActive = false
    }
    // Only the *presence* of comparators matters here (guards the redundant
    // fetch); depending on the full `comparators` array would re-run this
    // hydration on every comparator edit, so `?.length` is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    storedCanonical?.family?.familyHistory,
    storedCanonical?.reason?.clinicalDiagnosis,
    storedCanonical?.comparators?.length,
    storedCanonical?.order?.selectedOption,
    orderData,
    config,
    urlOrderId,
  ])

  const sendUpdatesSectionIds = useMemo((): SendOrderUpdatesSectionId[] => {
    const ids: SendOrderUpdatesSectionId[] = []
    if (hasClinicalSummaryChanges) ids.push('clinicalSummary')
    if (hasIndicationsChanges) ids.push('indications')
    if (hasFamilyHistoryChanges) ids.push('familyHistory')
    if (hasGuardianChanges) ids.push('guardianContact')
    if (hasSequencingChanges) ids.push('sequencingOptions')
    if (hasComparatorChanges) ids.push('comparatorParents')
    if (hasPatientConsentChanges) ids.push('patientConsent')
    if (hasPatientDemographicsChanges) ids.push('patientDemographics')
    return ids
  }, [
    hasClinicalSummaryChanges,
    hasComparatorChanges,
    hasFamilyHistoryChanges,
    hasGuardianChanges,
    hasIndicationsChanges,
    hasPatientConsentChanges,
    hasPatientDemographicsChanges,
    hasSequencingChanges,
  ])

  /**
   * Display labels for comparators removed this session that are still pending
   * cancellation (i.e. not re-added). Surfaced in the Send Updates confirm
   * modal so the provider sees which previously-sent comparators will be
   * removed from the order on confirm.
   */
  const pendingComparatorCancellationLabels = useMemo(
    () => pendingComparatorCancellations.map((entry) => entry.label),
    [pendingComparatorCancellations]
  )

  /**
   * Human-readable summary of what is currently pending, shown inline in the
   * status banner so the provider can see (before opening the confirm modal)
   * exactly which edits and cancellations are waiting on "Send Updates". Lists
   * the changed section short-labels followed by any comparators queued for
   * removal (e.g. "Clinical Summary and Sequencing option updated. Mother will
   * be removed.").
   */
  const pendingChangesSummaryText = useMemo(() => {
    const formatList = (items: string[]): string => {
      if (items.length === 0) return ''
      if (items.length === 1) return items[0]
      if (items.length === 2) return `${items[0]} and ${items[1]}`
      return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
    }

    const sentences: string[] = []
    const changedLabels = sendUpdatesSectionIds.map((id) => SEND_UPDATES_SECTION_SHORT_LABELS[id])
    if (changedLabels.length > 0) {
      sentences.push(`${formatList(changedLabels)} ${changedLabels.length === 1 ? 'was' : 'were'} updated.`)
    }
    if (pendingComparatorCancellationLabels.length > 0) {
      sentences.push(`${formatList(pendingComparatorCancellationLabels)} will be removed from the order.`)
    }
    return sentences.join(' ')
  }, [sendUpdatesSectionIds, pendingComparatorCancellationLabels])

  /** True iff the user has edited at least one section since the last successful send. */
  const hasAnyChanges =
    hasClinicalSummaryChanges ||
    hasIndicationsChanges ||
    hasFamilyHistoryChanges ||
    hasGuardianChanges ||
    hasSequencingChanges ||
    hasComparatorChanges ||
    hasPatientConsentChanges ||
    hasPatientDemographicsChanges

  /**
   * UX-054: completeness gate for the changed sections. "Send Updates" must
   * gate on required-field completeness of what the user edited — not just
   * change-detection — so an emptied required section (e.g. an erased clinical
   * summary) or an incomplete/added comparator (any relationship, not just
   * biological MOTHER/FATHER) blocks send. Mirrors OrderStart's submit gate
   * (`isCanonicalOrderSubmittable` + per-comparator
   * `isCanonicalComparatorComplete`) but scoped to the changed sections and
   * decoupled from `hasSequencingChanges` (a comparator/clinical edit gates on
   * completeness whether or not it was a sequencing/tier change). Server-side
   * defense-in-depth still applies: provider-api validates the update payload.
   */
  const sendUpdatesCompletenessInput = useMemo<SendUpdatesCompletenessInput>(
    () => ({
      canonical: storedCanonical,
      changed: {
        clinicalSummary: hasClinicalSummaryChanges,
        indications: hasIndicationsChanges,
        familyHistory: hasFamilyHistoryChanges,
        comparators: hasComparatorChanges,
        sequencing: hasSequencingChanges,
        patientConsent: hasPatientConsentChanges,
      },
      options: {
        requireClinicalSummary: Boolean(config.validationRules?.requireClinicalSummary),
        requirePhenotypes: Boolean(config.validationRules?.requirePhenotypes),
        requireFamilyHistory: familyHistoryRequired,
        incidentalApplicable: incidentalConsentGated(getPatientConsents(config)),
        comparatorMrnRequired,
        probandResearchContactConsent: storedCanonical?.patient?.researchContactConsent === true,
        nyRetentionRequireInteraction: consentInteractionGates.nyRetentionRequireInteraction,
        researchContactRequireInteraction: consentInteractionGates.researchContactRequireInteraction,
      },
    }),
    [
      storedCanonical,
      hasClinicalSummaryChanges,
      hasIndicationsChanges,
      hasFamilyHistoryChanges,
      hasComparatorChanges,
      hasSequencingChanges,
      hasPatientConsentChanges,
      config,
      familyHistoryRequired,
      comparatorMrnRequired,
      consentInteractionGates,
    ]
  )

  const incompleteChangedSections = useMemo(
    () => incompleteChangedSendSections(sendUpdatesCompletenessInput),
    [sendUpdatesCompletenessInput]
  )

  /**
   * True when a changed comparator (any relationship — parent, sibling,
   * extended relative) is not yet complete. Surfaces the incomplete non-parent
   * comparator rows in the banner (parents already have their own per-side
   * links) and blocks send. Only meaningful when comparators/sequencing changed.
   */
  const changedComparatorsIncomplete = useMemo(
    () =>
      (hasComparatorChanges || hasSequencingChanges) && !allOrderComparatorsComplete(sendUpdatesCompletenessInput),
    [hasComparatorChanges, hasSequencingChanges, sendUpdatesCompletenessInput]
  )

  /** Aggregate required-field completeness gate for "Send Updates". */
  const sendUpdatesHasIncompleteSection = incompleteChangedSections.length > 0


  /**
   * True when the (autosaved) clinical summary or differential diagnoses carry
   * characters outside the server free-text allowlist. Autosave persists the
   * clinical summary as the user types — including invalid characters — so this
   * guards the update submission: sending would be rejected by provider-api's
   * `ClinicalInfo.description` pattern. The Clinical Summary card/modal surfaces
   * the same error inline; this simply blocks "Send Updates" until it's fixed.
   */
  const clinicalSummaryHasInvalidChars = clinicalSummaryHasDisallowedChars(
    storedCanonical?.clinical,
    isValidFreeText
  )

  /**
   * Autosave persists clinical text past the modal's Done gate, so the lab's
   * clinical-text limits have to block Send Updates themselves.
   */
  const clinicalTextError = clinicalTextLimitError(fulfillmentStrategy, storedCanonical)
  const clinicalCombinedOverLimit = clinicalTextError !== null

  /**
   * Per-parent flag: patient research opt-in obligates this comparator to provide a contact
   * channel (email/phone) for research follow-up, but the parent is missing one. Mirrors the
   * `patientResearchContactAppliesToThisParent` prop already wired into each parent modal.
   */
  const motherNeedsResearchContactChannel = useMemo(() => {
    if (!storedCanonical) return false
    return (
      FormCompletionChecker.canonicalComparatorRowObligatedForProbandResearchContact('mother', storedCanonical, {
        parentSamplesRequired: parentSamplesForSequencingCard,
        selectedComparatorRelationships: inProgressSelectedParentRelationships,
      }) && !FormCompletionChecker.comparatorHasContactChannel(motherComp)
    )
  }, [
    storedCanonical,
    motherComp,
    parentSamplesForSequencingCard,
    inProgressSelectedParentRelationships,
  ])

  const fatherNeedsResearchContactChannel = useMemo(() => {
    if (!storedCanonical) return false
    return (
      FormCompletionChecker.canonicalComparatorRowObligatedForProbandResearchContact('father', storedCanonical, {
        parentSamplesRequired: parentSamplesForSequencingCard,
        selectedComparatorRelationships: inProgressSelectedParentRelationships,
      }) && !FormCompletionChecker.comparatorHasContactChannel(fatherComp)
    )
  }, [
    storedCanonical,
    fatherComp,
    parentSamplesForSequencingCard,
    inProgressSelectedParentRelationships,
  ])

  /**
   * UX-054: incomplete NON-parent comparators (sibling / extended relative)
   * surfaced in the Send Updates banner. Parents keep their own per-side links
   * (built from `motherComparatorSectionComplete` / `fatherComparatorSectionComplete`
   * below), so this list intentionally excludes MOTHER / FATHER to avoid double
   * links. Only meaningful once comparators/sequencing changed and only when the
   * side-nav editor is available to open the specific row. Uses the same
   * completeness definition as the send gate (`isCanonicalComparatorComplete`).
   */
  const incompleteComparatorSections = useMemo<ValidationFailureComparatorSection[]>(() => {
    if (!changedComparatorsIncomplete || !sideNavEnabled) return []
    const consents = getPatientConsents(config)
    const gates = resolveConsentInteractionGates(consents)
    const incidentalApplicable = gates.incidentalRequireInteraction
    return (storedCanonical?.comparators ?? [])
      .filter(
        (c): c is Comparator =>
          c != null && c.relationship !== 'PROBAND' && parentSideForComparator(c) === null
      )
      .filter(
        (c) =>
          !FormCompletionChecker.isCanonicalComparatorComplete(c, {
            incidentalApplicable,
            mrnRequired: comparatorMrnRequired,
            probandResearchContactConsent: storedCanonical?.patient?.researchContactConsent === true,
            nyRetentionRequireInteraction: gates.nyRetentionRequireInteraction,
            researchContactRequireInteraction: gates.researchContactRequireInteraction,
          })
      )
      .map((c) => ({
        id: typeof c.id === 'string' && c.id.trim() ? c.id : `comparator-${c.relationship}`,
        name: formatComparatorRelationshipLabel(c.relationship) || 'Comparator',
        onEdit: () => {
          if (typeof c.id === 'string' && c.id.trim()) setSelectedComparatorId(c.id)
        },
      }))
  }, [
    changedComparatorsIncomplete,
    sideNavEnabled,
    storedCanonical,
    config,
    comparatorMrnRequired,
    setSelectedComparatorId,
  ])

  /**
   * Sections whose state currently fails validation while the user is editing. The Send Updates
   * banner uses these to surface clickable "fix-me" links in place of the static "Order was sent on…"
   * info banner whenever the user has unsaved edits. Mirrors the OrderStart `sectionLinks` pattern.
   */
  const validationFailureSectionLinks = useMemo<AlertSectionLink[]>(
    () =>
      buildValidationFailureSectionLinks({
        // MOTHER/FATHER migration #6: parent rows are built by iterating the
        // selected parent relationships (order MOTHER → FATHER preserved) rather
        // than a hardcoded mother/father pair. ids/labels are relationship-derived
        // in the builder. The per-side section-complete / research-contact memos
        // are still consumed by the side-keyed ComparatorModal / side-nav
        // validation profile below, so they stay until that boundary is
        // generalized (item #4 cascade).
        parentSections: inProgressSelectedParentRelationships.map((relationship) => {
          const side: 'mother' | 'father' = relationship === 'MOTHER' ? 'mother' : 'father'
          const sectionComplete =
            side === 'mother' ? motherComparatorSectionComplete : fatherComparatorSectionComplete
          const needsResearchContactChannel =
            side === 'mother' ? motherNeedsResearchContactChannel : fatherNeedsResearchContactChannel
          return {
            relationship,
            selected: true,
            needsAttention: !sectionComplete || needsResearchContactChannel,
            onEdit: () => openComparatorEditorForParent(side),
          }
        }),
        comparatorSections: incompleteComparatorSections,
        guardianStandaloneResearchAttention,
        onEditGuardian: () => setGuardianContactModalOpen(true),
        clinicalSummaryHasInvalidChars,
        // UX-054: erase-to-empty / required-but-empty sections also surface as
        // fix-me links so the banner explains the disabled Send Updates button
        // instead of falsely claiming "Updates are ready to send."
        clinicalSummaryIncomplete: incompleteChangedSections.includes('clinicalSummary'),
        onEditClinicalSummary: () => setClinicalSummaryModalOpen(true),
        indicationsIncomplete: incompleteChangedSections.includes('phenotypes'),
        familyHistoryIncomplete: incompleteChangedSections.includes('familyHistory'),
        onEditFamilyHistory: () => setFamilyHistorySummaryModalOpen(true),
        additionalOptionsIncomplete: incompleteChangedSections.includes('additionalOptions'),
        onEditAdditionalOptions: () => setPatientConsentModalOpen(true),
      }),
    [
      inProgressSelectedParentRelationships,
      motherComparatorSectionComplete,
      motherNeedsResearchContactChannel,
      fatherComparatorSectionComplete,
      fatherNeedsResearchContactChannel,
      incompleteComparatorSections,
      guardianStandaloneResearchAttention,
      openComparatorEditorForParent,
      clinicalSummaryHasInvalidChars,
      incompleteChangedSections,
      setPatientConsentModalOpen,
    ]
  )

  const familyHistoryForModal = useMemo(
    () => familyCanonicalToSummaryData(storedCanonical?.family),
    [storedCanonical?.family],
  )

  const clinicalSummaryCardStatus = useMemo(
    () =>
      hasClinicalSummaryChanges
        ? ({ message: 'Updated by you - Ready to send to lab', variant: 'info' as const })
        : ({ message: 'Sent to lab', variant: 'success' as const }),
    [hasClinicalSummaryChanges],
  )

  const familyHistoryCardStatus = useMemo(
    () =>
      hasFamilyHistoryChanges
        ? ({ message: 'Updated by you - Ready to send to lab', variant: 'info' as const })
        : ({ message: 'Sent to lab', variant: 'success' as const }),
    [hasFamilyHistoryChanges],
  )

  // The fresh reanalysis decision for the CURRENT pending edit, or null when none
  // applies yet: not a reanalysis session, no reportable signal, the preview is
  // still in flight (decision cleared on signal change), it failed, or a resolved
  // decision belongs to a prior set of signals (`signalsKey` mismatch — stale).
  // Gating every derived value on this prevents the modal from promising a
  // reanalysis the commit won't fire (or hiding one it will) during the window
  // between a signal change and the fresh preview resolving.
  const freshReanalysisDecision =
    isReanalysis && reanalysisDecision?.signalsKey === reanalysisSignalsKey ? reanalysisDecision.decision : null

  // Reanalysis decision (reanalysis sessions only), owned by provider-api and
  // surfaced via the preview call above. The SPA no longer derives whether an
  // edit triggers a reanalysis or evaluates the in-flight / cap gate — it reads
  // the backend's authoritative decision. `null` when not a reanalysis session,
  // before the preview resolves, when the edits report no reanalysis signal, or
  // on a failed preview — all of which fail safe to "no reanalysis".
  const effectiveReanalysis = freshReanalysisDecision?.reanalysis_triggered === true

  // Whether the edits warranted a reanalysis at all, per the backend. Used to
  // tell "didn't warrant" apart from "warranted but blocked" for the modal copy.
  // The warranting rule is server-owned (and may differ from the SPA's former
  // logic) — the SPA does not reconstruct it.
  const changesWarrantReanalysis = freshReanalysisDecision?.changes_warranted_reanalysis === true

  // The edits would trigger a reanalysis, but the backend's gate blocks it (one
  // already in flight, or the org's per-order cap reached). The update is still
  // sent — the backend commits the edit without firing the reanalysis event — so
  // surface a note explaining a reanalysis won't be triggered. Driven entirely by
  // the server decision's `reanalysis_blocked_reason`.
  const reanalysisBlockedNote =
    changesWarrantReanalysis && freshReanalysisDecision?.reanalysis_blocked_reason != null
      ? freshReanalysisDecision.reanalysis_blocked_reason === 'in-flight'
        ? 'A reanalysis is already in progress for this order, so this update won’t trigger a new one.'
        : 'This order has reached the maximum number of reanalyses allowed by your organization, so this update won’t trigger a reanalysis.'
      : undefined

  const handleUpdateOrder = async () => {
    if (!canAddOrSendComparator) {
      return
    }
    const canonicalNow = appStore.getState().canonicalOrder.canonicalOrder
    const mrnCollision = FormCompletionChecker.findCanonicalOrderMrnCollision(canonicalNow)
    if (mrnCollision) {
      setUpdateError(
        `${mrnCollision.labels[0]} and ${mrnCollision.labels[1]} use the same MRN (${mrnCollision.mrn}). Each person needs a different MRN before sending updates.`
      )
      return
    }
    if (clinicalSummaryHasDisallowedChars(canonicalNow?.clinical, isValidFreeText)) {
      setUpdateError(
        'The clinical summary contains characters that are not allowed. Remove them before sending updates.'
      )
      return
    }
    const clinicalTextErrorNow = clinicalTextLimitError(fulfillmentStrategy, canonicalNow)
    if (clinicalTextErrorNow) {
      setUpdateError(clinicalTextErrorNow)
      return
    }
    if (
      hasSequencingChanges &&
      canonicalNow &&
      !comparatorSectionsReadyForSequencingSend(canonicalNow, config, { mrnRequired: comparatorMrnRequired })
    ) {
      setUpdateError(
        'Complete each biological comparator section, including MRN and incidental findings choices where shown, before sending sequencing updates.'
      )
      return
    }
    // UX-054 defense-in-depth: re-check changed-section completeness against the
    // fresh canonical (not the render closure) so an emptied required section or
    // an incomplete comparator (any relationship) can't be sent even if the
    // button gate was somehow bypassed. Server-side validation (provider-api)
    // remains the authoritative guard.
    if (
      incompleteChangedSendSections({
        ...sendUpdatesCompletenessInput,
        canonical: canonicalNow,
      }).length > 0
    ) {
      setUpdateError('Complete the highlighted sections before sending updates.')
      return
    }
    if (!orderData?.patient?.id) {
      setUpdateError('Unable to update order without patient data.')
      return
    }
    setUpdateError(null)
    setIsUpdatingOrder(true)

    try {
      const caller = getCallerFromOrderData(orderData)
      const existingOrder = urlOrderId
        ? await getOrderById(urlOrderId, caller)
        : await getOrderByPatient(orderData.patient.id, caller)
      if (!existingOrder?.id) {
        setUpdateError('Unable to update order. Order ID not found.')
        return
      }
      // Fresh canonical after any async work / modal saves (avoid stale render closure).
      const canonicalForPut = appStore.getState().canonicalOrder.canonicalOrder
      if (!canonicalForPut) {
        setUpdateError('Unable to update order: form state is not loaded.')
        return
      }
      const encounterFromCanonical = (canonicalForPut.ehrContext?.ehrEncounterId ?? '').trim()
      const encounterFromSubmitStorage =
        getEpicEhrEncounterForOrder(String(existingOrder.id))?.trim() ?? ''
      const encounterFromOrderForm = (orderData?.epicEncounter?.id ?? '').trim()
      const ehrEncounterForRedox =
        encounterFromCanonical || encounterFromSubmitStorage || encounterFromOrderForm
      // CSN companion to ehr_encounter_id: provider-api uses this as the tier-3 fallback
      // (Encounter?identifier=<csn_system>|<csn> via the SPA's SMART token) when the FHIR
      // encounter id is unavailable. Sourced from canonical.order.encounterCsn (matches
      // CANONICAL_FIELD_MATRIX) with a final fallback to the existing order's
      // ``requisitionFormData.order.encounter_csn`` (snake_case — populated server-side by
      // imdb_client>=0.16.5 on GETs; NOT on ``orderData`` which is ``OrderFormInfoData``
      // and has no ``order`` field).
      const csnFromCanonical = (canonicalForPut.order?.encounterCsn ?? '').trim()
      const csnFromExistingOrder = (
        existingOrder.json_payload?.requisitionFormData?.order?.encounter_csn ?? ''
      ).trim()
      const encounterCsnForRedox = csnFromCanonical || csnFromExistingOrder
      // SMART token session key: prefer navigation state (fresh handoff), else persisted OAuth
      // state (reload / deep link to in-progress — same pattern as getLaunchMode / ProviderAPIClient).
      const oauthStateForSmartFhir = epicState ?? getActiveEpicState()
      const epicAccessToken = oauthStateForSmartFhir
        ? EpicAuthClient.getInstance().getAccessToken(oauthStateForSmartFhir)?.trim() ?? ''
        : ''
      // Send each credential independently. Coupling them caused both to be dropped when only
      // the local encounter cache was missing (cross-machine edits, browser-cleared sessions,
      // pre-cache orders), preventing the server from using a perfectly valid access token to
      // resolve the encounter from persisted FHIR/JSON state.
      const redoxPutCredentials: {
        access_token?: string
        ehr_encounter_id?: string
        encounter_csn?: string
      } = {}
      if (oauthStateForSmartFhir && epicAccessToken) {
        redoxPutCredentials.access_token = epicAccessToken
      }
      if (ehrEncounterForRedox) {
        redoxPutCredentials.ehr_encounter_id = ehrEncounterForRedox
      }
      if (encounterCsnForRedox) {
        redoxPutCredentials.encounter_csn = encounterCsnForRedox
      }

      const updateResult = await updateOrder(existingOrder.id, orderData, {}, OrderStatus.SUBMITTED, caller, {
        canonicalOrder: canonicalForPut,
        ...redoxPutCredentials,
        // Report the raw change facts to provider-api, which owns the reanalysis
        // decision + enforcement. Only in a reanalysis session; a normal edit
        // reports no signals (the field is omitted). The backend reconstructs
        // `clinicalInformationModified && !comparatorAdded` and applies the
        // eligibility + in-flight / cap gate server-side.
        reanalysisSignals: isReanalysis
          ? {
              clinicalInformationModified: reanalysisClinicalInformationModified,
              comparatorAdded: reanalysisComparatorAdded,
            }
          : null,
      })
      // GEN-5494: refresh the banner's "sent" time from the PUT result without a
      // full reload, under two guards. Identity: apply only when the saved order
      // (`existingOrder.id`, the PUT target) is still the order on screen
      // (`currentOrderIdRef`), so a different-order PUT target or a navigate-away
      // mid-flight never writes this page's banner. Monotonic: `laterTrfSentAt`
      // only moves the time forward, so a degraded or garbage response never
      // blanks or regresses it.
      if (existingOrder.id === currentOrderIdRef.current) {
        setTrfSentAt((prev) => laterTrfSentAt(prev, updateResult?.trfSentAt))
      }
      // Authoritative decision from the commit response. May differ from the
      // preview if state changed in between (e.g. another reanalysis landed), so
      // the redirect/lock below uses THIS, not the pre-send `effectiveReanalysis`.
      const committedReanalysis = updateResult?.reanalysisDecision?.reanalysis_triggered === true
      // A material edit reissued the TRF envelope (GEN-5027). The reissue is
      // asynchronous and status-invisible — the order keeps projecting the
      // superseded envelope's `sent` status until the Lambda swaps the document —
      // so arm the poll to watch for the new `external_document_id`, keyed to the
      // superseded document. The page shows the "preparing" state until the
      // replacement lands, then resolves back to the sent badge.
      //
      // The baseline is the document currently outstanding. Prefer the one the page
      // already holds, but the reissue signal guarantees a current signature exists,
      // so read it fresh when local state is empty rather than skip arming — that
      // stale-state gap is what made an update look like it was doing nothing.
      let signingRedirectOrderId: string | null = null
      if (updateResult?.trfSignatureReissueRequested) {
        let reissuedFrom = trfSignature ?? null
        if (!reissuedFrom) {
          try {
            reissuedFrom = orderTrfSignatureFrom(await getOrderTrfSignature(existingOrder.id, caller)) ?? null
          } catch {
            // Best-effort: without a baseline the swap can't be detected, so leave
            // the poll unarmed rather than spin against an unknown document.
          }
        }
        if (reissuedFrom?.externalDocumentId) {
          armReissueWatch(reissuedFrom.externalDocumentId)
        }
        // Nothing is signable yet — the replacement envelope is built
        // asynchronously — so the signing page waits the window out. provider-api
        // refuses to mint against an envelope built from the superseded form,
        // which is what keeps the clinician from being handed the copy their edit
        // just replaced.
        signingRedirectOrderId = signingHandoffAfterUpdate({
          orderId: existingOrder.id,
          reissueRequested: true,
          reanalysisRequested: committedReanalysis,
          signature: reissuedFrom,
          providers: canonicalForPut?.providers ?? storedCanonical?.providers ?? null,
          currentUserNpi,
        })
      }
      const sentBaselines = captureCanonicalBaselines(canonicalForPut)
      baselineIndicationsRef.current = sentBaselines.indications
      baselineGuardianRef.current = sentBaselines.guardian
      baselineClinicalRef.current = sentBaselines.clinical
      baselineFamilyRef.current = sentBaselines.family
      baselineComparatorsRef.current = sentBaselines.comparators
      addedAfterSubmitKeysRef.current = recordAddedAfterSubmitKeys(
        addedAfterSubmitKeysRef.current,
        canonicalForPut.comparators ?? [],
        baselineComparatorKeysRef.current
      )
      baselineComparatorKeysRef.current = buildComparatorBaselineKeySet(canonicalForPut.comparators)
      baselineComparatorKeysReadyRef.current = true
      baselinePatientConsentRef.current = sentBaselines.patientConsent
      baselinePatientDemographicsRef.current = sentBaselines.patientDemographics
      sequencingBaselineRef.current = serializeSequencingBaseline(canonicalForPut)
      bumpSequencingBaselineVersion()
      setPersistedSequencingTestType((canonicalForPut.order?.selectedOption ?? '').trim())
      // The removals in `canonicalForPut` are now persisted, so re-hydration
      // will no longer carry the removed rows — clear the session guard set.
      removedComparatorKeysRef.current = new Set()
      setPendingComparatorCancellations([])
      setHasIndicationsChanges(false)
      setHasGuardianChanges(false)
      setHasClinicalSummaryChanges(false)
      setHasFamilyHistoryChanges(false)
      setHasComparatorChanges(false)
      setHasPatientConsentChanges(false)
      setHasPatientDemographicsChanges(false)
      setHasSequencingChanges(false)
      // Reanalysis one-shot: the edit that requested the reanalysis has now been
      // sent, moving the order to backend status "reanalysis". Force a FULL page
      // load of the plain route (dropping ?reanalysis=true) so the page remounts
      // and re-fetches status = "reanalysis", entering the read-only
      // reanalysis-locked state (status bar hidden, no Cancel Order, all edits +
      // Send Updates disabled). A client-side navigate() alone does NOT remount
      // this same-route component or refetch status, so `isReanalysis` /
      // `rawBackendStatus` stay stale and the locks never apply until a manual
      // reload — hence the hard reload here. Only when the reanalysis flag was
      // actually sent (checkbox opted in), not for an un-checked reanalysis
      // session that was sent as a plain update.
      //
      // The two exits are mutually exclusive by construction: a reanalysis update
      // yields no signing hand-off, because a locked order has nothing to sign.
      if (signingRedirectOrderId) {
        // A hard navigation, as on the Sign TRF button and on submit: /sign is
        // served under the relaxed CSP/COEP the ceremony iframe needs.
        markTrfSigningInProgress(signingRedirectOrderId)
        window.location.assign(trfSigningPath(signingRedirectOrderId))
      } else if (committedReanalysis && urlOrderId) {
        const lockedUrl = `/order/in-progress/${encodeURIComponent(urlOrderId)}${
          urlPatientId ? `/${encodeURIComponent(urlPatientId)}` : ''
        }`
        window.location.assign(lockedUrl)
      }
    } catch (error) {
      if (isMrnConflictError(error)) {
        // GEN-3010: surface the backend's specific MRN-in-use message inline so
        // the user can pick a different MRN. Not a bug — do not Sentry-log.
        setUpdateError(error.message)
      } else if (isOrderRejectedError(error)) {
        // GEN-5654: provider-api 422 with a PHI-free message (e.g. comparator
        // contact required). Show it inline; it is a validation outcome, not a bug.
        setUpdateError(error.message)
      } else {
        Sentry.captureException(error, { tags: { operation: 'order.hpo.update' } })
        setUpdateError('Failed to update order. Please try again.')
      }
    } finally {
      setIsUpdatingOrder(false)
    }
  }

  /**
   * Navigation guard for unsent edits. `hasAnyUnsentChanges` mirrors the
   * "Send Updates" enable condition. When true, four exit paths are
   * intercepted so the user can confirm before losing canonical state that
   * hasn't been pushed to the lab:
   *   1. Anchor element clicks (capture-phase listener) - covers <Link>
   *      and <NavLink> from react-router as well as plain <a> tags.
   *   2. Programmatic navigation via `history.pushState`/`replaceState`
   *      (react-router-dom calls these under the hood for `useNavigate`).
   *      Required because the Navbar logo, logout button, and other
   *      button-driven nav points are not anchors.
   *   3. Browser back / forward (sentinel `pushState` + `popstate`).
   *   4. Full-page exits (`beforeunload`).
   *
   * We can't use `useBlocker` from react-router-dom because the app is
   * mounted inside the legacy `<BrowserRouter>` (not a data router). The
   * manual approach below is router-agnostic.
   */
  const hasAnyUnsentChanges =
    hasIndicationsChanges ||
    hasGuardianChanges ||
    hasClinicalSummaryChanges ||
    hasFamilyHistoryChanges ||
    hasComparatorChanges ||
    hasPatientConsentChanges ||
    hasPatientDemographicsChanges ||
    hasSequencingChanges

  // A cancelled order is terminal — its unsent local edits can no longer be
  // pushed to the lab ("Send Updates" is gone once cancelled), so navigating
  // back to the workspace must not prompt about losing them.
  const shouldBlockNavigation = shouldBlockUnsavedChangesNav({
    hasUnsentChanges: hasAnyUnsentChanges,
    isUpdatingOrder,
    isCancelled,
  })

  const { showUnsavedChangesModal, handleKeepEditing, handleDiscardChanges } =
    useUnsavedChangesNavGuard(shouldBlockNavigation)

  if (isLoadingOrder) {
    return <OrderLoading />
  }

  if (orderLoadFailure === 'not_found') {
    return <OrderNotFound />
  }

  if (orderLoadFailure === 'unauthorized') {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (orderLoadFailure === 'error') {
    return <OrderLoadError message="Something went wrong loading this order. Please try again." />
  }

  const statusBannerNode =
    canAddOrSendComparator && hasAnyChanges ? (
      <AlertBanner
        useInflectionIcon={true}
        className="border-2 border-border-brand dark:border-brand-500 shadow-sm ring-1 ring-border-brand-subtle"
      >
        <span className="font-semibold text-text-heading dark:text-white">Pending changes — not sent to the lab yet.</span>{' '}
        {pendingChangesSummaryText}{' '}
        {clinicalTextError ? (
          <>{clinicalTextError}</>
        ) : validationFailureSectionLinks.length > 0 ? (
          <>
            {validationFailureSectionLinks.map((section, index) => {
              const isLast = index === validationFailureSectionLinks.length - 1
              const isSecondToLast = index === validationFailureSectionLinks.length - 2
              return (
                <span key={section.id}>
                  <button
                    type="button"
                    onClick={section.onClick}
                    className="font-medium underline hover:text-brand-600 dark:hover:text-brand-400"
                  >
                    {section.name}
                  </button>
                  {isSecondToLast && validationFailureSectionLinks.length > 2 && ', and '}
                  {isSecondToLast && validationFailureSectionLinks.length === 2 && ' and '}
                  {!isLast && !isSecondToLast && ', '}
                </span>
              )
            })}{' '}
            need your input before you can send updates to the lab.
          </>
        ) : clinicalSummaryReviewBlocksSend ? (
          <>Resolve the phenotypes that need your input before you can send updates to the lab.</>
        ) : (
          <>
            Click <span className="font-semibold">&ldquo;Send Updates&rdquo;</span> to send them to the lab.
          </>
        )}
        {comparatorAddWindowMessage ? <> {comparatorAddWindowMessage}</> : null}
      </AlertBanner>
    ) : isCancelled ? (
      <AlertBanner variant="error">{statusAlertMessage}</AlertBanner>
    ) : (
      <AlertBanner useInflectionIcon={true}>{statusAlertMessage}</AlertBanner>
    )

  const updateErrorNode = updateError ? (
    <AlertBanner onClose={() => setUpdateError(null)} variant="error">
      {updateError}
    </AlertBanner>
  ) : null

  const cancelErrorNode = cancelError ? (
    <AlertBanner onClose={() => setCancelError(null)} variant="error">
      {cancelError}
    </AlertBanner>
  ) : null

  // Cancel kebab: shown for both fulfillment strategies (canCancelOrder), gated
  // by the per-strategy status window (isCancellableStatusForStrategy):
  //   - inflection_test (LSMC): any status except `cancelled`.
  //   - rcigm (Rady): only before accessioning (order_placed / order_submitted),
  //     mirroring Rady's Registered/Hold server-side eligibility.
  const canShowCancelKebab =
    !reanalysisLocked &&
    canCancelOrder(config) &&
    isCancellableStatusForStrategy(getFulfillmentStrategy(config), orderStatus)

  // GEN-4647 (WIP): placeholder gate for blocking Cancel Order once a proband
  // sample has been collected. Stubbed to the non-blocking default so the kebab
  // renders the normal actionable "Cancel Order" item; wire up the real
  // proband-sample condition + copy when that work lands.
  const cancelBlockedByProbandSample = false
  const cancelBlockedReason = ''

  const handleConfirmCancel = () =>
    Sentry.startSpan({ op: 'ui.click', name: 'Cancel Order' }, async () => {
      const cancelId = urlOrderId
      if (!cancelId) {
        setCancelError("Couldn't cancel this order. Try again.")
        return
      }
      setIsCancelling(true)
      setCancelError(null)
      try {
        const caller = getCallerFromOrderData(orderData)
        // Lab-agnostic Redox cancel (opt-in): opt in whenever we're in an Epic session
        // with a live SMART token. We deliberately do NOT gate on a client-side Epic
        // Encounter binding: provider-api resolves the ServiceRequest/Encounter server-side
        // from the persisted CSN at cancel time, so requiring the encounter here only
        // starved the revoke for orders whose ehrContext was dropped on an IMDB round-trip
        // (all three former client sources trace back to that one fact). The revoke is
        // best-effort server-side, so sending the flag for an order it can't resolve is a
        // safe no-op. We still never send the flag without a token (that would trip the
        // provider-api "no EHR access token" abort).
        const oauthStateForRedoxCancel = epicState ?? getActiveEpicState()
        const epicAccessToken =
          authFlow === 'epic' && oauthStateForRedoxCancel
            ? EpicAuthClient.getInstance().getAccessToken(oauthStateForRedoxCancel)?.trim() ?? ''
            : ''
        const cancelViaRedox = authFlow === 'epic' && Boolean(epicAccessToken)
        // Diagnostic: an Epic order that can't opt in now has exactly one remaining cause —
        // no live SMART token (expired session, unresolvable OAuth state). The encounter is
        // no longer part of the decision. Surface that case so a skipped Epic revoke is
        // self-explaining from the SPA side rather than only visible in provider-api logs.
        if (authFlow === 'epic' && !cancelViaRedox) {
          Sentry.logger.warn('Redox cancel opt-in skipped: Epic order without a live SMART token', {
            orderId: cancelId,
            hasOauthState: Boolean(oauthStateForRedoxCancel),
          })
        }
        await cancelOrder(
          cancelId,
          caller,
          cancelViaRedox ? { cancelViaRedox: true, ehrAccessToken: epicAccessToken } : undefined
        )
        setOrderStatus(OrderStatusDisplay.CANCELLED)
        setCancelConfirmOpen(false)
      } catch (e) {
        // Degenerate contract: never optimistically mark cancelled on failure.
        Sentry.captureException(e)
        setCancelConfirmOpen(false)
        setCancelError("Couldn't cancel this order. Try again.")
      } finally {
        setIsCancelling(false)
      }
    })

  // Unsent edits and signing are mutually exclusive on an unsigned DocuSign
  // order. The button only renders while the envelope is still signable; hold
  // it closed until those edits are sent or discarded so the ceremony cannot
  // open against a form the clinician has not finished.
  const signTrfBlockedByPendingUpdates =
    docusignEnabled && hasAnyChanges && trfSignature?.isSigned !== true
  const signTrfDisabled = trfSigningInProgress || signTrfBlockedByPendingUpdates

  return (
    <div className={authFlow === 'standalone' ? 'page-container-standalone' : 'page-container'}>
      {authFlow === 'standalone' && (
        <Navbar
          userAvatar={auth0User?.picture}
          userName={auth0User?.name}
          onLogout={handleLogout}
          showNavLinks={true}
          organizationName={getClientName(config)}
        />
      )}
      <div className={`flex w-full flex-col gap-4 ${authFlow === 'standalone' ? 'px-6' : ''}`}>
        <div
          data-sticky-page-header
          className={`sticky top-0 z-40 border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-sm sm:p-6 ${
            authFlow === 'standalone' ? '-mx-6 border-b' : 'rounded-base border'
          }`}
        >
          <div className="flex flex-row flex-wrap items-center justify-between gap-3 md:flex-nowrap md:gap-6">
            <div className="flex min-w-0 flex-1 flex-row items-center gap-3 sm:gap-6 md:flex-none">
              {sideNavEnabled && (
                <OrderNavToggle
                  drawerOpen={sideNavDrawerOpen}
                  onOpen={() => setSideNavDrawerOpen(true)}
                />
              )}
              <div className="flex min-w-0 flex-col gap-1">
                <TruncatedText
                  as="h1"
                  text={patientName || 'Patient'}
                  className="text-xl font-semibold tracking-tight text-text-heading dark:text-white sm:text-2xl"
                />
                {mrnRaw ? (
                  <CopyableValue
                    copyValue={mrnRaw}
                    ariaLabel="Copy MRN"
                    truncate
                    overflowLabel={mrnDisplay}
                    className="w-fit max-w-full text-xs font-medium text-text-body dark:text-gray-400"
                  >
                    {mrnDisplay}
                  </CopyableValue>
                ) : (
                  <span className="text-xs font-medium text-text-body dark:text-gray-400">MRN not available</span>
                )}
                {inflectionExternalId ? <OrderExternalIdLabel externalId={inflectionExternalId} /> : null}
                {/*
                 * Between submit and the envelope reaching `sent`, show the
                 * preparing state; after that the badge takes over (GEN-5027).
                 * The badge itself renders nothing when no request is
                 * outstanding — see TrfSignatureBadge.
                 */}
                {trfSignaturePreparingState ? (
                  <TrfSignaturePreparing className="mt-1 self-start" />
                ) : trfSigningInProgress ? (
                  <TrfSignatureRecording className="mt-1 self-start" />
                ) : (
                  <TrfSignatureBadge
                    signature={trfSignature}
                    className="mt-1 self-start"
                    declineReasonOpen={declineReasonOpen}
                    onToggleDeclineReason={toggleDeclineReason}
                  />
                )}
              </div>
              <div className="h-10 w-px shrink-0 bg-border-base" />
              {/* `min-w-0` so age/DOB can ellipsise in Epic's narrow sidebar
                  rather than pushing the patient name to absorb the overflow. */}
              <div className="flex min-w-0 flex-col gap-1">
                <TruncatedText
                  text={ageDisplay || 'Age unavailable'}
                  className="text-sm font-bold text-text-heading dark:text-white"
                />
                <TruncatedText
                  text={dobDisplay || 'DOB unavailable'}
                  className="text-xs font-medium text-text-body dark:text-gray-400"
                />
              </div>
            </div>

            {/* R5: inline progress stepper shares the header row next to the age
                info on md+; on small screens it wraps to its own full-width line
                INSIDE the sticky header (order-last), matching OrderStart's
                condensed header so the timeline stays pinned with the header
                rather than scrolling away in a separate card below. */}
            {sideNavEnabled && steps.length > 0 && (
              <>
                <div className="hidden h-10 w-px bg-border-base md:block" />
                <div className="order-last w-full min-w-0 md:order-none md:flex-1 md:px-2">
                  <OrderStepsRow variant="tracker" steps={steps} />
                </div>
                <div className="hidden h-10 w-px bg-border-base md:block" />
              </>
            )}

            <div className="flex shrink-0 flex-nowrap items-center justify-end gap-2 md:ml-0">
              <div className="relative shrink-0" ref={cancelMenuRef}>
                <button
                  type="button"
                  id="order-actions-menu-trigger"
                  aria-label="Order actions"
                  aria-haspopup="menu"
                  aria-expanded={cancelMenuOpen}
                  aria-controls="order-actions-menu"
                  onClick={() => setCancelMenuOpen((open) => !open)}
                  className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border-base-medium dark:border-gray-700 bg-background-secondary-medium dark:bg-gray-800 px-4 py-2.5 text-sm font-medium text-text-body dark:text-gray-400 shadow-xs transition-colors hover:bg-background-tertiary dark:hover:bg-gray-700"
                >
                  <DotsVertical className="h-5 w-5 shrink-0" aria-hidden />
                  <span>More</span>
                </button>
                {cancelMenuOpen && (
                  <div
                    id="order-actions-menu"
                    role="menu"
                    aria-labelledby="order-actions-menu-trigger"
                    className="absolute right-0 top-full z-50 mt-2 min-w-[min(100vw-2rem,16rem)] overflow-hidden rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 py-1 shadow-lg sm:min-w-56"
                  >
                    {isReanalysis ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={handleViewReport}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-body dark:text-gray-400 transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                      >
                        <ChartPie className="h-4 w-4 shrink-0" aria-hidden />
                        View Report
                      </button>
                    ) : null}
                    {reanalysisLocked ? (
                      // Locked reanalysis order (viewed without ?reanalysis=true):
                      // the only action is to view the initial (completed) report.
                      // "View Order Form" is suppressed below in this state.
                      <button
                        type="button"
                        role="menuitem"
                        onClick={handleViewReport}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-body dark:text-gray-400 transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                      >
                        <ChartPie className="h-4 w-4 shrink-0" aria-hidden />
                        View Initial Report
                      </button>
                    ) : null}
                    {!reanalysisLocked ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setCancelMenuOpen(false)
                          setOrderFormRegenerateToken((t) => t + 1)
                          setIsOrderFormModalOpen(true)
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-body dark:text-gray-400 transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                      >
                        <FileLines className="h-4 w-4 shrink-0" aria-hidden />
                        View Order Form
                      </button>
                    ) : null}
                    {clinicalFieldsEditable ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setCancelMenuOpen(false)
                          setPatientDemographicsEditOpen(true)
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-body dark:text-gray-400 transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                      >
                        <Pen className="h-4 w-4 shrink-0" aria-hidden />
                        Edit Patient Details
                      </button>
                    ) : null}
                    {canShowCancelKebab &&
                      !isReanalysis &&
                      (cancelBlockedByProbandSample ? (
                        <div
                          role="menuitem"
                          aria-disabled="true"
                          title={cancelBlockedReason}
                          className="flex w-full cursor-not-allowed flex-col gap-1 px-4 py-2.5 text-left"
                        >
                          <span className="flex items-center gap-2 text-sm text-text-disabled dark:text-gray-600">
                            <Close className="h-4 w-4 shrink-0" aria-hidden />
                            Cancel Order
                          </span>
                          <span className="pl-6 text-xs font-medium text-text-body-subtle dark:text-gray-500">
                            {cancelBlockedReason}
                          </span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setCancelMenuOpen(false)
                            setCancelConfirmOpen(true)
                          }}
                          className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-body dark:text-gray-400 transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                        >
                          <Close className="h-4 w-4 shrink-0" aria-hidden />
                          Cancel Order
                        </button>
                      ))}
                  </div>
                )}
              </div>
              <RestartOrderAction
                orderId={urlOrderId}
                isCancelled={isCancelled}
                authFlow={authFlow}
                epicState={epicState}
                orgId={orgId}
                orderOrgUuidCandidate={orderOrgUuidCandidate}
              />
              {/* R5: the cards (and side-nav comparator detail) are directly
                  editable, so the "Edit Order" dropdown is redundant and
                  hidden. The legacy single-column layout keeps it. */}
              {!sideNavEnabled && canAddOrSendComparator && (
                <div className="relative shrink-0" ref={editOrderMenuRef}>
                  <button
                    type="button"
                    id="edit-order-menu-trigger"
                    aria-haspopup="menu"
                    aria-expanded={editOrderMenuOpen}
                    aria-controls="edit-order-menu"
                    onClick={() => setEditOrderMenuOpen((open) => !open)}
                    className="inline-flex items-center gap-2 rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-text-body dark:text-gray-400 shadow-xs transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                  >
                    <Pen className="h-4 w-4" aria-hidden />
                    <span>Edit Order</span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 transition-transform ${editOrderMenuOpen ? 'rotate-180' : ''}`}
                      aria-hidden
                    />
                  </button>
                  {editOrderMenuOpen && (
                    <div
                      id="edit-order-menu"
                      role="menu"
                      aria-labelledby="edit-order-menu-trigger"
                      className="absolute right-0 top-full z-50 mt-2 min-w-[min(100vw-2rem,16rem)] overflow-hidden rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 py-1 shadow-lg sm:min-w-[14rem]"
                    >
                      {clinicalFieldsEditable ? (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setEditOrderMenuOpen(false)
                            setClinicalSummaryModalOpen(true)
                          }}
                          className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-body dark:text-gray-400 transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                        >
                          <FileLines className="h-4 w-4 shrink-0" aria-hidden />
                          Edit Clinical Summary
                        </button>
                      ) : null}
                      {clinicalFieldsEditable ? (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setEditOrderMenuOpen(false)
                            setFamilyHistorySummaryModalOpen(true)
                          }}
                          className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-body dark:text-gray-400 transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                        >
                          <Pen className="h-4 w-4 shrink-0" aria-hidden />
                          Edit Family History
                        </button>
                      ) : null}
                      {canOpenSequencingOptionsModal ? (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setEditOrderMenuOpen(false)
                            openComparatorSequencingModal()
                          }}
                          className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-text-body dark:text-gray-400 transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                        >
                          <Pen className="h-4 w-4 shrink-0" aria-hidden />
                          Add Comparator
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
              {canAddOrSendComparator && (
                <Button
                  variant="primary"
                  size="md"
                  type="button"
                  onClick={() => {
                    setSendUpdatesConfirmOpen(true)
                  }}
                  disabled={
                    !hasAnyChanges ||
                    isUpdatingOrder ||
                    (hasSequencingChanges && !sequencingComparatorSendReady) ||
                    guardianStandaloneResearchAttention ||
                    clinicalSummaryHasInvalidChars ||
                    clinicalCombinedOverLimit ||
                    clinicalSummaryReviewBlocksSend ||
                    sendUpdatesHasIncompleteSection
                  }
                  title={
                    clinicalTextError
                      ? clinicalTextError
                      : clinicalSummaryHasInvalidChars
                        ? 'The clinical summary contains characters that are not allowed. Remove them before sending updates.'
                        : clinicalSummaryReviewBlocksSend
                          ? 'Resolve the phenotypes that need your input before sending updates.'
                          : sendUpdatesHasIncompleteSection
                            ? 'Complete the highlighted sections before sending updates.'
                            : undefined
                  }
                  className="shrink-0 whitespace-nowrap focus-visible:ring-brand-600"
                >
                  {isUpdatingOrder ? 'Sending…' : 'Send Updates'}
                </Button>
              )}
              {/* In-app TRF signing (GEN-5028): shown only to the clinician of
                  record while the signature is signable and embedded signing is
                  enabled. A hard navigation loads /sign under its relaxed
                  CSP/COEP behaviour. The server re-authorizes on mint. */}
              {showSignTrfCta && (
                <Button
                  variant="primary"
                  size="md"
                  type="button"
                  onClick={handleSignTrf}
                  disabled={signTrfDisabled}
                  aria-busy={trfSigningInProgress}
                  title={
                    signTrfBlockedByPendingUpdates
                      ? 'Send or discard your updates before signing the TRF.'
                      : undefined
                  }
                  className="shrink-0 whitespace-nowrap focus-visible:ring-brand-600"
                >
                  {trfSigningInProgress ? (
                    <>
                      {/* helix-exception: spinner kept; Flowbite Spinner would add role="status" and a label (F-13) */}
                      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Sign TRF</span>
                    </>
                  ) : (
                    <>
                      <Pen className="h-4 w-4" aria-hidden />
                      <span>Sign TRF</span>
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          {/*
           * The decline reason is a row of its own, under the header row rather
           * than inside the patient column, so revealing it cannot widen that
           * column or re-centre the age and the action buttons beside it.
           */}
          {declineReasonOpen ? <TrfDeclineReasonRow reason={trfSignature?.declineReason ?? null} /> : null}
        </div>

        {!sideNavEnabled ? (
          <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-6 pb-6 pt-12 shadow-sm">
            {steps.length > 0 && <OrderStepsRow variant="tracker" steps={steps} />}
            <EpicSessionExpiryWarning className={steps.length > 0 ? 'mt-3' : ''} />
            <div className={steps.length > 0 ? 'mt-3' : ''}>{statusBannerNode}</div>
            {updateErrorNode && <div className="mt-3">{updateErrorNode}</div>}
            {cancelErrorNode && <div className="mt-3">{cancelErrorNode}</div>}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <EpicSessionExpiryWarning />
            {statusBannerNode}
            {updateErrorNode}
            {cancelErrorNode}
          </div>
        )}

        <div className={sideNavEnabled ? 'flex w-full items-start gap-4' : 'contents'}>
          {sideNavEnabled && (
            <div className="sticky top-(--order-sticky-top) z-50 shrink-0 self-start lg:z-30 lg:max-h-[calc(100vh-var(--order-sticky-top)-1rem)] lg:overflow-y-auto">
              <OrderSideNav
                sections={sideNavSectionsForMergedClinicalSummary(
                  sideNavSections,
                  clinicalSummaryReviewBlocksSend,
                )}
                comparatorGroups={comparatorGroups}
                selectedComparatorId={selectedComparatorId}
                onSelectComparator={setSelectedComparatorId}
                onAddComparator={
                  canAddComparatorsForOrder ? () => setAddComparatorOpen(true) : undefined
                }
                addComparatorDisabled={comparatorLimitReached}
                addComparatorDisabledReason={comparatorLimitReachedReason ?? undefined}
                drawerOpen={sideNavDrawerOpen}
                onDrawerOpenChange={setSideNavDrawerOpen}
              />
            </div>
          )}
          <div className={sideNavEnabled ? 'flex min-w-0 flex-1 flex-col gap-4' : 'flex flex-col gap-4'}>
          {sideNavEnabled ? (
            <div className="sticky top-(--order-header-h) z-20 -mx-1 -mt-4 flex items-center gap-3 bg-background-variant-gray dark:bg-gray-950 px-1 pb-2 pt-4">
              <h2 className="text-xl font-semibold tracking-tight text-text-heading dark:text-white">
                Patient
                {patientName ? (
                  <span className="text-text-body-subtle dark:text-gray-500"> · {patientName}</span>
                ) : null}
              </h2>
            </div>
          ) : null}
          <>
          <section id="order-section-sequencing" className="scroll-mt-28">
          <TestOptionsCard
            testTypeLabel={selectedTestTypeLabel}
            turnaroundDescription={turnaroundSequencingSubtitle}
            familySamples={sequencingFamilySamplesTile}
            sampleType={mapCollectionTypeToSampleType(storedCanonical?.patient?.collectionType as CollectionType | undefined)}
            statusSuccessText="Sent to lab"
            // R5 side-nav: speed and sample type are both read-only on a
            // submitted order, and comparators are added via the side-nav (not
            // this modal), so the Sequencing Options modal has nothing editable
            // — hide the card's Edit button. The legacy layout keeps the modal
            // as its comparator-edit entry point.
            onEdit={
              !sideNavEnabled && canOpenSequencingOptionsModal
                ? () => openComparatorSequencingModal()
                : undefined
            }
            // Embedded "Add Comparator" pill (mirrors the Indications card's
            // "Add Phenotypes" action). Uses the side-nav comparator-add flow
            // and its cap-based disabled state; only shown when adding
            // comparators is applicable for this order.
            onAddComparator={
              sideNavEnabled && canAddComparatorsForOrder
                ? () => {
                    if (comparatorLimitReached) return
                    setAddComparatorOpen(true)
                  }
                : undefined
            }
            addComparatorDisabled={comparatorLimitReached}
            addComparatorDisabledReason={comparatorLimitReachedReason ?? undefined}
            // R5 side-nav: comparators get their own column, so drop the
            // third "Include N family samples" tile and show the purple
            // required-section accent — matching OrderStart's R5 layout.
            r5SpeedAndSampleOnly={sideNavEnabled}
            required={sideNavEnabled}
          />
          </section>

          <section id="order-section-clinical" className="scroll-mt-28">
          <ClinicalSummaryCard
            summary={storedCanonical?.clinical?.clinicalNote ?? ''}
            differentialDiagnosis={storedCanonical?.clinical?.differentialDiagnosis ?? ''}
            priorGeneticTesting={storedCanonical?.clinical?.priorGeneticTesting ?? ''}
            emptyStateText="None entered"
            statusMessage={clinicalSummaryCardStatus.message}
            statusVariant={clinicalSummaryCardStatus.variant}
            onEdit={clinicalFieldsEditable ? () => setClinicalSummaryModalOpen(true) : undefined}
            required={sideNavEnabled && Boolean(config.validationRules?.requireClinicalSummary)}
          />
          </section>

          {(() => {
            // The phenotypes on the order were extracted from the summary as it was
            // sent to the lab, so they may not match the version being edited. The
            // cue is passive: the editor itself reconciles on the next extraction.
            const previousSummary = parseClinicalBaselineSummary(baselineClinicalRef.current)
            const currentClinicalNote = (storedCanonical?.clinical?.clinicalNote ?? '').trim()
            if (!shouldShowStaleHpoBanner(previousSummary, currentClinicalNote)) {
              return null
            }
            return <EditFlowStaleHpoBanner previousSummary={previousSummary} />
          })()}

          <section id="order-section-family-history" className="scroll-mt-28">
          <FamilyHistoryCard
            hasData={Boolean(
              storedCanonical?.family?.familyHistory?.trim() ||
                storedCanonical?.family?.hasParent ||
                storedCanonical?.family?.hasGrandparent ||
                storedCanonical?.family?.hasSibling ||
                storedCanonical?.family?.hasCousin
            )}
            summary={storedCanonical?.family?.familyHistory?.trim() ?? ''}
            details={familyHistoryDetailsFromCanonical(storedCanonical?.family)}
            statusMessage={familyHistoryCardStatus.message}
            statusVariant={familyHistoryCardStatus.variant}
            showEmptyState={true}
            onEdit={clinicalFieldsEditable ? () => setFamilyHistorySummaryModalOpen(true) : undefined}
            required={sideNavEnabled && familyHistoryRequired}
          />
          </section>

          {/* Consent ("Additional Options") renders before Guardian Contact:
              the proband's consent choices (research-contact opt-in /
              incidental findings) gate whether Guardian Contact is required,
              so consent is collected first. Applies to draft + submitted. */}
          {storedCanonical && comparatorConsentAnyApplicable(comparatorParentConsentConfigs) ? (
            <div id={PROBAND_INCIDENTAL_FINDINGS_ANCHOR_ID} className="scroll-mt-24">
              {sideNavEnabled ? (
                <PatientConsentCard
                  configs={comparatorParentConsentConfigs}
                  patient={storedCanonical.patient}
                  onEdit={canEditIndications ? () => setPatientConsentModalOpen(true) : undefined}
                  needsAttention={probandConsentNeedsAttention}
                  required
                />
              ) : (
                <ProbandConsentSections
                  configs={comparatorParentConsentConfigs}
                  patient={storedCanonical.patient}
                  onPatch={(patch) => dispatch(mergePatient(patch))}
                  readOnly={!canEditIndications}
                  showValidationErrors={probandConsentNeedsAttention}
                />
              )}
            </div>
          ) : null}

          {/* R5: dedicated patient Guardian Contact section. When a comparator
              is the primary guardian the card mirrors it read-only and links to
              that comparator's detail view (single source of truth). */}
          {sideNavEnabled && (
            <section id="order-section-guardian" className="scroll-mt-28">
              {primaryGuardianComparator ? (
                <GuardianContactCard
                  firstName={primaryGuardianComparator.demographic?.firstName}
                  lastName={primaryGuardianComparator.demographic?.lastName}
                  dateOfBirth={primaryGuardianComparator.demographic?.dateOfBirth}
                  email={primaryGuardianComparator.demographic?.email}
                  phone={primaryGuardianComparator.demographic?.phone}
                  relationshipLink={{
                    label:
                      formatComparatorRelationshipLabel(primaryGuardianComparator.relationship) ||
                      'Comparator',
                    // Read-only mirror: route straight to the comparator's
                    // Demographics section so its contact info stays the single
                    // source of truth.
                    onClick: () =>
                      setComparatorSectionEdit({
                        id: primaryGuardianComparator.id as string,
                        group: 'demographics',
                      }),
                  }}
                  needsAttention={
                    guardianStandaloneResearchAttention || !primaryGuardianComparatorComplete
                  }
                  statusMessage={
                    guardianStandaloneResearchAttention
                      ? `Edit ${
                          formatComparatorRelationshipLabel(primaryGuardianComparator.relationship) ||
                          'comparator'
                        }'s contact for research contact`
                      : !primaryGuardianComparatorComplete
                        ? 'Incomplete'
                        : `Uses ${
                            formatComparatorRelationshipLabel(primaryGuardianComparator.relationship) ||
                            'comparator'
                          }'s contact`
                  }
                  statusVariant="info"
                />
              ) : (
                <GuardianContactCard
                  {...guardianContactCardFieldsFromCanonical(storedCanonical?.guardianContact)}
                  onEdit={canEditIndications ? () => setGuardianContactModalOpen(true) : undefined}
                  needsAttention={guardianStandaloneResearchAttention}
                  statusMessage={
                    guardianStandaloneResearchAttention
                      ? 'Guardian: add email or phone for research contact'
                      : undefined
                  }
                  statusVariant={guardianStandaloneResearchAttention ? 'info' : undefined}
                />
              )}
            </section>
          )}

          {/* Legacy single-column family list. In R5 comparators are surfaced as
              side-nav groups + the detail view, so this is hidden there. */}
          {!sideNavEnabled &&
          (() => {
            /**
             * F.1c (GEN-2999) Slice B1: `<ComparatorList>` is the sole
             * family-section render path. The legacy `<FamilyCard>` branch has
             * been removed and the projection runs unconditionally.
             * Hydration keeps `comparators[]` consistent with the legacy
             * `mother` / `father` slots, and the projection synthesizes empty
             * items for any parent row the user has not filled yet, so the
             * section never goes blank.
             */
            const comparatorProjection = projectComparatorsToOrderInProgressRows(
              familyDisplay,
              storedCanonical?.comparators,
              storedCanonical,
              { synthesizeEmptyForUnmatchedParents: true }
            )

            if (comparatorProjection) {
              return (
                <ComparatorList
                  display={familyDisplay}
                  parentItems={comparatorProjection.parentItems}
                  // GEN-2999: `deriveOrderInProgressFamilyRows` only emits a
                  // GUARDIAN_CONTACT row for proband-only orders, so this slot
                  // renders the standalone guardian card for proband-only. On
                  // family orders guardianship is owned by the comparator
                  // parents (PG badge), so no guardian row is produced here.
                  renderGuardianRow={(row, rowIndex) => (
                    <GuardianContactCard
                      key={`in-progress-guardian-${rowIndex}`}
                      {...guardianContactCardFieldsFromCanonical(storedCanonical?.guardianContact)}
                      showPrimaryGuardianBadge={row.showPrimaryGuardianBadge}
                      onEdit={canEditIndications ? () => setGuardianContactModalOpen(true) : undefined}
                      needsAttention={guardianStandaloneResearchAttention}
                      statusMessage={
                        guardianStandaloneResearchAttention
                          ? 'Guardian: add email or phone for research contact'
                          : undefined
                      }
                      statusVariant={guardianStandaloneResearchAttention ? 'info' : undefined}
                    />
                  )}
                  resolveEditHandlerForParent={(item) => {
                    // A guardian-backed row's person lives on
                    // `canonical.guardianContact`, so its pencil opens the
                    // guardian editor — the same one the standalone guardian
                    // card and the side-nav layout use. Routing it to
                    // <ComparatorModal> would persist a family member this
                    // proband-only order does not sequence.
                    if (item.guardianContactBacked) {
                      return canEditIndications ? () => setGuardianContactModalOpen(true) : undefined
                    }
                    // F.1c (GEN-2999): the edit pencil always opens the
                    // canonical <ComparatorModal>; the legacy mother/father
                    // modals are gone from this render path.
                    if (item.editTarget === 'mother') {
                      if (
                        !canEditComparatorRow(item.comparator) ||
                        !(
                          Boolean(item.statusVariant === 'success') ||
                          inProgressMotherSelected
                        )
                      ) {
                        return undefined
                      }
                      return () =>
                        setComparatorEditState({
                          // Plan C (C-read-4): resolve through the read adapter
                          // so <ComparatorModal> hydrates Sample Type + consent
                          // toggles when the API only returned demographic
                          // fields on the comparator. Resolves by side, so this
                          // equals the id-located `item.comparator` under the
                          // MOTHER / FATHER 1-of-1 invariant; `?? item.comparator`
                          // is a defensive fallback.
                          comparator:
                            getParentComparator(storedCanonical, 'mother') ?? item.comparator,
                        })
                    }
                    if (
                      !canEditComparatorRow(item.comparator) ||
                      !(
                        Boolean(item.statusVariant === 'success') ||
                        inProgressFatherSelected
                      )
                    ) {
                      return undefined
                    }
                    return () =>
                      setComparatorEditState({
                        // Plan C (C-read-4): resolve through the read adapter —
                        // see mother branch above for rationale.
                        comparator:
                          getParentComparator(storedCanonical, 'father') ?? item.comparator,
                      })
                  }}
                />
              )
            }
          })()}

          {/* GEN-3152 Phase 2: non-parent family members (e.g. a legal
              guardian who consents but is not sequenced) on family orders.
              Parents render above via <ComparatorList>; this lists the
              remaining comparators and offers an "Add family member" entry
              point that opens <ComparatorModal> restricted to non-parent
              relationships. Hidden in R5 (side-nav surfaces them as groups). */}
          {!sideNavEnabled && storedCanonical && familyDisplay.orderType !== 'PROBAND_ONLY'
            ? (() => {
                const nonParentMembers = (storedCanonical.comparators ?? []).filter(
                  (c): c is Comparator =>
                    c != null &&
                    parentSideForComparator(c) === null &&
                    c.relationship !== 'PROBAND',
                )
                return (
                  <>
                    {nonParentMembers.map((member, i) => (
                      <ComparatorCard
                        key={`in-progress-member-${member.id ?? member.demographic?.demographicId ?? i}`}
                        comparator={member}
                        onEdit={
                          canEditComparatorRow(member)
                            ? () => setMemberEditState({ comparator: member })
                            : undefined
                        }
                      />
                    ))}
                    {canAddComparatorsForOrder ? (
                      <button
                        type="button"
                        disabled={comparatorLimitReached}
                        title={comparatorLimitReached ? (comparatorLimitReachedReason ?? undefined) : undefined}
                        aria-disabled={comparatorLimitReached}
                        onClick={() => {
                          if (comparatorLimitReached) return
                          setMemberEditState({ comparator: null })
                        }}
                        className="flex w-full items-center justify-center gap-2 rounded-base border border-dashed border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 text-sm font-medium text-brand-600 dark:text-brand-400 transition-colors hover:border-brand-600 dark:hover:border-brand-500 hover:bg-brand-50 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border-base dark:disabled:hover:border-gray-700 disabled:hover:bg-white dark:disabled:hover:bg-gray-900"
                      >
                        + Add family member
                      </button>
                    ) : null}
                  </>
                )
              })()
            : null}

          </>

          {sideNavEnabled
            ? comparatorGroups.map((group) => {
                const rowComparator = resolveComparatorById(group.id)
                if (!rowComparator) return null
                const canEditRow = canEditComparatorRow(rowComparator)
                return (
                  <ComparatorRow
                    key={group.id}
                    groupId={group.id}
                    groupLabel={group.label}
                    comparator={rowComparator}
                    canEditRow={canEditRow}
                    canEditComparatorParents={canEditComparatorParents}
                    orderId={urlOrderId}
                    consentConfigs={comparatorParentConsentConfigs}
                    mrnRequired={comparatorMrnRequired}
                    probandResearchContactConsent={storedCanonical?.patient?.researchContactConsent === true}
                    onRemove={() => {
                      setComparatorPendingRemoval({ id: group.id, comparator: rowComparator })
                      setComparatorRemovalConfirmOpen(true)
                    }}
                    onEditDemographics={() =>
                      setComparatorSectionEdit({ id: group.id, group: 'demographics' })
                    }
                    onEditClinical={() => setComparatorSectionEdit({ id: group.id, group: 'clinical' })}
                    onEditConsent={() => setComparatorSectionEdit({ id: group.id, group: 'consent' })}
                  />
                )
              })
            : null}
          {/*
            Surface an Add Comparator button at the BOTTOM of the scrollable
            cards column on ALL widths (desktop + mobile), so a user can add a
            family member from the end of the cards without going to the left
            nav / hamburger. Same action + disabled state as the nav footer
            button. (On desktop this intentionally duplicates the nav's button.)
          */}
          {sideNavEnabled && canAddComparatorsForOrder ? (
            <button
              type="button"
              disabled={comparatorLimitReached}
              title={comparatorLimitReached ? (comparatorLimitReachedReason ?? undefined) : undefined}
              aria-disabled={comparatorLimitReached}
              onClick={() => {
                if (comparatorLimitReached) return
                setAddComparatorOpen(true)
              }}
              className="mt-1 inline-flex w-full shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full border border-border-brand dark:border-brand-500 bg-background-brand-softer dark:bg-gray-800 px-4 py-2.5 text-sm font-medium text-text-fg-brand dark:text-brand-400 shadow-xs transition-colors hover:bg-brand-100 dark:hover:bg-gray-700 disabled:cursor-not-allowed disabled:border-border-base dark:disabled:border-gray-700 disabled:bg-white dark:disabled:bg-gray-900 disabled:text-text-body dark:disabled:text-gray-400 disabled:opacity-50 disabled:hover:bg-white dark:disabled:hover:bg-gray-900"
            >
              <UserAdd className="h-4 w-4" />
              Add Comparator
            </button>
          ) : null}
          </div>
        </div>
      </div>

      {patientDemographicsEditInitial ? (
        <NewOrderModal
          isOpen={patientDemographicsEditOpen}
          onClose={() => setPatientDemographicsEditOpen(false)}
          onSubmit={handlePatientDemographicsSave}
          userFirstName={auth0User?.given_name}
          userLastName={auth0User?.family_name}
          userEmail={auth0User?.email}
          organizationId={orderOrgUuidCandidate ?? orgId ?? ''}
          mrnOrgResolution={{
            orgIdFromAuth: orgId,
            isEpicFlow: launchMode === 'epic_smart',
            epicOAuthState: epicState,
            // Mirror OrderStart: let the order's own org UUID satisfy the MRN
            // uniqueness lookup when the auth orgId is a non-UUID client key and
            // token-based IMDB org resolution fails (common in Epic flows). The
            // resolver only accepts it if it already passes `isImdbOrgUuid`.
            additionalUuidCandidate: orderOrgUuidCandidate,
          }}
          mrnUniquenessContext={{
            initialMrn: patientDemographicsEditInitial.mrn,
            currentImdbPatientId: orderData?.patient?.id,
          }}
          otherMrns={(storedCanonical?.comparators ?? [])
            .filter((c): c is Comparator => c != null && c.relationship !== 'PROBAND')
            .map((c) => ({
              label: (formatComparatorRelationshipLabel(c.relationship) || 'comparator').toLowerCase(),
              mrn: c.demographic?.mrn,
            }))}
          variant="edit"
          editInitialValues={patientDemographicsEditInitial}
        />
      ) : null}

      <ClinicalIndicationsModal
        isOpen={clinicalSummaryModalOpen}
        subtitle={[patientName, ageDisplay.replace(/ old$/, '')].filter(Boolean).join(' • ')}
        onSaveAndClose={saveAndCloseClinicalModal}
        onDiscard={discardClinicalModal}
        lastSaveTime={clinicalEditorLastSaveTime}
        isSaving={false}
      >
        <ClinicalSummaryEditor
          key={clinicalSummaryEditorEpoch}
          controlRef={clinicalEditorControls}
          summary={storedCanonical?.clinical?.clinicalNote ?? ''}
          differentialDiagnosis={storedCanonical?.clinical?.differentialDiagnosis ?? ''}
          priorGeneticTesting={storedCanonical?.clinical?.priorGeneticTesting ?? ''}
          familyHistory={storedCanonical?.family?.familyHistory ?? ''}
          clinicalTextBudget={probandClinicalTextBudget(fulfillmentStrategy, storedCanonical)}
          attachmentsReferenceId={
            config.featureFlags?.clinicalNoteAttachments ? urlOrderId : undefined
          }
          phenotypeModel={getPhenotypeModel(config)}
          // Update flow: the phenotypes here were decided and persisted in an
          // earlier session, so one background extraction on open can recover
          // removals the stored HPO rows could not record.
          isSentOrder
          clinicalDiagnosis={storedCanonical?.reason?.clinicalDiagnosis ?? []}
          review={storedCanonical?.reason?.clinicalSummaryReview}
          buttonOnly={config.featureFlags?.buttonOnlyHpoExtraction === true}
          onSummaryChange={(summary) => {
            dispatch(mergeClinical({ clinicalNote: summary }))
            noteClinicalEditorSave()
          }}
          onSupplementalChange={({ differentialDiagnosis, priorGeneticTesting }) => {
            dispatch(
              mergeClinical({
                differentialDiagnosis: differentialDiagnosis.trim() || null,
                priorGeneticTesting: priorGeneticTesting.trim() || null,
              }),
            )
            noteClinicalEditorSave()
          }}
          onReviewChange={(clinicalDiagnosis, review) => {
            // An extraction can land after the editor closes; one from a previous
            // opening is stale against phenotypes that changed since.
            if (clinicalSummaryEditorEpoch !== latestClinicalSummaryEditorEpoch.current) return
            // Opening the editor stamps a review on an order nobody edited, which is a
            // write with nothing behind it: dispatching it would mark an order the
            // clinician only looked at as having unsent changes, and reporting a save
            // for it puts "Saved just now" in the footer before they did anything.
            // Compare what actually reaches the lab.
            const canonical = storedCanonicalRef.current
            const proposed = {
              clinicalDiagnosis,
              userInteracted: clinicalDiagnosis.length > 0,
            }
            if (!stagedPhenotypesWouldChange(canonical?.reason, proposed)) {
              // Keep the review for this session so the editor's own merge state is intact,
              // without touching the phenotypes the dirty check reads. A review that is
              // already identical would only hand the sync effects a new reference.
              if (
                JSON.stringify(canonical?.reason?.clinicalSummaryReview ?? null) !== JSON.stringify(review)
              ) {
                dispatch(setClinicalSummaryReview(review))
              }
              return
            }
            dispatch(mergeReason(proposed))
            dispatch(setClinicalSummaryReview(review))
            applyClinicalDiagnosisToIndications(clinicalDiagnosis)
            noteClinicalEditorSave()
          }}
          onUndoRestore={handleMergedSummaryUndo}
        />
      </ClinicalIndicationsModal>

      <FamilyHistorySummaryModal
        key="family-history-summary-modal"
        isOpen={familyHistorySummaryModalOpen}
        onClose={() => setFamilyHistorySummaryModalOpen(false)}
        initialData={familyHistoryForModal}
        clinicalNote={storedCanonical?.clinical?.clinicalNote ?? ''}
        differentialDiagnosis={storedCanonical?.clinical?.differentialDiagnosis ?? ''}
        priorGeneticTesting={storedCanonical?.clinical?.priorGeneticTesting ?? ''}
        clinicalTextBudget={probandClinicalTextBudget(fulfillmentStrategy, storedCanonical)}
        onSave={(newData) => {
          if (newData.includeFamilyHistory) {
            dispatch(
              mergeFamily({
                familyHistory: newData.summary,
                hasParent: newData.familyMembersWithPhenotypes.parent,
                hasGrandparent: newData.familyMembersWithPhenotypes.grandparent,
                hasSibling: newData.familyMembersWithPhenotypes.sibling,
                hasCousin: newData.familyMembersWithPhenotypes.cousin,
              }),
            )
          } else {
            dispatch(
              mergeFamily({
                familyHistory: null,
                hasParent: false,
                hasGrandparent: false,
                hasSibling: false,
                hasCousin: false,
              }),
            )
          }
        }}
      />
      {(guardianContactEditable || guardianContactRequiredForComparator) && (
        <GuardianContactModal
          key="guardian-contact-modal"
          isOpen={guardianContactModalOpen}
          requireContactChannel={guardianContactRequiredForComparator}
          onClose={() => {
            setGuardianContactModalOpen(false)
            setGuardianContactRequiredForComparator(false)
          }}
          initialCanonical={storedCanonical?.guardianContact}
          allowExtendedRelationships={sideNavEnabled}
          onSave={(canonical) => {
            dispatch(setGuardianContact(canonical))
          }}
        />
      )}
      {sideNavEnabled && (
        <ComparatorSideNavModals
          key="comparator-side-nav-modals"
          storedCanonical={storedCanonical}
          comparatorParentConsentConfigs={comparatorParentConsentConfigs}
          parentSampleTypes={selectedTestTypeConfig?.parentSampleTypes}
          kitOrderingEnabled={kitOrderingEnabled}
          isEpicFlow={authFlow === 'epic'}
          epicState={epicState ?? undefined}
          addComparatorOpen={addComparatorOpen}
          setAddComparatorOpen={setAddComparatorOpen}
          addComparatorRelationshipOptions={addComparatorRelationshipOptions}
          onAddComparatorSave={handleAddComparatorSave}
          comparatorSectionEdit={comparatorSectionEdit}
          setComparatorSectionEdit={setComparatorSectionEdit}
          onComparatorSectionSave={handleComparatorSectionSave}
          platformCollectsFor={platformCollectsForComparator}
          guardianContactOnFile={orderGuardianContactOnFile}
          onRequestGuardianContact={requestGuardianContactForComparator}
          guardianModalOpen={guardianContactModalOpen}
          // Cell D (F2): the section editor always targets a persisted
          // comparator, so freeze its HPO editor when the order's SeqOne lock
          // is engaged (other clinical fields stay editable; no-op for rcigm).
          suppressExistingComparatorHpoEditing={isExistingComparatorHpoLocked}
          /* GEN-2999 fix: bring the side-nav demographics editor in line with
             the legacy ComparatorList pencil — both flows now enforce MRN +
             clinical identity, and project the proband's research-contact
             opt-in onto the matching parent. */
          validationProfile={
            {
              mrnRequired: comparatorMrnRequired,
              requireClinicalIdentity: true,
              patientResearchContactOptIn: true,
              probandResearchContactConsent: storedCanonical?.patient?.researchContactConsent === true,
              appliesToThisParent: (side) =>
                side === 'mother'
                  ? motherNeedsResearchContactChannel
                  : fatherNeedsResearchContactChannel,
            } satisfies ComparatorSectionValidationProfile
          }
          patientConsentModalOpen={patientConsentModalOpen}
          setPatientConsentModalOpen={setPatientConsentModalOpen}
          onPatientConsentSave={(patch) => dispatch(mergePatient(patch))}
          patientConsentShowValidationErrors={probandConsentNeedsAttention}
        />
      )}
      {storedCanonical && inProgressSequencingTestOptions ? (
        <Fragment key="in-progress-sequencing-modals">
          {/* Canonical comparator-edit modal. Mounts while `comparatorEditState`
              is non-null (set by the ComparatorList edit pencil). Saves dispatch
              `upsertComparator(...)`, which keeps the legacy mother/father slots
              and the `comparators[]` array symmetric. Flag-independent as of
              F.1c (GEN-2999) Slice B1. */}
          {comparatorEditState ? (
            <ComparatorModal
              isOpen={true}
              {...comparatorGuardianPropsFor(comparatorEditState.comparator)}
              onClose={() => setComparatorEditState(null)}
              initialData={comparatorEditState.comparator}
              clinicalNoteBudget={comparatorClinicalNoteBudget(
                fulfillmentStrategy,
                storedCanonical,
                comparatorEditState.comparator?.id
              )}
              /* Defense-in-depth: the parent edit-pencil only ever opens on a
                 MOTHER / FATHER comparator (so `legacyFixedParentDefaults`
                 locks the relationship select today), but we still restrict the
                 option list by fulfillment strategy so an `inflection_test`
                 (LSMC / SeqOne) order can never surface the extended
                 relationship list here even if the lock invariant ever drifts.
                 Mirrors the `memberEditState` modal below. */
              relationshipOptions={relationshipOptionsForStrategy(
                fulfillmentStrategy,
                ADD_FAMILY_MEMBER_RELATIONSHIP_OPTIONS
              )}
              comparatorConsentConfig={comparatorParentConsentConfigs}
              enableClinicalFields
              suppressHpoEditing={isExistingComparatorHpoLocked && comparatorEditState.comparator.id != null}
              enableSampleType
              parentSampleTypes={selectedTestTypeConfig?.parentSampleTypes
                ?.map((c) => mapCollectionStringToSampleTypeStrict(c))
                .filter((s): s is SampleTypeOption => s !== null)}
              /* Align modal save-blocking with the page completion rules so a
                 parent saved here can never leave the section "needs attention"
                 with no in-modal explanation: this page requires MRN for any
                 selected parent only under the `rcigm` strategy
                 (comparatorMrnRequired; optional for inflection_test) and
                 obligates a research-contact channel when the proband's research
                 opt-in projects onto this row. */
              mrnRequired={comparatorMrnRequired}
              requireClinicalIdentity
              legacyFixedParentDefaults
              patientResearchContactOptIn
              patientResearchContactAppliesToThisParent={
                comparatorEditState.comparator.relationship === 'MOTHER'
                  ? motherNeedsResearchContactChannel
                  : fatherNeedsResearchContactChannel
              }
              probandResearchContactPrimaryGuardian={storedCanonical?.patient?.researchContactConsent === true}
              probandIncidentalFindingsSelection={storedCanonical?.patient?.incidentalFindingsSelection ?? null}
              onSave={(updated) => {
                dispatch(upsertComparator(updated))
                setComparatorEditState(null)
              }}
            />
          ) : null}
          {/* GEN-3152 Phase 2: add/edit a non-parent family member. The
              relationship picker is restricted to non-parent options so the
              add flow can only append (never collide with the mother/father
              cards). The testing toggle lets a consenting-but-not-sequenced
              legal guardian be captured. Save only upserts the comparator. */}
          {memberEditState ? (
            <ComparatorModal
              isOpen={true}
              {...comparatorGuardianPropsFor(memberEditState.comparator)}
              onClose={() => setMemberEditState(null)}
              initialData={memberEditState.comparator}
              clinicalNoteBudget={comparatorClinicalNoteBudget(
                fulfillmentStrategy,
                storedCanonical,
                memberEditState.comparator?.id
              )}
              relationshipOptions={relationshipOptionsForStrategy(
                fulfillmentStrategy,
                NON_PARENT_COMPARATOR_RELATIONSHIP_OPTIONS
              )}
              enableTestingToggle
              enableClinicalFields
              suppressHpoEditing={isExistingComparatorHpoLocked && memberEditState.comparator?.id != null}
              enableSampleType
              parentSampleTypes={selectedTestTypeConfig?.parentSampleTypes
                ?.map((c) => mapCollectionStringToSampleTypeStrict(c))
                .filter((s): s is SampleTypeOption => s !== null)}
              onSave={(updated) => {
                dispatch(upsertComparator(updated))
                setMemberEditState(null)
              }}
            />
          ) : null}
        </Fragment>
      ) : null}
      <OrderFormViewModal
        isOpen={isOrderFormModalOpen}
        onClose={() => setIsOrderFormModalOpen(false)}
        canonical={storedCanonical}
        formData={null}
        orderData={orderData ?? null}
        orderId={urlOrderId}
        caller={getCallerFromOrderData(orderData)}
        regenerateFromCanonical={hasAnyUnsentChanges}
        regenerateRefreshToken={orderFormRegenerateToken}
      />
      {storedCanonical ? (
        <TestOptionsModal
          key={sequencingModalKey}
          isOpen={testOptionsComparatorModalOpen}
          onClose={() => {
            setTestOptionsComparatorModalOpen(false)
          }}
          initialData={testOptionsFromCanonicalOrder(storedCanonical, config)}
          discardBaseline={
            testOptionsFromSequencingBaselineSerialized(sequencingBaselineRef.current, config) ??
            testOptionsFromCanonicalOrder(storedCanonical, config)
          }
          lockSampleType={true}
          // R5: align with OrderStart — the Sequencing Options modal is a
          // speed-only picker (one radio per category). Tier (proband/duo/trio)
          // is DERIVED from `comparators[]`, added via the side-nav, never chosen
          // here. The legacy per-tier Test Type + Mother/Father "Comparators"
          // chooser (driven by `allowedTestTypeValues` + `parentDataByRelationship`)
          // is kept only for the non-side-nav layout. Post-submit upgrade-only /
          // add-comparator gating still applies via `canOpenSequencingOptionsModal`
          // and the comparator add/remove windows.
          r5SpeedAndSampleOnly={sideNavEnabled}
          // Submitted (in-progress) orders can no longer change the sequencing
          // speed — the sample is already committed to a lab turnaround. Show
          // the original selection read-only; only comparator-driven tier
          // upgrades remain possible (via the side-nav).
          lockSpeed={sideNavEnabled}
          allowedTestTypeValues={sideNavEnabled ? undefined : comparatorModalAllowedTypes}
          parentDataByRelationship={sideNavEnabled ? undefined : comparatorModalFamilyProps}
          testTypeLabel={selectedTestTypeLabel}
          onSave={(next) => {
            if (sideNavEnabled) {
              // R5: speed is locked here, so this only keeps the SKU/orderType in
              // sync with the comparator-derived tier (idempotent re-point to the
              // current category). Sample type is locked too, so nothing else is
              // written.
              const co = appStore.getState().canonicalOrder.canonicalOrder
              if (!next.testType || !co) return
              const { selectedOption, orderType } = resolveSequencingTierForComparators(
                next.testType,
                co.comparators ?? [],
                config
              )
              if (selectedOption) {
                dispatch(mergeOrderSelection({ selectedOption }))
              }
              dispatch(setOrderType(orderType))
              return
            }
            const prev = sequencingModalPrevOptions
            if (!prev) return
            const co = appStore.getState().canonicalOrder.canonicalOrder
            applySequencingOptionsChangeToCanonical({
              dispatch,
              canonical: co,
              prev,
              next,
              config,
            })
            setSequencingModalPrevOptions(next)
          }}
        />
      ) : null}

      <SendOrderUpdatesConfirmModal
        isOpen={sendUpdatesConfirmOpen}
        onClose={() => {
          if (!isUpdatingOrder) setSendUpdatesConfirmOpen(false)
        }}
        onConfirm={async () => {
          await handleUpdateOrder()
          setSendUpdatesConfirmOpen(false)
        }}
        updatedSections={sendUpdatesSectionIds}
        cancelledComparatorLabels={pendingComparatorCancellationLabels}
        isSubmitting={isUpdatingOrder}
        isReanalysis={isReanalysis}
        willReanalyze={effectiveReanalysis}
        reanalysisBlockedNote={reanalysisBlockedNote}
        mayRequireNewSignature={isTrfSignatureDocusignEnabled(config)}
      />

      <EhrDemographicsUpdateModal
        isOpen={ehrDriftModalOpen}
        onClose={() => {
          if (!isUpdatingOrder) setEhrDriftModalOpen(false)
        }}
        onConfirm={async () => {
          // The order may have been cancelled / reanalysis-locked between the
          // modal opening and this click. Don't merge (which would strand unsent
          // local EHR values) or attempt a send that would bail — just dismiss.
          if (!ehrDriftSendableRef.current) {
            setEhrDriftModalOpen(false)
            return
          }
          if (ehrDriftMergePatch && Object.keys(ehrDriftMergePatch).length > 0) {
            // Merge ONLY the drifted fields — never the full EHR snapshot — so a
            // blank EHR value that the comparison excluded can't overwrite a good
            // saved name / MRN / sex / DOB.
            dispatch(mergePatient(ehrDriftMergePatch))
            setDemographicsLocallyEdited(true)
          }
          await handleUpdateOrder()
          setEhrDriftModalOpen(false)
        }}
        changedFields={ehrDriftFields}
        isSubmitting={isUpdatingOrder}
      />

      <CancelOrderConfirmModal
        isOpen={cancelConfirmOpen}
        onClose={() => {
          if (!isCancelling) setCancelConfirmOpen(false)
        }}
        onConfirm={handleConfirmCancel}
        isSubmitting={isCancelling}
      />

      <ConfirmDialog
        isOpen={comparatorRemovalConfirmOpen}
        onClose={() => {
          setComparatorRemovalConfirmOpen(false)
          setComparatorPendingRemoval(null)
        }}
        onConfirm={() => {
          setComparatorRemovalConfirmOpen(false)
          const pending = comparatorPendingRemoval
          setComparatorPendingRemoval(null)
          if (!pending) return
          const { id: removalId, comparator: removalTarget } = pending
          const label =
            formatComparatorRelationshipLabel(removalTarget.relationship) ||
            'this comparator'
          // Compute the post-removal comparator set BEFORE the reducer mutates
          // the store, then downgrade the sequencing SKU + orderType so the
          // "Sequencing Options" card no longer shows e.g. "urWGS Trio (Proband +
          // Two Comparators)" once only one comparator remains.
          const remaining = (storedCanonical?.comparators ?? []).filter(
            (c) => c?.id !== removalId
          )
          // Remember this comparator's identity so a later re-hydration (which
          // re-projects the authoritative `requisitionFormData.comparators`)
          // can't resurrect it before the removal is persisted on the next Send
          // Updates. Especially matters for previously-sent non-parent rows
          // (extended relatives / siblings).
          const removedKeys = comparatorIdentityKeys(removalTarget)
          for (const k of removedKeys) removedComparatorKeysRef.current.add(k)
          // Surface this removal in the Send Updates confirm modal as a pending
          // cancellation, but only if the row was previously sent to the lab
          // (present in the baseline). A comparator added and removed this
          // session was never sent, so there is nothing to cancel. Keyed by the
          // row's primary identity key.
          const wasSent = removedKeys.some((k) => baselineComparatorKeysRef.current.has(k))
          if (wasSent && removedKeys.length > 0) {
            const baseLabel = label === 'this comparator' ? 'Comparator' : label
            const removedName = [
              removalTarget.demographic?.firstName,
              removalTarget.demographic?.lastName,
            ]
              .filter((p) => p && String(p).trim())
              .join(' ')
              .trim()
            const cancellationLabel = removedName ? `${baseLabel} (${removedName})` : baseLabel
            setPendingComparatorCancellations((prev) => {
              // Drop any prior entry for the same person (key overlap) before
              // adding, so a remove → re-add → remove cycle can't accumulate
              // duplicate notices.
              const withoutDup = dropMatchingCancellations(prev, removedKeys)
              return [...withoutDup, { keys: removedKeys, label: cancellationLabel }]
            })
          }
          dispatch(removeComparator(removalId))
          syncSequencingTierToComparators(remaining)
          // Only clear the live selection if it still points at the row we just
          // removed; a mid-dialog selection change must not be clobbered.
          setSelectedComparatorId((current) => (current === removalId ? null : current))
        }}
        title={`Remove ${
          (comparatorPendingRemoval &&
            formatComparatorRelationshipLabel(comparatorPendingRemoval.comparator.relationship)) ||
          'this comparator'
        } from this order?`}
        description="This cannot be undone."
        confirmLabel="Yes, remove"
        cancelLabel="No, keep"
      />

      {/* Unsaved Changes Warning Modal */}
      {showUnsavedChangesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="w-full max-w-md rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 shadow-2xl">
            <div className="px-6 py-4">
              <h3 className="text-lg font-semibold text-text-heading dark:text-white">Unsaved Changes</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm leading-5 font-medium text-text-heading dark:text-white">
                You have changes that haven&apos;t been sent to the lab yet. If you leave now, your changes may be lost. Click &quot;Send Updates&quot; to send your changes before leaving.
              </p>
            </div>
            <div className="flex justify-end gap-3 border-t border-border-base dark:border-gray-700 px-6 py-4">
              <button
                type="button"
                onClick={handleKeepEditing}
                className="rounded-full border border-border-base-medium dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-text-body dark:text-gray-400 hover:bg-background-secondary-medium dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
              >
                Keep Editing
              </button>
              <Button
                variant="danger"
                size="md"
                type="button"
                onClick={handleDiscardChanges}
                className="focus-visible:ring-error-500"
              >
                Discard Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Route entry for `/order/in-progress/:orderId?/:patientId?`.
 *
 * Keyed on the route ids so moving between orders remounts the page with
 * fresh state (loader first, no carry-over from the previous order). The
 * `OrderScopeGuard` this route used to have gave the same remount as a side
 * effect of its per-id loading state.
 */
export function OrderInProgressRoute() {
  const { orderId, patientId } = useParams<{ orderId?: string; patientId?: string }>()
  return <OrderInProgress key={`${orderId ?? ''}/${patientId ?? ''}`} />
}
